import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isCloudinaryUrl } from '../../utils/cloudinaryUtils';
import { usePosLocalStorage } from '../hooks/usePosLocalStorage';
import { usePosWebSocket } from '../hooks/usePosWebSocket';
import MenuGallery2 from '../../pages/menuGallery2';

const CATEGORIES = [
  { id: 'cocktails', label: 'NA', fullName: 'Social Tonics' },
  { id: 'mocktails', label: 'A+', fullName: 'CLASSICS' },
  { id: 'beer', label: 'B', fullName: 'Beer' },
  { id: 'wine', label: 'W', fullName: 'Wine' },
  { id: 'spirits', label: 'S', fullName: 'Spirits' }
];

const normalizeCategoryKey = (value = '') => {
  const key = String(value).toLowerCase();
  const categoryMap = {
    'classics': 'cocktails',
    'originals': 'mocktails'
  };
  return categoryMap[key] || key;
};

// Convert string to Title Case
const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

function useMeasuredSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    
    // Measure function
    const measure = () => {
      const { clientWidth, clientHeight } = node;
      if (clientWidth > 0 && clientHeight > 0) {
        setSize({ width: clientWidth, height: clientHeight });
        return true;
      }
      return false;
    };
    
    // Immediately measure on mount
    if (!measure()) {
      // If initial measurement fails, try again after a frame
      requestAnimationFrame(() => {
        if (!measure()) {
          // Final fallback: try after a short delay
          setTimeout(measure, 50);
        }
      });
    }
    
    if (!window.ResizeObserver) {
      const handle = () => measure();
      window.addEventListener('resize', handle);
      return () => window.removeEventListener('resize', handle);
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setSize({ width, height });
        }
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, size];
}

// Inner POS Content Component for 9:19 view
function POSContent({ outerWidth, outerHeight, items, activeCategory, setActiveCategory, onItemClick, loading, total, categoryCounts, selectedItems, lastAction, onRemoveItem, tabs, activeTabId, onCreateTab, onSelectTab, onDeleteTab, onArchiveTab, onInvoiceTab, onReopenTab, onUpdateTabName, onUpdateItemModifiers, onMoveItems, onCheckout, checkoutLoading, onDuplicateItems, showSpendLimitWarning, setShowSpendLimitWarning, spendLimitWarningTab, setSpendLimitWarningTab, isSidebarOpen }) {
  // Bottom drawer state - starts collapsed, receipt view is default
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragCurrentY, setDragCurrentY] = useState(0);

  // Force collapse bottom drawer if sidebar opens
  useEffect(() => {
    if (isSidebarOpen) {
      setDrawerExpanded(false);
    }
  }, [isSidebarOpen]);
  
  // Drawer view state: 'receipt' or 'tabs'
  const [drawerView, setDrawerView] = useState('receipt');
  
  const [itemToRemove, setItemToRemove] = useState(null); // For confirmation popup
  const [actionKey, setActionKey] = useState(0); // For triggering fade-in animation
  const [flashingItemId, setFlashingItemId] = useState(null); // For item click flash effect
  const [showCloseTabConfirm, setShowCloseTabConfirm] = useState(false); // For close tab confirmation
  const [showInvoiceConfirm, setShowInvoiceConfirm] = useState(false); // For invoice tab confirmation
  
  // Receipt item selection state (for moving items between tabs)
  const [selectedReceiptIndices, setSelectedReceiptIndices] = useState(new Set());
  const [showMoveModal, setShowMoveModal] = useState(false);
  const receiptLongPressTimerRef = useRef(null);
  const receiptLongPressTriggeredRef = useRef(false);
  
  // Archived tabs view state
  const [showArchivedTabs, setShowArchivedTabs] = useState(false);
  const addTabLongPressRef = useRef(null);
  
  // Tab spend limit state (showSpendLimitWarning and spendLimitWarningTab are passed as props)
  const [showSpendLimitModal, setShowSpendLimitModal] = useState(false);
  const [spendLimitTabId, setSpendLimitTabId] = useState(null);
  const [spendLimitInput, setSpendLimitInput] = useState('');
  const tabLongPressTimerRef = useRef(null);
  
  // Modifier system state
  const [editingItem, setEditingItem] = useState(null); // Item being edited (long-press)
  const [showAddModifierModal, setShowAddModifierModal] = useState(false); // Add modifier modal
  const [newModifierName, setNewModifierName] = useState('');
  const [newModifierPrice, setNewModifierPrice] = useState('');
  const [newModifierLink, setNewModifierLink] = useState(false);
  const [showMaxModifiersAlert, setShowMaxModifiersAlert] = useState(false);
  const [orderingItem, setOrderingItem] = useState(null); // Item being ordered (tap with modifiers)
  const [selectedModifiers, setSelectedModifiers] = useState([]); // Selected modifiers during ordering
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const pressHandledRef = useRef(false); // Track if current press has been handled to prevent double-fire
  const resetHandledTimeoutRef = useRef(null);
  
  // Handle item long-press start (for editing flow)
  const handleItemPressStart = useCallback((item, e) => {
    // Don't reset pressHandledRef here - it will be reset after a delay in handleItemPressEnd
    // This prevents the double-fire issue where touch events fire, then mouse events fire
    
    // Only start long-press timer if not already handled
    if (pressHandledRef.current) {
      return;
    }
    
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      pressHandledRef.current = true; // Mark as handled
      setEditingItem(item);
      
      // Reset after a delay
      setTimeout(() => {
        pressHandledRef.current = false;
      }, 300);
    }, 500); // 500ms for long-press
  }, []);
  
  // Handle item press end
  const handleItemPressEnd = useCallback((item, e) => {
    // If already handled (by long-press or previous event), skip
    if (pressHandledRef.current) {
      return;
    }
    
    // Mark as handled immediately to prevent double-fire
    pressHandledRef.current = true;
    
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    
    // If long-press wasn't triggered, treat as tap (ordering flow)
    if (!longPressTriggeredRef.current) {
      const itemModifiers = item.modifiers || [];
      if (itemModifiers.length === 0) {
        // No modifiers - add item immediately with flash
        setFlashingItemId(item._id);
        onItemClick(item);
        setTimeout(() => setFlashingItemId(null), 500);
      } else {
        // Has modifiers - open modifier selection popup
        console.log(`[POS] Opening modifier popup for "${item.name}", base price: ${item.price}`);
        setOrderingItem(item);
        setSelectedModifiers([]);
      }
    }
    
    // Reset the handled flag after 300ms to allow the next press
    // This prevents mouse events from firing after touch events
    if (resetHandledTimeoutRef.current) {
      clearTimeout(resetHandledTimeoutRef.current);
    }
    resetHandledTimeoutRef.current = setTimeout(() => {
      pressHandledRef.current = false;
    }, 300);
  }, [onItemClick]);
  
  // Handle item click with flash effect (legacy, kept for compatibility)
  const handleItemClickWithFlash = useCallback((item) => {
    setFlashingItemId(item._id);
    onItemClick(item);
    // Clear flash after animation completes
    setTimeout(() => {
      setFlashingItemId(null);
    }, 500);
  }, [onItemClick]);
  
  // Handle receipt item long-press start (for selection/move flow)
  const handleReceiptItemPressStart = useCallback((index, e) => {
    e.preventDefault();
    receiptLongPressTriggeredRef.current = false;
    receiptLongPressTimerRef.current = setTimeout(() => {
      receiptLongPressTriggeredRef.current = true;
      // Toggle selection for this item
      setSelectedReceiptIndices(prev => {
        const newSet = new Set(prev);
        if (newSet.has(index)) {
          newSet.delete(index);
        } else {
          newSet.add(index);
        }
        return newSet;
      });
    }, 400); // 400ms for long-press
  }, []);
  
  // Handle receipt item press end
  const handleReceiptItemPressEnd = useCallback((e) => {
    if (receiptLongPressTimerRef.current) {
      clearTimeout(receiptLongPressTimerRef.current);
      receiptLongPressTimerRef.current = null;
    }
  }, []);
  
  // Clear selection when drawer view changes or drawer closes
  useEffect(() => {
    if (drawerView !== 'receipt' || !drawerExpanded) {
      setSelectedReceiptIndices(new Set());
    }
  }, [drawerView, drawerExpanded]);
  
  // Update actionKey when lastAction changes to trigger fade-in
  useEffect(() => {
    if (lastAction) {
      setActionKey(prev => prev + 1);
    }
  }, [lastAction]);
  
  // Calculate dimensions
  // 4px padding on each side (8px total) + 4px gap between each button (2 gaps = 8px) = 16px total
  const itemButtonSize = (outerWidth - 4 - 4 - 4 - 4) / 3; // 4px left padding + 4px right padding + 2x 4px gaps
  const tabButtonSize = itemButtonSize; // Same size as item buttons
  const footerHeight = itemButtonSize / 2; // Collapsed footer height
  const headerHeight = outerWidth / 5; // Header buttons are 1:1, 5 across = width/5 each
  const handleBarHeight = 36; // Height of the swipe handle bar (balanced for touch and aesthetics)
  // Bottom drawer expanded: from footer up to header
  const bottomDrawerExpandedHeight = outerHeight - headerHeight - footerHeight - handleBarHeight;
  
  // Calculate current drawer height based on drag or expanded state
  const getDrawerHeight = () => {
    if (isDragging) {
      const dragDelta = dragStartY - dragCurrentY;
      const baseHeight = drawerExpanded ? bottomDrawerExpandedHeight : handleBarHeight;
      const newHeight = Math.max(handleBarHeight, Math.min(bottomDrawerExpandedHeight, baseHeight + dragDelta));
      return newHeight;
    }
    return drawerExpanded ? bottomDrawerExpandedHeight : handleBarHeight;
  };
  
  const currentDrawerHeight = getDrawerHeight();
  
  const handleDragStart = (e) => {
    if (isSidebarOpen) return; // Prevent dragging when sidebar is open
    setIsDragging(true);
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStartY(clientY);
    setDragCurrentY(clientY);
  };
  
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragCurrentY(clientY);
  }, [isDragging]);
  
  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    const dragDelta = dragStartY - dragCurrentY;
    const threshold = (bottomDrawerExpandedHeight - handleBarHeight) / 3;
    
    if (drawerExpanded) {
      // If expanded and dragged down enough, collapse
      if (dragDelta < -threshold) {
        setDrawerExpanded(false);
      }
    } else {
      // If collapsed and dragged up enough, expand
      if (dragDelta > threshold) {
        setDrawerExpanded(true);
      }
    }
  }, [isDragging, dragStartY, dragCurrentY, drawerExpanded, bottomDrawerExpandedHeight, handleBarHeight]);
  
  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => handleDragMove(e);
      const handleEnd = () => handleDragEnd();
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);
  
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };
  
  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex',
      flexDirection: 'column',
      background: '#f5f5f5',
      fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
      overflow: 'hidden',
      boxSizing: 'border-box',
      position: 'relative'
    }}>
      <style>{`
        @keyframes fadeSlideIn {
          from { 
            opacity: 0; 
            transform: translateY(-8px);
          }
          to { 
            opacity: 1; 
            transform: translateY(0);
          }
        }
        @keyframes flashFadeOut {
          from { 
            opacity: 1;
          }
          to { 
            opacity: 0;
          }
        }
        @keyframes spin {
          to { 
            transform: rotate(360deg); 
          }
        }
      `}</style>
      {/* Sticky Category Header - 5 buttons, 1:1 aspect ratio, edge-to-edge */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        display: 'flex',
        width: '100%',
        flexShrink: 0,
        background: '#d0d0d0'
      }}>
        {CATEGORIES.map((category) => (
          <button
            key={category.id}
            onClick={() => { setActiveCategory(category.id); setDrawerExpanded(false); }}
            title={category.fullName}
            style={{
              flex: 1,
              aspectRatio: '1 / 1',
              border: '1px solid #fff',
              background: activeCategory === category.id ? '#800080' : '#999',
              color: '#fff',
              fontSize: `${outerWidth / 12}px`,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
              padding: 0,
              margin: 0
            }}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Scrollable Item Grid */}
      <div className="scrollable-content" style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '4px',
        paddingBottom: `${footerHeight + handleBarHeight + 12}px`
      }}>
        {loading ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #999',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        ) : items.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            color: '#666'
          }}>
            No items in this category
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '4px',
            width: '100%'
          }}>
            {items.map((item) => (
              <button
                key={item._id}
                onMouseDown={(e) => handleItemPressStart(item, e)}
                onMouseUp={(e) => handleItemPressEnd(item, e)}
                onMouseLeave={() => {
                  if (longPressTimerRef.current) {
                    clearTimeout(longPressTimerRef.current);
                    longPressTimerRef.current = null;
                  }
                }}
                onTouchStart={(e) => handleItemPressStart(item, e)}
                onTouchEnd={(e) => handleItemPressEnd(item, e)}
                onContextMenu={(e) => e.preventDefault()}
                style={{
                  aspectRatio: '1 / 1',
                  border: 'none',
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: 0,
                  fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  position: 'relative',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none'
                }}
              >
                {/* Flash overlay on click */}
                {flashingItemId === item._id && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#fff',
                    zIndex: 10,
                    animation: 'flashFadeOut 0.5s ease-out forwards'
                  }} />
                )}
                
                {/* Video/Image background */}
                {(() => {
                  const videoSrc = item.cloudinaryIconUrl || item.cloudinaryVideoUrl || item.videoUrl;
                  if (isCloudinaryUrl(videoSrc)) {
                    return (
                      <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        webkit-playsinline="true"
                        poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                        style={{ 
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%) scale(1.10)',
                          transformOrigin: 'bottom center',
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          zIndex: 1,
                          background: '#fff'
                        }}
                      >
                        <source src={videoSrc} type="video/mp4" />
                      </video>
                    );
                  }
                  return null;
                })()}
                
                {/* Item name overlay at bottom */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '8px 4px 0px 4px',
                  zIndex: 2
                }}>
                  <span style={{
                    fontSize: `${Math.max(4, outerWidth / 32)}px`,
                    fontWeight: 500,
                    color: '#000',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {(item.name || 'Untitled').toUpperCase()}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Sliding Drawer Footer */}
      <div style={{
        position: 'absolute',
        bottom: footerHeight + handleBarHeight,
        left: 0,
        right: 0,
        height: `${currentDrawerHeight}px`,
        background: '#fff',
        borderRadius: '4px',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: drawerExpanded ? 103 : 102,
        overflow: 'hidden',
        transition: isDragging ? 'none' : 'height 0.3s ease-out'
      }}>
        {/* Handle Bar - Swipe indicator - always draggable */}
        <div
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          style={{
            height: `${handleBarHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            flexShrink: 0,
            background: '#f8f8f8',
            borderBottom: '1px solid #eee'
          }}
        >
          <div style={{
            width: '60px',
            height: '6px',
            background: '#999',
            borderRadius: '3px'
          }} />
        </div>
        
        {/* Drawer Content - switches between Receipt and Tabs views */}
        {currentDrawerHeight > handleBarHeight + 20 && (
          <>
            {/* Control Buttons Row - always show when on tabs view */}
            {drawerView === 'tabs' && (
              <div style={{
                display: 'flex',
                gap: '4px',
                padding: '4px 8px',
                background: '#fff',
                borderBottom: '1px solid #eee',
                flexShrink: 0
              }}>
                {/* PAID/TABS - Archive Toggle (first position) */}
                <button
                  onClick={() => {
                    setShowArchivedTabs(prev => !prev);
                  }}
                  style={{
                    flex: 1,
                    height: `${Math.round(footerHeight * 0.7)}px`,
                    border: 'none',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <img 
                    src={showArchivedTabs ? "/assets/icons/TABS.png" : "/assets/icons/PAID.png"} 
                    alt={showArchivedTabs ? "All Tabs" : "Archived"} 
                    style={{ height: '90%', width: 'auto', opacity: 0.6 }} 
                  />
                </button>
                
                {/* BIN - Spillage Tab (second position) */}
                <button
                  onClick={() => {
                    // Select spillage tab and go to receipt view
                    const spillageTab = tabs.find(t => t.isSpillage);
                    if (spillageTab) {
                      onSelectTab(spillageTab.id);
                      setDrawerView('receipt'); // Switch to receipt view
                      setShowArchivedTabs(false); // Exit archive view
                    }
                  }}
                  style={{
                    flex: 1,
                    height: `${Math.round(footerHeight * 0.7)}px`,
                    border: tabs.find(t => t.isSpillage && t.id === activeTabId) ? '2px solid #800080' : 'none',
                    background: tabs.find(t => t.isSpillage && t.id === activeTabId) ? '#f0e6f0' : '#f5f5f5',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <img 
                    src="/assets/icons/BIN.png"
                    alt="Spillage" 
                    style={{ height: '90%', width: 'auto', opacity: 0.6 }} 
                  />
                </button>
                
                {/* ADD_TAB - Create New Tab */}
                <button
                  onClick={() => {
                    if (!showArchivedTabs) {
                      onCreateTab();
                    }
                  }}
                  style={{
                    flex: 1,
                    height: `${Math.round(footerHeight * 0.7)}px`,
                    border: 'none',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    cursor: showArchivedTabs ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    opacity: showArchivedTabs ? 0.4 : 1
                  }}
                >
                  <img src="/assets/icons/ADD_TAB.png" alt="Add Tab" style={{ height: '90%', width: 'auto', opacity: 0.6 }} />
                </button>
              </div>
            )}
            
            {/* Tab Name Input Row - only show when on tabs view and a tab is selected */}
            {drawerView === 'tabs' && activeTabId && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px',
                background: '#fff',
                borderBottom: '1px solid #eee',
                flexShrink: 0,
                height: `${Math.max(Math.round(outerWidth / 7), Math.round(footerHeight))}px`
              }}>
                {(() => {
                const activeTab = tabs.find(t => t.id === activeTabId);
                const isSpillageTab = activeTab?.isSpillage;
                
                if (isSpillageTab) {
                  // Spillage tab: show "Name:" on left and "SPILL TAB" aligned right like P1
                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, marginRight: '12px' }}>
                        <span style={{
                          color: '#d0d0d0',
                          fontSize: `${Math.max(10, footerHeight * 0.3)}px`,
                          fontWeight: 500,
                          fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
                        }}>
                          NAME:
                        </span>
                      </div>
                      <span style={{
                        color: '#800080',
                        fontSize: `${Math.max(14, footerHeight * 0.45)}px`,
                        fontWeight: 600,
                        fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
                      }}>
                        SPILL TAB
                      </span>
                    </>
                  );
                }
                
                // Regular tab: show NAME input and tab name
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, marginRight: '12px' }}>
                      <span style={{
                        color: '#d0d0d0',
                        fontSize: `${Math.max(10, footerHeight * 0.3)}px`,
                        fontWeight: 500,
                        fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
                      }}>
                        NAME:
                      </span>
                      <input
                        type="text"
                        value={activeTab?.customName || ''}
                        onChange={(e) => onUpdateTabName(activeTabId, e.target.value)}
                        style={{
                          border: 'none',
                          outline: 'none',
                          color: '#000',
                          fontSize: `${Math.max(14, footerHeight * 0.45)}px`,
                          fontWeight: 600,
                          fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                          background: 'transparent',
                          flex: 1,
                          minWidth: 0,
                          textTransform: 'uppercase'
                        }}
                      />
                    </div>
                    <span style={{
                      color: '#800080',
                      fontSize: `${Math.max(14, footerHeight * 0.45)}px`,
                      fontWeight: 600,
                      fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
                    }}>
                      {activeTab?.name}
                    </span>
                  </>
                );
              })()}
              </div>
            )}
            
            {/* Receipt View - Order List */}
            {drawerView === 'receipt' && (
              <div className="scrollable-content" style={{
                flex: 1,
                overflowY: 'auto',
                padding: '8px',
                borderBottom: '1px solid #eee'
              }}>
                {selectedItems.length === 0 ? (
                  <div style={{
                    color: '#999',
                    fontSize: `${Math.max(10, outerWidth / 30)}px`,
                    textAlign: 'center',
                    padding: '16px'
                  }}>
                    No items added yet
                  </div>
                ) : (
                  (() => {
                    const activeTab = tabs.find(t => t.id === activeTabId);
                    const isArchivedTab = activeTab?.status === 'archived';
                    return selectedItems.map((item, index) => {
                      const isSelected = selectedReceiptIndices.has(index);
                      return (
                        <div 
                          key={`${item._id}-${index}`}
                          onMouseDown={isArchivedTab ? undefined : (e) => handleReceiptItemPressStart(index, e)}
                          onMouseUp={isArchivedTab ? undefined : handleReceiptItemPressEnd}
                          onMouseLeave={isArchivedTab ? undefined : handleReceiptItemPressEnd}
                          onTouchStart={isArchivedTab ? undefined : (e) => handleReceiptItemPressStart(index, e)}
                          onTouchEnd={isArchivedTab ? undefined : handleReceiptItemPressEnd}
                          onTouchCancel={isArchivedTab ? undefined : handleReceiptItemPressEnd}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '6px 8px',
                            marginBottom: '2px',
                            borderRadius: '4px',
                            background: isSelected ? '#f0f0f0' : 'transparent',
                            cursor: isArchivedTab ? 'default' : 'pointer',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            transition: 'background 0.15s ease'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: `${Math.max(10, outerWidth / 28)}px`,
                            color: isSelected ? '#fff' : '#333'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 500, color: isSelected ? '#333' : '#333' }}>{(item.name || 'Item').toUpperCase()}</span>
                              <span style={{ color: isSelected ? '#666' : '#999' }}>-</span>
                              <span style={{ color: isSelected ? '#666' : '#999' }}>{item.modifier || CATEGORIES.find(c => c.id === normalizeCategoryKey(item.category))?.fullName || item.category || '—'}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ color: isSelected ? '#666' : '#999' }}>{formatTimestamp(item.addedAt)}</span>
                              {/* Hide remove button for archived tabs */}
                              {!isArchivedTab && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setItemToRemove({ item, index }); }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: isSelected ? '#666' : '#d0d0d0',
                                    fontSize: `${Math.max(18, outerWidth / 16)}px`,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    padding: '0 4px',
                                    lineHeight: 1
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                          <div style={{
                            fontSize: `${Math.max(9, outerWidth / 32)}px`,
                            color: isSelected ? '#666' : '#666',
                            marginTop: '2px'
                          }}>
                            ${(parseFloat(item.price) || 0).toFixed(2)}
                          </div>
                        </div>
                      );
                    });
                  })()
                )}
              </div>
            )}
            
            {/* Tabs View - Tab Grid */}
            {drawerView === 'tabs' && (
              <div className="scrollable-content" style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '4px',
                  padding: '4px',
                  paddingBottom: '8px',
                  alignContent: 'start'
                }}>
                  {/* Tab Buttons - show archived or unarchived based on toggle, exclude spillage tab (has its own button) */}
                  {/* When showing archived tabs, sort invoice tabs first */}
                  {tabs.filter(tab => !tab.isSpillage && (showArchivedTabs ? tab.status === 'archived' : tab.status !== 'archived'))
                    .sort((a, b) => {
                      if (!showArchivedTabs) return 0; // Don't sort unpaid tabs
                      // Invoice tabs come first
                      if (a.isInvoice && !b.isInvoice) return -1;
                      if (!a.isInvoice && b.isInvoice) return 1;
                      return 0;
                    })
                    .map((tab) => {
                    // Determine tab colors based on status
                    const isInvoiced = tab.isInvoice && tab.status === 'archived';
                    const isArchived = tab.status === 'archived';
                    const isSelected = activeTabId === tab.id;
                    
                    let borderColor = 'transparent';
                    let bgColor = '#fff';
                    let textColor = '#333';
                    
                    if (isInvoiced) {
                      borderColor = isSelected ? '#f59e0b' : 'transparent';
                      bgColor = '#fef3c7'; // Yellow tint for invoiced
                      textColor = isSelected ? '#b45309' : '#92400e';
                    } else if (isArchived) {
                      borderColor = isSelected ? '#22c55e' : 'transparent';
                      bgColor = '#dcfce7';
                      textColor = isSelected ? '#166534' : '#333';
                    } else {
                      borderColor = isSelected ? '#800080' : 'transparent';
                      bgColor = isSelected ? '#f0e6f0' : '#fff';
                      textColor = isSelected ? '#800080' : '#333';
                    }
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          // Allow selecting any tab (archived tabs will be read-only)
                          onSelectTab(tab.id);
                        }}
                        onMouseDown={(e) => {
                          // Start long-press timer for spend limit
                          if (isArchived) return;
                          tabLongPressTimerRef.current = setTimeout(() => {
                            e.preventDefault();
                            setSpendLimitTabId(tab.id);
                            setSpendLimitInput(tab.spendLimit ? String(tab.spendLimit) : '');
                            setShowSpendLimitModal(true);
                          }, 500);
                        }}
                        onMouseUp={() => {
                          if (tabLongPressTimerRef.current) {
                            clearTimeout(tabLongPressTimerRef.current);
                            tabLongPressTimerRef.current = null;
                          }
                        }}
                        onMouseLeave={() => {
                          if (tabLongPressTimerRef.current) {
                            clearTimeout(tabLongPressTimerRef.current);
                            tabLongPressTimerRef.current = null;
                          }
                        }}
                        onTouchStart={(e) => {
                          // Start long-press timer for spend limit (touch)
                          if (isArchived) return;
                          tabLongPressTimerRef.current = setTimeout(() => {
                            e.preventDefault();
                            setSpendLimitTabId(tab.id);
                            setSpendLimitInput(tab.spendLimit ? String(tab.spendLimit) : '');
                            setShowSpendLimitModal(true);
                          }, 500);
                        }}
                        onTouchEnd={() => {
                          if (tabLongPressTimerRef.current) {
                            clearTimeout(tabLongPressTimerRef.current);
                            tabLongPressTimerRef.current = null;
                          }
                        }}
                        onTouchCancel={() => {
                          if (tabLongPressTimerRef.current) {
                            clearTimeout(tabLongPressTimerRef.current);
                            tabLongPressTimerRef.current = null;
                          }
                        }}
                        style={{
                          aspectRatio: '1 / 1',
                          width: '100%',
                          border: borderColor !== 'transparent' ? `2px solid ${borderColor}` : 'none',
                          background: bgColor,
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                          padding: '24px',
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                          opacity: 1,
                          position: 'relative'
                        }}
                      >
                        {(() => {
                          const displayName = (tab.customName || tab.name).toUpperCase();
                          const words = displayName.split(' ');
                          const longestWord = Math.max(...words.map(w => w.length));
                          const numLines = words.length;
                          const buttonWidth = (outerWidth - 16) / 3;
                          const availableWidth = buttonWidth - 48;
                          const availableHeight = buttonWidth - 48;
                          const fontSizeByWidth = availableWidth / (longestWord * 0.6);
                          const fontSizeByHeight = availableHeight / (numLines * 1.2);
                          const fontSize = Math.min(fontSizeByWidth, fontSizeByHeight, outerWidth / 12);
                          
                          return words.map((word, idx) => (
                            <span 
                              key={idx}
                              style={{
                                color: textColor,
                                fontSize: `${fontSize}px`,
                                fontWeight: 600,
                                lineHeight: 1.2
                              }}
                            >
                              {word}
                            </span>
                          ));
                        })()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Action Buttons - always visible when drawer is expanded */}
        {drawerExpanded && (
          <div style={{
            display: 'flex',
            padding: '8px',
            gap: '8px',
            background: '#fff',
            flexShrink: 0
          }}>
            {/* Page Switcher Button - TABS/MOVE when on receipt, VIEW when on tabs */}
            {(() => {
              const activeTab = tabs.find(t => t.id === activeTabId);
              const isArchivedTab = activeTab?.status === 'archived';
              const canMove = drawerView === 'receipt' && selectedReceiptIndices.size > 0 && !isArchivedTab;
              return (
                <button
                  onClick={() => {
                    if (canMove) {
                      // MOVE mode - open tab selection modal
                      setShowMoveModal(true);
                    } else {
                      // Normal mode - switch views (always allow switching)
                      setDrawerView(drawerView === 'receipt' ? 'tabs' : 'receipt');
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                    background: '#f0f0f0',
                    color: '#333',
                    fontSize: `${Math.max(12, outerWidth / 25)}px`,
                    fontWeight: 600,
                    fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                    cursor: 'pointer',
                    borderRadius: '4px'
                  }}
                >
                  {drawerView === 'receipt' ? (selectedReceiptIndices.size > 0 && !isArchivedTab ? 'MOVE' : 'TABS') : 'VIEW'}
                </button>
              );
            })()}
            {/* Close/Cancel/Receipt Button - shows RECEIPT for paid tabs, CANCEL when items selected, CLOSE otherwise */}
            {(() => {
              const currentTab = tabs.find(t => t.id === activeTabId);
              const isSpillageTab = currentTab?.isSpillage;
              const isPaidTab = currentTab?.status === 'archived';
              const hasItems = (currentTab?.items || []).length > 0;
              // For paid tabs, show RECEIPT button (always enabled)
              // For unpaid tabs with items and no selection, disable CLOSE
              const isDisabled = isSpillageTab || (!isPaidTab && hasItems && selectedReceiptIndices.size === 0);
              
              // Determine button label
              let buttonLabel = 'CLOSE';
              if (selectedReceiptIndices.size > 0) {
                buttonLabel = 'CANCEL';
              } else if (isPaidTab) {
                buttonLabel = 'RECEIPT';
              }
              
              return (
                <button
                  onClick={() => {
                    if (isPaidTab) {
                      // RECEIPT mode - switch to receipt view for paid tab
                      setDrawerView('receipt');
                      return;
                    }
                    if (isDisabled) return; // Disabled for spillage tab
                    if (selectedReceiptIndices.size > 0) {
                      // CANCEL mode - deselect all items
                      setSelectedReceiptIndices(new Set());
                    } else {
                      // CLOSE mode - close empty tab (show confirmation)
                      activeTabId && setShowCloseTabConfirm(true);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                    background: '#f0f0f0',
                    color: isDisabled ? '#999' : ((selectedReceiptIndices.size > 0 || activeTabId) ? '#333' : '#999'),
                    fontSize: `${Math.max(12, outerWidth / 25)}px`,
                    fontWeight: 600,
                    fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                    cursor: isDisabled ? 'not-allowed' : ((selectedReceiptIndices.size > 0 || activeTabId) ? 'pointer' : 'not-allowed'),
                    borderRadius: '4px',
                    opacity: isDisabled ? 0.4 : 1
                  }}
                >
                  {buttonLabel}
                </button>
              );
            })()}
            {/* Checkout/Duplicate/Reopen Button - shows DUPLICATE when items selected, REOPEN for invoice tabs, disabled for paid CASH/CREDIT tabs */}
            {(() => {
              const currentTab = tabs.find(t => t.id === activeTabId);
              const isSpillageTab = currentTab?.isSpillage;
              const isPaidTab = currentTab?.status === 'archived';
              const paymentMethod = currentTab?.paymentMethod;
              const isInvoiceTab = isPaidTab && paymentMethod === 'invoice';
              const isPaidCashOrCredit = isPaidTab && (paymentMethod === 'cash' || paymentMethod === 'credit');
              
              // Determine button state
              let buttonLabel = 'CHECKOUT';
              let isDisabled = false;
              let buttonBackground = '#800080';
              
              if (selectedReceiptIndices.size > 0) {
                buttonLabel = 'DUPLICATE';
                buttonBackground = '#666';
                isDisabled = false;
              } else if (isInvoiceTab) {
                buttonLabel = 'REOPEN';
                buttonBackground = '#2196F3';
                isDisabled = false;
              } else if (isPaidCashOrCredit) {
                buttonLabel = 'CHECKOUT';
                buttonBackground = '#ccc';
                isDisabled = true;
              } else if (isSpillageTab) {
                buttonBackground = '#ccc';
                isDisabled = true;
              } else if (checkoutLoading) {
                buttonLabel = 'PROCESSING...';
                buttonBackground = '#666';
                isDisabled = true;
              } else if (selectedItems.length === 0) {
                buttonBackground = '#ccc';
                isDisabled = true;
              }
              
              return (
                <button
                  onClick={() => {
                    // If items are selected, duplicate them (works on spillage tab too)
                    if (selectedReceiptIndices.size > 0) {
                      if (onDuplicateItems) {
                        onDuplicateItems(activeTabId, Array.from(selectedReceiptIndices));
                      }
                      setSelectedReceiptIndices(new Set());
                      return;
                    }
                    // REOPEN invoice tab - move back to unpaid
                    if (isInvoiceTab && onReopenTab) {
                      onReopenTab(activeTabId);
                      return;
                    }
                    // Normal checkout - disabled for spillage tab and paid tabs
                    if (isSpillageTab || isPaidCashOrCredit) return;
                    onCheckout && onCheckout();
                  }}
                  disabled={isDisabled}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                    background: buttonBackground,
                    color: '#fff',
                    fontSize: `${Math.max(12, outerWidth / 25)}px`,
                    fontWeight: 600,
                    fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    opacity: isDisabled ? 0.4 : 1
                  }}
                >
                  {buttonLabel}
                </button>
              );
            })()}
          </div>
        )}
        
      </div>
      
      {/* Fixed Footer Bar - always visible at bottom like header */}
      <div style={{
        position: 'absolute',
        bottom: handleBarHeight,
        left: 0,
        right: 0,
        height: `${footerHeight}px`,
        background: '#fff',
        zIndex: 104,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        boxShadow: '0 -1px 3px rgba(0,0,0,0.1)'
      }}>
        {/* Top row - Tab P# left, TOTAL right */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 8px'
        }}>
          {/* Tab P# - top left, with custom name to the right if exists */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', flex: 1, marginRight: '12px' }}>
            {activeTabId && tabs.find(t => t.id === activeTabId) && (
              <>
                <span style={{
                  color: '#800080',
                  fontSize: `${Math.max(10, footerHeight * 0.28)}px`,
                  fontWeight: 600,
                  fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                  flexShrink: 0
                }}>
                  {tabs.find(t => t.id === activeTabId)?.name}
                </span>
                {tabs.find(t => t.id === activeTabId)?.customName && (
                  <span style={{
                    color: '#333',
                    fontSize: `${Math.max(10, footerHeight * 0.28)}px`,
                    fontWeight: 500,
                    fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {tabs.find(t => t.id === activeTabId)?.customName?.toUpperCase()}
                  </span>
                )}
              </>
            )}
          </div>
          {/* TOTAL - top right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              color: '#d0d0d0',
              fontSize: `${Math.max(10, footerHeight * 0.3)}px`,
              fontWeight: 500,
              fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
            }}>
              TOTAL:
            </span>
            <span style={{
              color: '#000',
              fontSize: `${Math.max(14, footerHeight * 0.45)}px`,
              fontWeight: 600,
              fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
            }}>
              ${total.toFixed(2)}
            </span>
          </div>
        </div>
        
        {/* Bottom row - Category Counters left, Action Visualizer right */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 8px'
        }}>
          {/* Category Counters - left */}
          <div style={{ display: 'flex', gap: '12px' }}>
            {CATEGORIES.map((cat) => (
              <span 
                key={cat.id}
                style={{
                  color: '#d0d0d0',
                  fontSize: `${Math.max(8, footerHeight * 0.18)}px`,
                  fontWeight: 500,
                  fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
                }}
              >
                {cat.label}{categoryCounts[cat.id] || 0}
              </span>
            ))}
          </div>
          {/* Action Visualizer - right: show Tip for selected archived tab, Last Action for others */}
          {showArchivedTabs ? (
            // Show payment method and tip for the selected archived/paid tab
            (() => {
              const activeTab = tabs.find(t => t.id === activeTabId);
              const tabTip = activeTab?.tipAmount || 0;
              const paymentType = activeTab?.paymentMethod || (activeTab?.isInvoice ? 'invoice' : 'credit');
              const paymentLabel = paymentType === 'cash' ? 'CASH' : paymentType === 'invoice' ? 'INVOICE' : 'CREDIT';
              const paymentColor = paymentType === 'cash' ? '#2196f3' : paymentType === 'invoice' ? '#f59e0b' : '#4caf50';
              return (
                <span style={{
                  fontSize: `${Math.max(8, footerHeight * 0.18)}px`,
                  fontWeight: 500,
                  fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
                }}>
                  <span style={{ color: '#d0d0d0' }}>Payment: </span>
                  <span style={{ color: paymentColor }}>{paymentLabel}</span>
                  <span style={{ color: '#d0d0d0' }}> — Tip: </span>
                  <span style={{ color: tabTip > 0 ? '#22c55e' : '#d0d0d0' }}>
                    ${tabTip.toFixed(2)}
                  </span>
                </span>
              );
            })()
          ) : (
            lastAction && (
              <span 
                key={actionKey}
                style={{
                  fontSize: `${Math.max(8, footerHeight * 0.18)}px`,
                  fontWeight: 500,
                  fontFamily: "'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                  animation: 'fadeSlideIn 0.5s ease-out'
                }}>
                {lastAction.tabName && (
                  <span style={{ color: '#d0d0d0' }}>{lastAction.tabName} </span>
                )}
                <span style={{ color: lastAction.type === 'add' ? '#22c55e' : '#ef4444' }}>
                  {lastAction.type === 'add' ? '+' : '-'} {lastAction.itemName}
                </span>
              </span>
            )
          )}
        </div>
      </div>
      
      {/* Subfooter - white rectangle below the footer */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: `${handleBarHeight}px`,
        background: '#fff',
        zIndex: 104
      }} />
      
      {/* Confirmation Popup */}
      {itemToRemove && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 200
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '16px 24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            maxWidth: '80%'
          }}>
            <span style={{
              fontSize: `${Math.max(12, outerWidth / 24)}px`,
              fontWeight: 500,
              color: '#333',
              textAlign: 'center'
            }}>
              Remove "{toTitleCase(itemToRemove.item.name)}"?
            </span>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  onRemoveItem(itemToRemove.index);
                  setItemToRemove(null);
                }}
                style={{
                  background: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '8px 20px',
                  fontSize: `${Math.max(11, outerWidth / 28)}px`,
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Remove
              </button>
              <button
                onClick={() => setItemToRemove(null)}
                style={{
                  background: '#e5e5e5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '8px 20px',
                  fontSize: `${Math.max(11, outerWidth / 28)}px`,
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Close Tab / Archive Tab Confirmation Popup */}
      {showCloseTabConfirm && activeTabId && (() => {
        const currentTab = tabs.find(t => t.id === activeTabId);
        const isPaid = currentTab?.status === 'paid';
        const hasItems = currentTab?.items?.length > 0;
        const canClose = !hasItems && !isPaid; // Can only close empty, unpaid tabs
        
        return (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '16px 24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              maxWidth: '80%'
            }}>
              <span style={{
                fontSize: `${Math.max(12, outerWidth / 24)}px`,
                fontWeight: 500,
                color: '#333',
                textAlign: 'center'
              }}>
                {isPaid 
                  ? `Archive tab "${(currentTab?.customName || currentTab?.name)?.toUpperCase()}"?`
                  : hasItems
                    ? `Cannot close tab with items. Remove items first.`
                    : `Close tab "${(currentTab?.customName || currentTab?.name)?.toUpperCase()}"?`
                }
              </span>
              <div style={{ display: 'flex', gap: '12px' }}>
                {(isPaid || canClose) && (
                  <button
                    onClick={() => {
                      if (isPaid) {
                        // Archive: mark as archived (removes from UI but keeps in data)
                        onArchiveTab(activeTabId);
                      } else {
                        // Close: delete empty tab
                        onDeleteTab(activeTabId);
                      }
                      setShowCloseTabConfirm(false);
                    }}
                    style={{
                      background: isPaid ? '#22c55e' : '#ef4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '8px 20px',
                      fontSize: `${Math.max(11, outerWidth / 28)}px`,
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    {isPaid ? 'ARCHIVE' : 'CLOSE'}
                  </button>
                )}
                <button
                  onClick={() => setShowCloseTabConfirm(false)}
                  style={{
                    background: '#e5e5e5',
                    color: '#333',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '8px 20px',
                    fontSize: `${Math.max(11, outerWidth / 28)}px`,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  {(isPaid || canClose) ? 'CANCEL' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      
      {/* Invoice Tab Confirmation Popup */}
      {showInvoiceConfirm && activeTabId && (() => {
        const currentTab = tabs.find(t => t.id === activeTabId);
        const tabTotal = (currentTab?.items || []).reduce((sum, item) => sum + (item.price || item.finalPrice || 0), 0);
        
        return (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '16px 24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              maxWidth: '80%'
            }}>
              <span style={{
                fontSize: `${Math.max(14, outerWidth / 22)}px`,
                fontWeight: 600,
                color: '#f59e0b',
                textAlign: 'center'
              }}>
                INVOICE
              </span>
              <span style={{
                fontSize: `${Math.max(12, outerWidth / 24)}px`,
                fontWeight: 500,
                color: '#333',
                textAlign: 'center'
              }}>
                Send "{(currentTab?.customName || currentTab?.name)?.toUpperCase()}" to invoiced tabs?
              </span>
              <span style={{
                fontSize: `${Math.max(11, outerWidth / 26)}px`,
                color: '#666',
                textAlign: 'center'
              }}>
                Total: ${tabTotal.toFixed(2)} (will be collected later)
              </span>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button
                  onClick={() => {
                    // Mark tab as invoiced (paid but with isInvoice flag)
                    if (onInvoiceTab) {
                      onInvoiceTab(activeTabId);
                    }
                    setShowInvoiceConfirm(false);
                  }}
                  style={{
                    background: '#f59e0b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '10px 24px',
                    fontSize: `${Math.max(11, outerWidth / 28)}px`,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  YES
                </button>
                <button
                  onClick={() => setShowInvoiceConfirm(false)}
                  style={{
                    background: '#e5e5e5',
                    color: '#333',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '10px 24px',
                    fontSize: `${Math.max(11, outerWidth / 28)}px`,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  NO
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      
      {/* Move Items Modal - Select destination tab */}
      {showMoveModal && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 200
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '16px 24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            maxWidth: '80%',
            minWidth: '200px'
          }}>
            <span style={{
              fontSize: `${Math.max(12, outerWidth / 24)}px`,
              fontWeight: 500,
              color: '#333',
              textAlign: 'center'
            }}>
              SELECT TAB
            </span>
            {tabs.filter(t => t.id !== activeTabId).length === 0 ? (
              <span style={{
                fontSize: `${Math.max(10, outerWidth / 30)}px`,
                color: '#999',
                textAlign: 'center',
                padding: '8px'
              }}>
                No other tabs available
              </span>
            ) : (
              <div style={{
                width: '100%',
                maxHeight: '200px',
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {tabs.filter(t => t.id !== activeTabId && t.status !== 'archived').map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (onMoveItems) {
                        onMoveItems(activeTabId, tab.id, Array.from(selectedReceiptIndices));
                      }
                      setSelectedReceiptIndices(new Set());
                      setShowMoveModal(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      fontSize: `${Math.max(14, outerWidth / 24)}px`,
                      fontWeight: 500,
                      color: '#333',
                      background: '#f0f0f0',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      flexShrink: 0
                    }}
                  >
                    {(tab.customName || tab.name).toUpperCase()}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => setShowMoveModal(false)}
              style={{
                background: '#e5e5e5',
                color: '#333',
                border: 'none',
                borderRadius: '20px',
                padding: '8px 20px',
                fontSize: `${Math.max(11, outerWidth / 28)}px`,
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}
      
      {/* Spend Limit Modal - Set/Adjust tab spend limit */}
      {showSpendLimitModal && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 300
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '24px',
            width: '80%',
            maxWidth: '300px',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
              Set Spend Limit
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#666' }}>
              {(() => {
                const tab = tabs.find(t => t.id === spendLimitTabId);
                return tab ? (tab.customName || tab.name).toUpperCase() : 'Tab';
              })()}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
              <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>$</span>
              <input
                type="number"
                value={spendLimitInput}
                onChange={(e) => setSpendLimitInput(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '120px',
                  padding: '12px',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  border: '2px solid #ddd',
                  borderRadius: '8px'
                }}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={() => {
                  // Save the limit
                  const limit = parseFloat(spendLimitInput) || 0;
                  const tab = tabs.find(t => t.id === spendLimitTabId);
                  if (tab && limit > 0) {
                    tab.spendLimit = limit;
                  }
                  setShowSpendLimitModal(false);
                  setSpendLimitTabId(null);
                  setSpendLimitInput('');
                }}
                style={{
                  padding: '10px 32px',
                  background: '#800080',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                SET LIMIT
              </button>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={() => {
                    // Clear the limit
                    const tab = tabs.find(t => t.id === spendLimitTabId);
                    if (tab) {
                      tab.spendLimit = null;
                    }
                    setShowSpendLimitModal(false);
                    setSpendLimitTabId(null);
                    setSpendLimitInput('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#f5f5f5',
                    color: '#666',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  CLEAR
                </button>
                <button
                  onClick={() => {
                    setShowSpendLimitModal(false);
                    setSpendLimitTabId(null);
                    setSpendLimitInput('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#e5e5e5',
                    color: '#333',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Spend Limit Warning Popup */}
      {showSpendLimitWarning && spendLimitWarningTab && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 300
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '24px',
            width: '85%',
            maxWidth: '320px',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 600, color: '#f59e0b' }}>
              Spend Limit Warning
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#333', lineHeight: 1.4 }}>
              <strong>{(spendLimitWarningTab.customName || spendLimitWarningTab.name).toUpperCase()}</strong> has almost reached its spend limit!
            </p>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#666' }}>
              Limit: ${spendLimitWarningTab.spendLimit?.toFixed(2)} | Current: ${((spendLimitWarningTab.items || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)).toFixed(2)}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  // Open adjust limit modal
                  setShowSpendLimitWarning(false);
                  setSpendLimitTabId(spendLimitWarningTab.id);
                  setSpendLimitInput(spendLimitWarningTab.spendLimit ? String(spendLimitWarningTab.spendLimit) : '');
                  setShowSpendLimitModal(true);
                  setSpendLimitWarningTab(null);
                }}
                style={{
                  padding: '12px 24px',
                  background: '#800080',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                ADJUST LIMIT
              </button>
              <button
                onClick={() => {
                  setShowSpendLimitWarning(false);
                  setSpendLimitWarningTab(null);
                }}
                style={{
                  padding: '12px 24px',
                  background: '#e5e5e5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                EXIT
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Item Info Sheet - Editing Flow (Long-press) */}
      {editingItem && (
        <>
          {/* Backdrop - tap to close */}
          <div 
            onClick={() => {
              // Save modifiers and close
              if (onUpdateItemModifiers && editingItem._id) {
                onUpdateItemModifiers(editingItem._id, editingItem.modifiers || []);
              }
              setEditingItem(null);
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.3)',
              zIndex: 200
            }}
          />
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '75%',
            background: '#fff',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.2)',
            zIndex: 201,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
          {/* Header Row */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '16px',
            borderBottom: '1px solid #eee',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{
                fontSize: `${Math.max(14, outerWidth / 20)}px`,
                fontWeight: 600,
                color: '#333'
              }}>
                {toTitleCase(editingItem.name)}
              </span>
              <span style={{
                fontSize: `${Math.max(12, outerWidth / 24)}px`,
                fontWeight: 500,
                color: '#d0d0d0'
              }}>
                ${(parseFloat(editingItem.price) || 0).toFixed(2)}
                {editingItem.costPerUnit > 0 && (
                  <span style={{ color: '#999' }}> | ${(parseFloat(editingItem.costPerUnit) || 0).toFixed(2)}</span>
                )}
              </span>
            </div>
            <button
              onClick={() => {
                const currentModifiers = editingItem.modifiers || [];
                if (currentModifiers.length >= 10) {
                  setShowMaxModifiersAlert(true);
                } else {
                  setNewModifierName('');
                  setNewModifierPrice('');
                  setNewModifierLink(false);
                  setShowAddModifierModal(true);
                }
              }}
              style={{
                background: '#800080',
                color: '#fff',
                border: 'none',
                borderRadius: '20px',
                padding: '8px 16px',
                fontSize: `${Math.max(11, outerWidth / 28)}px`,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              NEW MODIFIER
            </button>
          </div>
          
          {/* Modifiers Display */}
          <div style={{
            flex: 1,
            padding: '16px',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignContent: 'start'
            }}>
              {(() => {
                const modifiers = editingItem.modifiers || [];
                const categoryName = CATEGORIES.find(c => c.id === activeCategory)?.fullName || activeCategory;
                
                if (modifiers.length === 0) {
                  // Show category as default modifier
                  return (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#f0f0f0',
                      borderRadius: '20px',
                      padding: '8px 14px',
                      fontSize: `${Math.max(11, outerWidth / 28)}px`,
                      color: '#999',
                      fontStyle: 'italic'
                    }}>
                      {categoryName}
                    </div>
                  );
                }
                
                return modifiers.map((mod, idx) => {
                  const modPrice = mod.priceAdjustment ?? mod.price ?? 0;
                  const modIsLink = mod.isLink ?? !!mod.linkedItemId;
                  return (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: modIsLink ? '#e6f0ff' : '#f0f0f0',
                        borderRadius: '20px',
                        padding: '8px 14px',
                        fontSize: `${Math.max(11, outerWidth / 28)}px`,
                        color: '#333'
                      }}
                    >
                      <span>{mod.name}</span>
                      {modPrice !== 0 && (
                        <span style={{ color: modPrice > 0 ? '#22c55e' : '#ef4444' }}>
                          {modPrice > 0 ? '+' : ''}{modPrice.toFixed(2)}
                        </span>
                      )}
                      {modIsLink && (
                        <span style={{ color: '#3b82f6', fontSize: '10px' }}>LINK</span>
                      )}
                      <button
                        onClick={() => {
                          const newModifiers = [...modifiers];
                          newModifiers.splice(idx, 1);
                          setEditingItem({ ...editingItem, modifiers: newModifiers });
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#999',
                          cursor: 'pointer',
                          padding: '0 2px',
                          fontSize: '14px',
                          lineHeight: 1
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
          
          {/* Close Button */}
          <div style={{
            padding: '16px',
            borderTop: '1px solid #eee',
            flexShrink: 0
          }}>
            <button
              onClick={() => {
                // Save modifiers back to item
                if (onUpdateItemModifiers && editingItem._id) {
                  onUpdateItemModifiers(editingItem._id, editingItem.modifiers || []);
                }
                setEditingItem(null);
              }}
              style={{
                width: '100%',
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '14px',
                fontSize: `${Math.max(12, outerWidth / 24)}px`,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              DONE
            </button>
          </div>
        </div>
        </>
      )}
      
      {/* Add Modifier Modal */}
      {showAddModifierModal && editingItem && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 202
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            width: '85%',
            maxWidth: '320px'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: `${Math.max(11, outerWidth / 30)}px`,
                color: '#666',
                marginBottom: '6px'
              }}>
                Modifier Name
              </label>
              <input
                type="text"
                value={newModifierName}
                onChange={(e) => setNewModifierName(e.target.value.slice(0, 16))}
                maxLength={16}
                placeholder="Max 16 characters"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: `${Math.max(12, outerWidth / 26)}px`,
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: `${Math.max(11, outerWidth / 30)}px`,
                color: '#666',
                marginBottom: '6px'
              }}>
                Price Adjustment
              </label>
              <input
                type="number"
                value={newModifierPrice}
                onChange={(e) => setNewModifierPrice(e.target.value)}
                placeholder="0"
                step="1"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: `${Math.max(12, outerWidth / 26)}px`,
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={newModifierLink}
                  onChange={(e) => setNewModifierLink(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{
                  fontSize: `${Math.max(12, outerWidth / 26)}px`,
                  color: '#333'
                }}>
                  LINK
                </span>
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  if (newModifierName.trim()) {
                    const newMod = {
                      name: newModifierName.trim(),
                      priceAdjustment: parseInt(newModifierPrice, 10) || 0,
                      isLink: newModifierLink
                    };
                    const currentModifiers = editingItem.modifiers || [];
                    setEditingItem({
                      ...editingItem,
                      modifiers: [newMod, ...currentModifiers]
                    });
                    setShowAddModifierModal(false);
                  }
                }}
                style={{
                  flex: 1,
                  background: '#800080',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: `${Math.max(12, outerWidth / 26)}px`,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                SAVE
              </button>
              <button
                onClick={() => setShowAddModifierModal(false)}
                style={{
                  flex: 1,
                  background: '#e5e5e5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: `${Math.max(12, outerWidth / 26)}px`,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Max Modifiers Alert */}
      {showMaxModifiersAlert && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 203
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: `${Math.max(14, outerWidth / 22)}px`,
              fontWeight: 600,
              color: '#333',
              marginBottom: '16px'
            }}>
              10 Modifiers Max
            </div>
            <button
              onClick={() => setShowMaxModifiersAlert(false)}
              style={{
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                fontSize: `${Math.max(12, outerWidth / 26)}px`,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              BACK
            </button>
          </div>
        </div>
      )}
      
      {/* Modifier Selection Popup - Ordering Flow */}
      {orderingItem && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 200
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            width: '85%',
            maxWidth: '320px'
          }}>
            <div style={{
              fontSize: `${Math.max(14, outerWidth / 22)}px`,
              fontWeight: 600,
              color: '#333',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              {toTitleCase(orderingItem.name)}
            </div>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '16px',
              justifyContent: 'center'
            }}>
              {(() => {
                const modifiers = orderingItem.modifiers || [];
                const hasLinkSelected = selectedModifiers.some(m => m.isLink);
                
                return modifiers.map((mod, idx) => {
                  const isSelected = selectedModifiers.some(m => m.name === mod.name);
                  const modIsLink = mod.isLink ?? !!mod.linkedItemId;
                  const isDisabled = hasLinkSelected && !modIsLink;
                  
                  return (
                    <button
                      key={idx}
                      disabled={isDisabled}
                      onClick={() => {
                        // Normalize modifier to ensure priceAdjustment exists
                        const normalizedMod = {
                          ...mod,
                          priceAdjustment: mod.priceAdjustment ?? mod.price ?? 0,
                          isLink: mod.isLink ?? !!mod.linkedItemId
                        };
                        if (!normalizedMod.isLink) {
                          // Non-LINK: add item immediately with this modifier
                          setFlashingItemId(orderingItem._id);
                          onItemClick(orderingItem, normalizedMod);
                          setTimeout(() => setFlashingItemId(null), 500);
                          setOrderingItem(null);
                          setSelectedModifiers([]);
                        } else {
                          // LINK: toggle selection
                          if (isSelected) {
                            setSelectedModifiers(selectedModifiers.filter(m => m.name !== mod.name));
                          } else {
                            setSelectedModifiers([...selectedModifiers, normalizedMod]);
                          }
                        }
                      }}
                      style={{
                        background: isSelected ? '#800080' : (isDisabled ? '#f5f5f5' : '#f0f0f0'),
                        color: isSelected ? '#fff' : (isDisabled ? '#ccc' : '#333'),
                        border: 'none',
                        borderRadius: '20px',
                        padding: '10px 16px',
                        fontSize: `${Math.max(11, outerWidth / 28)}px`,
                        fontWeight: 500,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.5 : 1
                      }}
                    >
                      {mod.name}
                      {(mod.priceAdjustment ?? mod.price ?? 0) !== 0 && (
                        <span style={{ marginLeft: '4px' }}>
                          {(mod.priceAdjustment ?? mod.price ?? 0) > 0 ? '+' : ''}{(mod.priceAdjustment ?? mod.price ?? 0).toFixed(2)}
                        </span>
                      )}
                    </button>
                  );
                });
              })()}
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              {selectedModifiers.length > 0 && selectedModifiers.some(m => m.isLink) && (
                <button
                  onClick={() => {
                    // Add item with all selected LINK modifiers
                    const modifierNames = selectedModifiers.map(m => m.name).join(', ');
                    const totalPriceAdjustment = selectedModifiers.reduce((sum, m) => sum + (m.priceAdjustment || 0), 0);
                    setFlashingItemId(orderingItem._id);
                    onItemClick(orderingItem, { name: modifierNames, priceAdjustment: totalPriceAdjustment });
                    setTimeout(() => setFlashingItemId(null), 500);
                    setOrderingItem(null);
                    setSelectedModifiers([]);
                  }}
                  style={{
                    flex: 1,
                    background: '#800080',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: `${Math.max(12, outerWidth / 26)}px`,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  ADD
                </button>
              )}
              <button
                onClick={() => {
                  setOrderingItem(null);
                  setSelectedModifiers([]);
                }}
                style={{
                  flex: 1,
                  background: '#e5e5e5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: `${Math.max(12, outerWidth / 26)}px`,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const registerGlobalCallback = (name, callback) => {
  if (!window[name]) {
    window[name] = callback;
    return () => {
      if (window[name] === callback) {
        window[name] = null;
      }
    };
  }
  
  const existing = window[name];
  if (existing.isMulticast) {
    existing.listeners.push(callback);
    return () => {
      existing.listeners = existing.listeners.filter(l => l !== callback);
      if (existing.listeners.length === 1) {
        window[name] = existing.listeners[0];
      } else if (existing.listeners.length === 0) {
        window[name] = null;
      }
    };
  } else {
    const multicast = (...args) => {
      multicast.listeners.forEach(l => {
        try { l(...args); } catch (e) { console.error('[Multicast Error]', e); }
      });
    };
    multicast.isMulticast = true;
    multicast.listeners = [existing, callback];
    window[name] = multicast;
    return () => {
      if (window[name] === multicast) {
        multicast.listeners = multicast.listeners.filter(l => l !== callback);
        if (multicast.listeners.length === 1) {
          window[name] = multicast.listeners[0];
        } else if (multicast.listeners.length === 0) {
          window[name] = null;
        }
      }
    };
  }
};

export default function POSSalesUI({ layoutMode = 'auto', outerWidth: propOuterWidth, outerHeight: propOuterHeight, isStandalone = false }) {
  // layoutMode: 'vertical' | 'horizontal' | 'auto'
  // When layoutMode is 'vertical' or 'horizontal', force that layout and ignore device orientation
  // When layoutMode is 'auto', detect device orientation and switch layouts automatically:
  //   - Horizontal (landscape): Show MenuGallery2 customer view
  //   - Vertical (portrait): Show POS admin interface
  const forcedOrientation = layoutMode === 'auto' ? null : layoutMode;
  const [orientation, setOrientation] = useState(forcedOrientation || 'vertical');
  
  // Device orientation detection for layoutMode='auto'
  useEffect(() => {
    if (layoutMode !== 'auto') return;
    
    const detectOrientation = () => {
      const isHorizontal = window.innerWidth > window.innerHeight;
      setOrientation(isHorizontal ? 'horizontal' : 'vertical');
      console.log('[POS] Device orientation:', isHorizontal ? 'HORIZONTAL' : 'VERTICAL');
    };
    
    // Check on mount
    detectOrientation();
    
    // Listen for changes
    window.addEventListener('resize', detectOrientation);
    window.addEventListener('orientationchange', detectOrientation);
    
    return () => {
      window.removeEventListener('resize', detectOrientation);
      window.removeEventListener('orientationchange', detectOrientation);
    };
  }, [layoutMode]);
  
  // If layoutMode is forced (not 'auto'), update orientation to match
  useEffect(() => {
    if (forcedOrientation) {
      setOrientation(forcedOrientation);
    }
  }, [forcedOrientation]);
  const [frameRef, frameSize] = useMeasuredSize();
  const isHorizontalDevice = (layoutMode === 'auto' && orientation === 'horizontal') || layoutMode === 'horizontal';
  const isVerticalDevice = (layoutMode === 'auto' && orientation === 'vertical') || layoutMode === 'vertical';
  const [activeCategory, setActiveCategory] = useState('cocktails');
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastAction, setLastAction] = useState(null); // { type: 'add' | 'remove', itemName: string }
  
  // localStorage-backed tab and event management
  const {
    eventId,
    eventName,
    eventStarted,
    tabs,
    setTabs,
    nextTabNumber,
    setNextTabNumber,
    activeTabId,
    setActiveTabId,
    eventSetupData,
    setEventSetupData,
    uiState,
    setUiState,
    startEvent,
    clearEvent,
    hasActiveEvent,
  } = usePosLocalStorage();
  
  // Derive UI state values from persisted uiState
  const showSummaryView = uiState.showSummaryView;
  const showEventSetup = uiState.showEventSetup;
  const isPostEventEdit = uiState.isPostEventEdit;
  const testMode = uiState.testMode ?? false;
  
  // Helper functions to update UI state
  const setShowSummaryView = (value) => setUiState(prev => ({ ...prev, showSummaryView: typeof value === 'function' ? value(prev.showSummaryView) : value }));
  const setShowEventSetup = (value) => setUiState(prev => ({ ...prev, showEventSetup: typeof value === 'function' ? value(prev.showEventSetup) : value }));
  const setIsPostEventEdit = (value) => setUiState(prev => ({ ...prev, isPostEventEdit: typeof value === 'function' ? value(prev.isPostEventEdit) : value }));
  const setTestMode = (value) => setUiState(prev => ({ ...prev, testMode: typeof value === 'function' ? value(prev.testMode ?? false) : value }));

  // Cumulative time offset in minutes added by +15 MIN button during test mode
  // Resets to 0 when a new event starts
  const [testTimeOffsetMins, setTestTimeOffsetMins] = useState(0);
  
  // Event management UI state (showSummaryView, showEventSetup, isPostEventEdit are now derived from uiState above)
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEndEventModal, setShowEndEventModal] = useState(false);
  const [eventSummary, setEventSummary] = useState(null);
  const [newEventName, setNewEventName] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [showEventSaveSuccess, setShowEventSaveSuccess] = useState(false);
  const [showEndEventButton, setShowEndEventButton] = useState(false);
  const [showForceQuitConfirm, setShowForceQuitConfirm] = useState(false);
  const endEventTimeoutRef = useRef(null);
  const forceQuitLongPressRef = useRef(null);
  const saveToServerRef = useRef(null); // Ref for saveToServer function to use in earlier callbacks
  const sendCheckoutStageRef = useRef(null); // Ref for sendCheckoutStage to avoid circular dependency
  
  // Event Setup data - now managed by usePosLocalStorage hook for persistence
  
  // Checkout state
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);
  const [lastCheckoutResult, setLastCheckoutResult] = useState(null);
  
  // Payment status state for Square webhook updates
  // States: null, 'pending', 'payment_success', 'payment_canceled', 'payment_failed', 'payment_expired'
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [currentCheckoutId, setCurrentCheckoutId] = useState(null);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState(null);
  
  // Checkout mode for horizontal view - when true, shows receipt/tipping screen instead of MenuGallery2
  // This state is synced via WebSocket so it works across different devices
  const [checkoutMode, setCheckoutMode] = useState(false);
  const [checkoutItems, setCheckoutItems] = useState([]);
  const [checkoutSubtotal, setCheckoutSubtotal] = useState(0);
  const [checkoutTabInfo, setCheckoutTabInfo] = useState(null);
  const [showCustomTip, setShowCustomTip] = useState(false); // Show custom tip input (local only)
  const [customTipAmount, setCustomTipAmount] = useState(''); // Custom tip in dollars (local only)
  const [showTabView, setShowTabView] = useState(false); // Toggle between tip view and receipt view
  const [showScanCard, setShowScanCard] = useState(false); // Show scan card screen after tip selection
  const [selectedTipAmount, setSelectedTipAmount] = useState(0); // Store selected tip for scan card screen
  const [checkoutStage, setCheckoutStage] = useState(''); // Track checkout stage: 'tip', 'tab', 'payment', 'processing', 'success', 'failed'
  const [showCheckoutOptions, setShowCheckoutOptions] = useState(false); // Show Credit/Cash/Invoice popup
  const [paymentMethod, setPaymentMethod] = useState(''); // 'credit', 'cash', 'invoice'
  const [cardBrand, setCardBrand] = useState(''); // Card brand from Stripe (visa, mastercard, etc.)
  const [cardLast4, setCardLast4] = useState(''); // Last 4 digits of card
  const [cashTendered, setCashTendered] = useState(''); // Amount of cash given by customer
  
  // Receipt prompt state (horizontal view after payment success)
  const [showReceiptPrompt, setShowReceiptPrompt] = useState(false); // Show "Would you like a receipt?" screen
  const [showReceiptKeypad, setShowReceiptKeypad] = useState(false); // Show email/phone keypad
  const [receiptContact, setReceiptContact] = useState(''); // Email or phone number entered
  const [receiptSending, setReceiptSending] = useState(false); // Sending receipt in progress
  const [showSymbolKeypad, setShowSymbolKeypad] = useState(false); // Toggle between letter and symbol keypad
  const receiptTimerRef = useRef(null); // 10 second auto-dismiss timer
  
  // Tab spend limit state
  const [showSpendLimitWarning, setShowSpendLimitWarning] = useState(false);
  const [spendLimitWarningTab, setSpendLimitWarningTab] = useState(null);
  
  // Checkout timeout - 1 minute idle returns to menu
  const checkoutTimeoutRef = useRef(null);
  const CHECKOUT_TIMEOUT_MS = 60000; // 1 minute
  
  // Stripe Terminal M2 Reader state
  const [showReaderSetup, setShowReaderSetup] = useState(false);
  const [readerConnected, setReaderConnected] = useState(false);
  const [readerInfo, setReaderInfo] = useState(null);
  const [discoveredReaders, setDiscoveredReaders] = useState([]);
  const [scanningReaders, setScanningReaders] = useState(false);
  
  // Remote reader status (from other device via WebSocket)
  const [remoteReaderConnected, setRemoteReaderConnected] = useState(false);
  const [remoteReaderIsSimulated, setRemoteReaderIsSimulated] = useState(false);
  
  // Ref to track current readerConnected state (avoids stale closure in callbacks)
  const readerConnectedRef = useRef(false);
  useEffect(() => {
    readerConnectedRef.current = readerConnected;
  }, [readerConnected]);
  
  // Ref to track current checkoutTabInfo (avoids stale closure in callbacks)
  const checkoutTabInfoRef = useRef(null);
  useEffect(() => {
    checkoutTabInfoRef.current = checkoutTabInfo;
  }, [checkoutTabInfo]);
  
  // WebSocket handlers for checkout sync across devices
  const handleWsCheckoutStart = useCallback((data) => {
    console.log('[POS] WebSocket checkout_start received:', data);
    setCheckoutMode(true);
    setCheckoutItems(data.items || []);
    setCheckoutSubtotal(data.subtotal || 0);
    setCheckoutTabInfo(data.tabInfo || null);
    setShowCustomTip(false);
    setCustomTipAmount('');
    setShowTabView(false);
    
    // Set payment method and checkout stage based on incoming data
    if (data.paymentMethod === 'cash') {
      setPaymentMethod('cash');
      setCheckoutStage('cash');
      setCashTendered('');
    } else {
      setPaymentMethod(data.paymentMethod || 'credit');
      setCheckoutStage('tip'); // Start at tip selection stage for credit
    }
  }, []);
  
  const handleWsCheckoutComplete = useCallback((data) => {
    console.log('[POS] WebSocket checkout_complete received:', data);
    
    // Mark tab as paid and store tip amount
    if (data.tabId) {
      setTabs(prev => prev.map(t => 
        t.id === data.tabId 
          ? { ...t, status: 'paid', paidAt: new Date().toISOString(), tipAmount: data.tipAmount || 0, paymentMethod: data.paymentMethod || 'credit' }
          : t
      ));
      setActiveTabId(null);
      
      // Save to server after payment completed (use ref since saveToServer is defined later)
      setTimeout(() => saveToServerRef.current?.('payment-complete'), 500);
    }
    
    // On horizontal device: don't immediately clear if showing success animation
    // The animation flow (handleWsCheckoutStage 'success' handler) will clean up after 3s + receipt
    if (isHorizontalDevice) {
      // Let the success animation flow handle the cleanup - just sync tab state above
      return;
    }
    
    setCheckoutMode(false);
    setCheckoutItems([]);
    setCheckoutSubtotal(0);
    setCheckoutTabInfo(null);
    setShowScanCard(false);
    setSelectedTipAmount(0);
    setCheckoutStage('');
  }, [setTabs, setActiveTabId, orientation, layoutMode]);
  
  const handleWsCheckoutCancel = useCallback(() => {
    console.log('[POS] WebSocket checkout_cancel received');
    setCheckoutMode(false);
    setCheckoutItems([]);
    setCheckoutSubtotal(0);
    setCheckoutTabInfo(null);
    setShowScanCard(false);
    setSelectedTipAmount(0);
    setCheckoutStage('');
  }, []);
  
  // Handle tip updates from horizontal device - sync tip amount across all devices and save to tab
  const handleWsTipUpdate = useCallback((data) => {
    console.log('[POS] WebSocket tip_update received:', data);
    if (data.tipAmount !== undefined) {
      setSelectedTipAmount(data.tipAmount);
      // Save tip to the tab (only if tab is not already archived)
      if (data.tabId) {
        setTabs(prev => prev.map(t => 
          t.id === data.tabId && t.status !== 'archived'
            ? { ...t, tipAmount: data.tipAmount }
            : t
        ));
      }
    }
  }, [setTabs]);
  
  // Handle checkout stage updates from other devices (for vertical screen sync)
  const handleWsCheckoutStage = useCallback((data) => {
    console.log('[POS] WebSocket checkout_stage received:', data.stage, data.paymentMethod ? `(${data.paymentMethod})` : '');
    
    // Vertical device IGNORES success/failed stages from WebSocket
    // It manages its own state locally in handleCompleteCashPayment / onPaymentComplete
    // Receiving its own broadcast would re-set checkoutStage after the 1.5s timeout clears it
    if (isVerticalDevice && (data.stage === 'success' || data.stage === 'failed')) {
      console.log('[POS] Vertical ignoring own success/failed broadcast');
      return;
    }
    
    // Update checkout stage (for horizontal receiving from vertical, or other stages)
    setCheckoutStage(data.stage || '');
    
    // If stage is empty, also exit checkoutMode on vertical to clear header notification
    if (!data.stage && isVerticalDevice) {
      setCheckoutMode(false);
    }
    
    // Keep checkoutMode true on vertical for receipt-related stages so notification shows
    if (isVerticalDevice && (data.stage === 'receipt_request' || data.stage === 'receipt_sending' || data.stage === 'receipt_sent' || data.stage === 'receipt_canceled')) {
      setCheckoutMode(true);
    }
    
    // Update payment method if provided
    if (data.paymentMethod) {
      setPaymentMethod(data.paymentMethod);
    }
    
    // Horizontal device only: show success animation + receipt prompt flow
    if (data.stage === 'success') {
      if (isHorizontalDevice) {
        // Ensure checkoutMode is true so the success animation shows (not the menu)
        setCheckoutMode(true);
        setPaymentStatus('payment_success');
        // After 3 seconds of success animation (Thank you!), show receipt prompt
        setTimeout(() => {
          setPaymentStatus(null);
          setShowReceiptPrompt(true);
          // Send receipt_request to vertical to show "Requesting Receipt" with spinner
          sendCheckoutStage('receipt_request');
          // Start 10 second timer for auto-dismiss
          if (receiptTimerRef.current) clearTimeout(receiptTimerRef.current);
          receiptTimerRef.current = setTimeout(() => {
            setShowReceiptPrompt(false);
            setCheckoutMode(false);
            setCheckoutStage('');
            setShowScanCard(false);
            setSelectedTipAmount(0);
            // Clear vertical notification on auto-dismiss
            sendCheckoutStage('');
          }, 10000);
        }, 3000);
      }
    } else if (data.stage === 'failed') {
      if (isHorizontalDevice) {
        setPaymentStatus('payment_failed');
        // Auto-clear after 3 seconds
        setTimeout(() => {
          setPaymentStatus(null);
        }, 3000);
      }
    }
  }, [orientation, layoutMode]);
  
  // Handle process_payment request from horizontal device (customer-facing)
  // This is received by the vertical device (with Stripe reader) to process payment
  const handleWsProcessPayment = useCallback((data) => {
    console.log('[POS] WebSocket process_payment received:', data);
    
    // Only process if this device has the Stripe Terminal bridge (reader connected)
    if (window.stripeBridge && readerConnected) {
      console.log('[POS] This device has reader - processing payment');
      const { amountCents, tipAmount, subtotal } = data;
      
      // Update local state to show scan card screen
      setCheckoutMode(true); // Ensure checkout mode is active
      setShowScanCard(true);
      setSelectedTipAmount(tipAmount || 0);
      setCheckoutSubtotal(subtotal || 0);
      setCheckoutStage('payment');
      
      // Check if this is a simulated reader - if so, wait for manual tap
      const status = JSON.parse(window.stripeBridge.getReaderStatus());
      const isSimulatedReader = status?.serialNumber?.toUpperCase()?.includes('SIMULATOR');
      
      if (isSimulatedReader) {
        console.log('[POS] Simulated reader - waiting for manual Simulate Card Tap');
        // Don't auto-start payment - wait for user to click Simulate Card Tap button
      } else {
        // Real reader - auto-start payment collection
        console.log('[POS] Real reader - auto-starting payment collection');
        window.stripeBridge.processPayment(amountCents, 'usd');
      }
    } else {
      console.log('[POS] This device does not have reader - ignoring process_payment');
    }
  }, [readerConnected]);
  
  // Ref to hold sendPaymentResult function (to avoid circular dependency)
  const sendPaymentResultRef = useRef(null);
  
  // Handle simulate_tap request from horizontal device (customer-facing)
  // This is received by the vertical device (with Stripe reader) to process the payment
  const handleWsSimulateTap = useCallback((data) => {
    console.log('[POS] WebSocket simulate_tap received:', data);
    console.log('[POS] stripeBridge:', !!window.stripeBridge, 'readerConnectedRef:', readerConnectedRef.current);
    console.log('[POS] checkoutTabInfoRef:', checkoutTabInfoRef.current);
    
    // Only process if this device has the Stripe Terminal bridge
    // Use ref to get current value (avoids stale closure)
    if (window.stripeBridge && readerConnectedRef.current) {
      console.log('[POS] This device has reader - starting payment process');
      setCheckoutStage('processing');
      setPaymentStatus('processing'); // Also set paymentStatus to show overlay
      
      // Broadcast processing status to H
      if (sendPaymentResultRef.current) {
        sendPaymentResultRef.current({ status: 'processing', stage: 'processing' });
      }
      
      // Use amount from WebSocket message, fallback to local state
      const amountCents = data?.amountCents || Math.round((checkoutSubtotal + selectedTipAmount) * 100);
      
      console.log('[POS] Processing payment for amount:', amountCents, 'cents');
      
      let cleanupComplete = null;
      let cleanupError = null;
      const cleanupCallbacks = () => {
        if (cleanupComplete) cleanupComplete();
        if (cleanupError) cleanupError();
      };

      // Set up payment callbacks to broadcast result to H
      cleanupComplete = registerGlobalCallback('onPaymentComplete', (result) => {
        cleanupCallbacks();
        console.log('[POS] Simulated payment completed:', result);
        if (result.success) {
          setPaymentStatus('payment_success');
          setCheckoutStage('success');
          setPaymentMethod('credit');
          
          // Build itemData from tab items before marking as paid
          if (checkoutTabInfoRef.current) {
            const tab = checkoutTabInfoRef.current;
            if (tab.items && tab.items.length > 0) {
              const itemDataStr = tab.items.map(item => {
                const itemName = item.name || 'Unknown';
                const category = item.category || 'other';
                const timestamp = item.addedAt || item.timestamp || new Date().toISOString();
                const timeStr = new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                const cost = (item.price || item.finalPrice || 0).toFixed(2);
                return `${itemName}, ${category}, ${timeStr}, CREDIT, ${cost}`;
              }).join('\n');
              if (itemDataStr) {
                setEventSetupData(prev => ({
                  ...prev,
                  itemData: prev.itemData ? `${prev.itemData}\n${itemDataStr}` : itemDataStr
                }));
              }
            }
          }
          
          // Mark tab as paid (use ref to get current value)
          if (checkoutTabInfoRef.current?.id) {
            setTabs(prev => prev.map(t => 
              t.id === checkoutTabInfoRef.current.id 
                ? { ...t, status: 'archived', paidAt: new Date().toISOString(), paymentMethod: 'credit' }
                : t
            ));
            setActiveTabId(null);
          }
          
          // Broadcast success to H with payment method
          if (sendPaymentResultRef.current) {
            sendPaymentResultRef.current({ status: 'payment_success', stage: 'success', paymentMethod: 'credit' });
          }
          
          // Clear payment status after 2 seconds but DON'T clear checkout mode
          // The receipt prompt on H will handle clearing checkout mode
          setTimeout(() => {
            setPaymentStatus(null);
            setShowScanCard(false);
            // Don't clear checkoutMode or checkoutStage - receipt prompt handles this
          }, 2000);
        } else {
          setPaymentStatus('payment_failed');
          setCheckoutStage('failed');
          
          // Broadcast failure to H
          if (sendPaymentResultRef.current) {
            sendPaymentResultRef.current({ status: 'payment_failed', stage: 'failed' });
          }
          
          setTimeout(() => {
            setPaymentStatus(null);
            setCheckoutStage('payment');
          }, 1500);
        }
      });
      
      cleanupError = registerGlobalCallback('onPaymentError', (error) => {
        cleanupCallbacks();
        console.error('[POS] Simulated payment error:', error);
        setPaymentStatus('payment_failed');
        setCheckoutStage('failed');
        
        // Broadcast failure to H
        if (sendPaymentResultRef.current) {
          sendPaymentResultRef.current({ status: 'payment_failed', stage: 'failed', error: error });
        }
        
        setTimeout(() => {
          setPaymentStatus(null);
          setCheckoutStage('payment');
        }, 1500);
      });
      
      // Check if this is a simulated reader
      const status = JSON.parse(window.stripeBridge.getReaderStatus());
      const isSimulatedReader = status?.serialNumber?.toUpperCase()?.includes('SIMULATOR');
      
      if (isSimulatedReader) {
        // For simulated readers, simulate success or failure based on shouldSucceed flag
        // The native SDK's auto-complete isn't reliable, so we handle it in JS
        const shouldSucceed = data?.shouldSucceed !== false; // Default to success if not specified
        console.log('[POS] Simulated reader - simulating payment', shouldSucceed ? 'success' : 'failure');
        setTimeout(() => {
          if (shouldSucceed) {
            // Trigger success
            if (window.onPaymentComplete) {
              window.onPaymentComplete({ success: true, transactionId: 'SIM-' + Date.now() });
            }
          } else {
            // Trigger failure
            if (window.onPaymentError) {
              window.onPaymentError('Simulated payment declined');
            }
          }
        }, 1500); // 1.5 second delay to simulate processing
      } else {
        // Real reader - use native payment process
        if (window.stripeBridge.processPayment) {
          window.stripeBridge.processPayment(amountCents, 'usd');
        }
      }
    } else {
      console.log('[POS] This device does not have reader - ignoring simulate_tap');
    }
  }, [checkoutSubtotal, selectedTipAmount]);
  
  // Handle reader_status updates from other devices
  // This tells us if another device has a reader connected (and if it's simulated)
  const handleWsReaderStatus = useCallback((data) => {
    console.log('[POS] WebSocket reader_status received:', data);
    setRemoteReaderConnected(data.connected || false);
    setRemoteReaderIsSimulated(data.isSimulated || false);
  }, []);
  
  // Handle payment_result from V device - V always broadcasts payment status to H
  const handleWsPaymentResult = useCallback((data) => {
    console.log('[POS] WebSocket payment_result received:', data);
    
    // Update local state to match V's payment status
    setPaymentStatus(data.status);
    setCheckoutStage(data.stage || '');
    
    // Update payment method if provided
    if (data.paymentMethod) {
      setPaymentMethod(data.paymentMethod);
    }
    
    if (data.status === 'payment_success') {
      // After 3 seconds of success animation (Thank you!), show receipt prompt
      setTimeout(() => {
        setPaymentStatus(null);
        setShowScanCard(false);
        setShowReceiptPrompt(true);
        // Send receipt_request to vertical to show "Requesting Receipt" with spinner
        sendCheckoutStageRef.current?.('receipt_request');
        // Start 10 second timer for auto-dismiss
        if (receiptTimerRef.current) clearTimeout(receiptTimerRef.current);
        receiptTimerRef.current = setTimeout(() => {
          setShowReceiptPrompt(false);
          setCheckoutMode(false);
          setCheckoutStage('');
          setSelectedTipAmount(0);
          // Clear vertical notification on auto-dismiss
          sendCheckoutStageRef.current?.('');
        }, 10000);
      }, 3000);
    } else if (data.status === 'payment_failed') {
      // Show failure, then return to payment screen after 1.5 seconds
      setTimeout(() => {
        setPaymentStatus(null);
        setCheckoutStage('payment');
      }, 1500);
    }
  }, []);
  
  // Handle payment status updates from Square webhook via WebSocket
  const handlePaymentStatus = useCallback((message) => {
    console.log('[POS] Payment status received:', message);
    const { checkoutId, status, transactionId, tabId, tabName } = message;
    
    // Only process if it matches our current checkout or if we're waiting for any payment
    if (currentCheckoutId && checkoutId !== currentCheckoutId && checkoutId !== 'unknown') {
      console.log('[POS] Ignoring payment status for different checkout:', checkoutId);
      return;
    }
    
    setPaymentStatus(status);
    
    if (status === 'payment_success') {
      setPaymentStatusMessage(`Payment successful! Transaction: ${transactionId || 'N/A'}`);
      // Auto-clear after 5 seconds
      setTimeout(() => {
        setPaymentStatus(null);
        setPaymentStatusMessage(null);
        setCurrentCheckoutId(null);
        setCheckoutMode(false);
        setCheckoutLoading(false);
      }, 5000);
    } else if (status === 'payment_canceled') {
      setPaymentStatusMessage('Payment was canceled');
      setTimeout(() => {
        setPaymentStatus(null);
        setPaymentStatusMessage(null);
        setCurrentCheckoutId(null);
        setCheckoutLoading(false);
      }, 3000);
    } else if (status === 'payment_failed') {
      setPaymentStatusMessage('Payment failed. Please try again.');
      setTimeout(() => {
        setPaymentStatus(null);
        setPaymentStatusMessage(null);
        setCurrentCheckoutId(null);
        setCheckoutLoading(false);
      }, 3000);
    } else if (status === 'payment_expired') {
      setPaymentStatusMessage('Payment session expired');
      setPaymentStatus(null);
      setCurrentCheckoutId(null);
      setCheckoutLoading(false);
    }
  }, [currentCheckoutId]);
  
  // ============================================
  // STRIPE TERMINAL M2 READER MANAGEMENT
  // ============================================
  
  // Check reader status on mount
  useEffect(() => {
    if (window.stripeBridge) {
      try {
        const status = JSON.parse(window.stripeBridge.getReaderStatus());
        if (status.connected) {
          setReaderConnected(true);
          setReaderInfo(status);
        }
      } catch (e) {
        console.error('[POS] Error getting reader status:', e);
      }
    }
  }, []);
  
  // Set up reader status update callback
  useEffect(() => {
    const cleanupStatus = registerGlobalCallback('onReaderStatusUpdate', (status) => {
      console.log('[POS] Reader status update:', status);
      setReaderConnected(status.connected);
      setReaderInfo(status.connected ? status : null);
    });

    const cleanupDiscovered = registerGlobalCallback('onReadersDiscovered', (readers) => {
      console.log('[POS] Readers discovered callback:', readers);
      setDiscoveredReaders(readers);
      setScanningReaders(false);
    });
    
    return () => {
      cleanupStatus();
      cleanupDiscovered();
    };
  }, []);
  
  const handleDiscoverReaders = useCallback(() => {
    if (!window.stripeBridge) {
      alert('Stripe Terminal not available. Please use the native Android app.');
      return;
    }
    
    setScanningReaders(true);
    setDiscoveredReaders([]);
    
    try {
      window.stripeBridge.discoverReaders();
      
      // Stop scanning after 10 seconds
      setTimeout(() => {
        setScanningReaders(false);
      }, 10000);
    } catch (e) {
      console.error('[POS] Error discovering readers:', e);
      alert('Error scanning for readers: ' + e.message);
      setScanningReaders(false);
    }
  }, []);
  
  const handleConnectReader = useCallback((readerIndex) => {
    if (!window.stripeBridge) return;
    
    try {
      window.stripeBridge.connectReader(readerIndex);
      setShowReaderSetup(false);
    } catch (e) {
      console.error('[POS] Error connecting to reader:', e);
      alert('Error connecting to reader: ' + e.message);
    }
  }, []);
  
  const handleDisconnectReader = useCallback(() => {
    if (!window.stripeBridge) return;
    
    try {
      window.stripeBridge.disconnectReader();
      setReaderConnected(false);
      setReaderInfo(null);
    } catch (e) {
      console.error('[POS] Error disconnecting reader:', e);
      alert('Error disconnecting reader: ' + e.message);
    }
  }, []);
  
  // ============================================
  // VISIBILITY CHANGE DETECTION
  // Detect when user returns from Square POS app
  // Since Square POS doesn't send webhooks for in-person payments,
  // we detect when the app regains focus and show a confirmation dialog
  // ============================================
  const [showPaymentConfirmDialog, setShowPaymentConfirmDialog] = useState(false);
  const squareLaunchedRef = useRef(false);
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When app becomes visible again and we launched Square
      if (document.visibilityState === 'visible' && squareLaunchedRef.current && paymentStatus === 'pending') {
        console.log('[POS] App regained focus after Square launch - showing confirmation dialog');
        // Small delay to ensure the app is fully visible
        setTimeout(() => {
          setShowPaymentConfirmDialog(true);
        }, 500);
        squareLaunchedRef.current = false;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [paymentStatus]);
  
  // Handle payment confirmation from user
  const handlePaymentConfirm = useCallback((wasSuccessful) => {
    setShowPaymentConfirmDialog(false);
    
    if (wasSuccessful) {
      setPaymentStatus('payment_success');
      setPaymentStatusMessage('Payment confirmed!');
      // Clear the tab after successful payment
      if (checkoutTabInfo?.id) {
        setTabs(prev => prev.filter(t => t.id !== checkoutTabInfo.id));
        setActiveTabId(null);
      }
      setTimeout(() => {
        setPaymentStatus(null);
        setPaymentStatusMessage(null);
        setCurrentCheckoutId(null);
        setCheckoutMode(false);
        setCheckoutLoading(false);
      }, 3000);
    } else {
      setPaymentStatus('payment_canceled');
      setPaymentStatusMessage('Payment was not completed');
      setTimeout(() => {
        setPaymentStatus(null);
        setPaymentStatusMessage(null);
        setCurrentCheckoutId(null);
        setCheckoutLoading(false);
      }, 2000);
    }
  }, [checkoutTabInfo, setTabs, setActiveTabId]);
  
  // Connect to WebSocket for cross-device checkout sync
  const { isConnected: wsConnected, sendCheckoutStart, sendCheckoutComplete, sendCheckoutCancel, sendCheckoutStage, sendProcessPayment, sendSimulateTap, sendReaderStatus, sendPaymentResult, sendTipUpdate } = usePosWebSocket(
    handleWsCheckoutStart,
    handleWsCheckoutComplete,
    handleWsCheckoutCancel,
    handlePaymentStatus,
    handleWsCheckoutStage,
    handleWsProcessPayment,
    handleWsSimulateTap,
    handleWsReaderStatus,
    handleWsPaymentResult,
    handleWsTipUpdate
  );
  
  // Update ref when sendPaymentResult is available (to avoid circular dependency)
  useEffect(() => {
    sendPaymentResultRef.current = sendPaymentResult;
  }, [sendPaymentResult]);
  
  // Update ref when sendCheckoutStage is available (to avoid circular dependency in handleWsPaymentResult)
  useEffect(() => {
    sendCheckoutStageRef.current = sendCheckoutStage;
  }, [sendCheckoutStage]);
  
  // Helper to update checkout stage locally AND broadcast via WebSocket
  const updateCheckoutStage = useCallback((stage, paymentMethodParam = null) => {
    console.log('[POS] updateCheckoutStage called:', stage, paymentMethodParam ? `(${paymentMethodParam})` : '');
    setCheckoutStage(stage);
    if (paymentMethodParam) {
      setPaymentMethod(paymentMethodParam);
    }
    sendCheckoutStage(stage, paymentMethodParam);
  }, [sendCheckoutStage]);
  
  // When horizontal view returns to menu (checkoutMode false), clear vertical's transaction messages
  useEffect(() => {
    if (isHorizontalDevice && !checkoutMode) {
      // Send empty stage to clear any lingering transaction messages on vertical
      sendCheckoutStage('');
      console.log('[POS] Horizontal menu loaded - clearing vertical transaction messages');
    }
  }, [checkoutMode, orientation, layoutMode, sendCheckoutStage]);
  
  // Checkout timeout effect - 1 minute idle on horizontal view returns to menu
  // Only applies to horizontal view (no stripeBridge) and non-processing stages
  useEffect(() => {
    // Clear any existing timeout
    if (checkoutTimeoutRef.current) {
      clearTimeout(checkoutTimeoutRef.current);
      checkoutTimeoutRef.current = null;
    }
    
    // Only set timeout if in checkout mode on horizontal view (no stripeBridge)
    // Don't timeout during processing or success - let those complete naturally
    if (checkoutMode && !window.stripeBridge && 
        checkoutStage !== 'processing' && checkoutStage !== 'success') {
      console.log('[POS] Starting 1-minute checkout timeout');
      checkoutTimeoutRef.current = setTimeout(() => {
        console.log('[POS] Checkout timeout - returning to menu');
        // Cancel checkout and return to menu
        setCheckoutMode(false);
        setCheckoutItems([]);
        setCheckoutSubtotal(0);
        setCheckoutTabInfo(null);
        setShowScanCard(false);
        setShowTabView(false);
        setShowCustomTip(false);
        setSelectedTipAmount(0);
        setCheckoutStage('');
        setPaymentStatus(null);
        setPaymentStatusMessage(null);
        // Broadcast cancel to other devices
        sendCheckoutCancel();
      }, CHECKOUT_TIMEOUT_MS);
    }
    
    // Cleanup on unmount or when dependencies change
    return () => {
      if (checkoutTimeoutRef.current) {
        clearTimeout(checkoutTimeoutRef.current);
      }
    };
  }, [checkoutMode, checkoutStage, showTabView, showScanCard, showCustomTip, sendCheckoutCancel]);
  
  // Broadcast reader status to other devices when local reader connects/disconnects
  useEffect(() => {
    if (readerConnected && readerInfo) {
      const isSimulated = readerInfo?.serialNumber?.toUpperCase()?.includes('SIMULATOR');
      sendReaderStatus({
        connected: true,
        isSimulated: isSimulated,
        serialNumber: readerInfo?.serialNumber || ''
      });
    } else if (!readerConnected) {
      sendReaderStatus({
        connected: false,
        isSimulated: false,
        serialNumber: ''
      });
    }
  }, [readerConnected, readerInfo, sendReaderStatus]);
  
  // ============================================
  // SQUARE POS TEST MODE
  // When true: all items are $0.01, orders labeled as TEST
  // When false: real prices, production checkout
  // ============================================
  const [squareTestMode, setSquareTestMode] = useState(true);
  
  // Logo state - fetched from admin logo uploader
  const [logoUrl, setLogoUrl] = useState(null);
  
  // Ref for receipt container to scroll to bottom
  const receiptContainerRef = useRef(null);
  
  // Scroll receipt to bottom when tab view is shown
  useEffect(() => {
    if (showTabView && receiptContainerRef.current) {
      // Use setTimeout to ensure DOM has rendered
      setTimeout(() => {
        if (receiptContainerRef.current) {
          receiptContainerRef.current.scrollTop = receiptContainerRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [showTabView]);
  
  const { apiCall } = useAuth();
  
  // On startup, sync with backend to check for active events
  // This handles the case where localStorage is out of sync with DB
  useEffect(() => {
    const syncWithBackend = async () => {
      try {
        const response = await apiCall('/pos-events/active');
        
        if (response && response.active && response.event) {
          // There's an active event in the DB
          const dbEvent = response.event;
          
          if (!eventId) {
            // localStorage has no event but DB does - restore it
            console.log(`[POS] Restoring active event from DB: ${dbEvent.name}`);
            startEvent(dbEvent._id, dbEvent.name);
          } else if (eventId !== dbEvent._id) {
            // localStorage has different event than DB - use DB's event
            console.log(`[POS] Syncing to DB event: ${dbEvent.name} (was: ${eventId})`);
            startEvent(dbEvent._id, dbEvent.name);
          }
          // If eventId matches dbEvent._id, we're already in sync
        } else {
          // No active event in DB
          if (eventId) {
            // Don't clear localStorage if we're in post-event edit mode or showing summary
            // This allows editing the event after it's ended but before saving
            if (isPostEventEdit || showSummaryView || showEventSetup) {
              console.log('[POS] No active event in DB but in post-event mode - keeping localStorage');
            } else {
              // localStorage has event but DB doesn't - clear localStorage
              console.log('[POS] No active event in DB - clearing localStorage');
              clearEvent();
            }
          }
        }
      } catch (error) {
        console.error('[POS] Error syncing with backend:', error);
      }
    };
    
    syncWithBackend();
  }, [apiCall]); // Only run once on mount - don't include eventId/startEvent/clearEvent to avoid loops
  
  // Fetch logo from admin logo uploader
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await apiCall('/media/logo');
        if (response && response.content && response.content.startsWith('https://res.cloudinary.com/')) {
          setLogoUrl(response.content);
        }
      } catch (error) {
        console.error('[POS] Error fetching logo:', error);
      }
    };
    fetchLogo();
  }, [apiCall]);

  // Fetch menu items
  useEffect(() => {
    const loadItems = async () => {
      setLoading(true);
      
      // Add timeout to prevent infinite loading
      const timeout = setTimeout(() => {
        console.warn('[POS] Menu items loading timeout - using fallback');
        setLoading(false);
        // Use fallback empty items to prevent stuck loading
        setAllItems([]);
      }, 5000); // 5 second timeout
      
      try {
        const data = await apiCall('/menu-items?includeArchived=true');
        
        // Clear timeout on successful response
        clearTimeout(timeout);
        
        // Default modifiers for Spirits category
        const defaultSpiritsModifiers = [
          { name: 'Shot', priceAdjustment: 0, isLink: false },
          { name: 'Single', priceAdjustment: 3, isLink: false },
          { name: 'Double', priceAdjustment: 5, isLink: false }
        ];
        
        const normalized = Array.isArray(data)
          ? data.map((item) => {
              const category = normalizeCategoryKey(item.category || item.section || 'cocktails');
              // Apply default modifiers for spirits if none exist
              const modifiers = (item.modifiers && item.modifiers.length > 0) 
                ? item.modifiers 
                : (category === 'spirits' ? defaultSpiritsModifiers : []);
              return {
                ...item,
                category,
                modifiers
              };
            })
          : [];
        
        // Filter out archived and placeholder items
        const filtered = normalized.filter(item => {
          const isActive = item.status !== 'archived' && (item.isActive !== false);
          const name = String(item?.name || '').trim();
          const isPlaceholder = /^item\s*\d+$/i.test(name);
          return isActive && !isPlaceholder;
        });

        filtered.sort((a, b) => (a.order || 0) - (b.order || 0));
        setAllItems(filtered);
      } catch (err) {
        console.error('Failed to load menu items:', err);
        // Clear timeout on error
        clearTimeout(timeout);
        
        // In WebView, API might fail - try fallback or show error
        if (window.location.search.includes('app=native')) {
          console.log('[POS] Running in native app - API might be blocked');
          // You could add hardcoded fallback items here if needed
          setAllItems([]);
        }
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, [apiCall]);

  // Reusable function to save POS state to server (rolling backup - keeps last 5 saves)
  const saveToServer = useCallback(async (reason = 'manual') => {
    if (!eventId) return;
    
    try {
      console.log(`[POS] Saving state to server (${reason})...`);
      const response = await fetch(`/api/catering-events/${eventId}/autosave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tabs,
          eventSetupData,
          uiState,
          reason, // Track why this save was triggered
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`[POS] Save #${data.saveNumber} successful (${reason}), ${data.totalSaves} backups stored`);
      } else {
        console.error(`[POS] Save failed (${reason}):`, response.status);
      }
    } catch (err) {
      console.error(`[POS] Save error (${reason}):`, err);
    }
  }, [eventId, tabs, eventSetupData, uiState]);
  
  // Keep ref updated for use in earlier-defined callbacks
  useEffect(() => {
    saveToServerRef.current = saveToServer;
  }, [saveToServer]);

  // Auto-save POS state to server every 5 minutes when there's an active event
  useEffect(() => {
    if (!eventId) return;
    
    const autoSaveInterval = setInterval(() => {
      saveToServer('auto-5min');
    }, 300000); // Every 5 minutes (300 seconds)
    
    return () => clearInterval(autoSaveInterval);
  }, [eventId, saveToServer]);

  // Track if we've already restored from server to avoid duplicate restores
  const hasRestoredRef = useRef(false);
  
  // Restore state from server when app loads with an active event
  // Uses rolling backup - tries most recent save first, falls back to older saves if corrupted
  useEffect(() => {
    const restoreFromServer = async () => {
      if (!eventId || hasRestoredRef.current) return;
      
      hasRestoredRef.current = true; // Mark as restored to prevent duplicate calls
      
      try {
        console.log('[POS] Checking for saved state on server for event:', eventId);
        const response = await fetch(`/api/catering-events/${eventId}`);
        
        if (response.ok) {
          const data = await response.json();
          const posSaves = data.event?.posSaves;
          
          if (posSaves && posSaves.length > 0) {
            // Sort by saveNumber descending to get most recent first
            const sortedSaves = [...posSaves].sort((a, b) => b.saveNumber - a.saveNumber);
            
            console.log(`[POS] Found ${sortedSaves.length} backup saves, trying most recent first...`);
            
            // Try each save starting from most recent
            for (const save of sortedSaves) {
              try {
                console.log(`[POS] Trying save #${save.saveNumber} from ${save.savedAt} (${save.reason})`);
                
                // Validate save data
                if (!save.tabs || !Array.isArray(save.tabs)) {
                  console.warn(`[POS] Save #${save.saveNumber} has invalid tabs, trying next...`);
                  continue;
                }
                
                const localTabsJson = localStorage.getItem('pos_tabs');
                const localTabs = localTabsJson ? JSON.parse(localTabsJson) : [];
                const serverTime = new Date(save.savedAt).getTime();
                
                // If local is empty or server data is recent, restore from server
                if (localTabs.length === 0 || serverTime > Date.now() - 300000) {
                  console.log(`[POS] Restoring from save #${save.saveNumber}...`);
                  setTabs(save.tabs);
                  
                  // Restore eventSetupData if available
                  if (save.eventSetupData && Object.keys(save.eventSetupData).length > 0) {
                    setEventSetupData(prev => ({
                      ...prev,
                      ...save.eventSetupData,
                    }));
                  }
                  
                  // Restore uiState if available
                  if (save.uiState && Object.keys(save.uiState).length > 0) {
                    setUiState(prev => ({
                      ...prev,
                      ...save.uiState,
                    }));
                  }
                  
                  console.log(`[POS] State restored from save #${save.saveNumber} successfully`);
                  break; // Successfully restored, exit loop
                } else {
                  console.log('[POS] Local data is newer, skipping server restore');
                  break;
                }
              } catch (saveErr) {
                console.error(`[POS] Error restoring save #${save.saveNumber}:`, saveErr);
                // Continue to next save
              }
            }
          } else {
            console.log('[POS] No saved states found on server');
          }
        }
      } catch (err) {
        console.error('[POS] Error restoring state from server:', err);
      }
    };
    
    // Restore when eventId becomes available
    restoreFromServer();
  }, [eventId, setTabs, setEventSetupData, setUiState]);

  // Filter items by active category
  const items = useMemo(() => {
    return allItems.filter(item => normalizeCategoryKey(item.category) === activeCategory);
  }, [allItems, activeCategory]);

  // Get selected items for active tab
  const selectedItems = useMemo(() => {
    const activeTab = tabs.find(t => t.id === activeTabId);
    return activeTab ? activeTab.items : [];
  }, [tabs, activeTabId]);

  // Calculate total from selected items (active tab)
  const total = useMemo(() => {
    const result = selectedItems.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      console.log(`[POS] Total calc - item: ${item.name}, price: ${price}, modifier: ${item.modifier || 'none'}`);
      return sum + price;
    }, 0);
    console.log(`[POS] Total: ${result}`);
    return result;
  }, [selectedItems]);

  // Shared helper function to calculate spillage total - ensures consistency between brief and full summary
  const calculateSpillageTotal = useCallback((spillageTab, menuItems) => {
    if (!spillageTab?.items?.length) return 0;
    return spillageTab.items.reduce((sum, item) => {
      const menuItem = menuItems.find(i => i.name === item.name);
      // Use costPerUnit if available, otherwise use the item's price from the tab
      const costPerUnit = menuItem?.costPerUnit || item.basePrice || item.price || 0;
      return sum + costPerUnit;
    }, 0);
  }, []);

  // Memoized spillage total for consistent display across views
  const spillageTotal = useMemo(() => {
    const spillageTab = tabs.find(t => t.isSpillage);
    return calculateSpillageTotal(spillageTab, allItems);
  }, [tabs, allItems, calculateSpillageTotal]);

  // Auto-update labor hours when start/end time changes
  useEffect(() => {
    if (eventSetupData.startTime && eventSetupData.endTime && eventSetupData.labor?.length > 0) {
      const [startH, startM] = eventSetupData.startTime.split(':').map(Number);
      const [endH, endM] = eventSetupData.endTime.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      const durationMinutes = endMinutes >= startMinutes ? endMinutes - startMinutes : (24 * 60 - startMinutes + endMinutes);
      const calculatedHours = (durationMinutes / 60).toFixed(2);
      
      // Update all labor entries with the calculated hours (only if hours is empty or matches previous calculated value)
      setEventSetupData(prev => ({
        ...prev,
        labor: prev.labor.map(l => ({
          ...l,
          hours: l.hours === '' || l.hours === '0' || l.hours === prev._lastCalculatedHours ? calculatedHours : l.hours
        })),
        _lastCalculatedHours: calculatedHours
      }));
    }
  }, [eventSetupData.startTime, eventSetupData.endTime]);

  // Calculate category counts from selected items (active tab)
  const categoryCounts = useMemo(() => {
    const counts = {};
    CATEGORIES.forEach(cat => { counts[cat.id] = 0; });
    selectedItems.forEach(item => {
      const cat = normalizeCategoryKey(item.category);
      if (counts[cat] !== undefined) {
        counts[cat]++;
      }
    });
    return counts;
  }, [selectedItems]);

  // Create a new tab
  const handleCreateTab = useCallback(() => {
    const newTab = {
      id: `tab-${Date.now()}`,
      name: `P${nextTabNumber}`,
      items: []
    };
    setTabs(prev => [newTab, ...prev]);
    setActiveTabId(newTab.id);
    setNextTabNumber(prev => prev + 1);
    console.log(`[POS] Created new tab: ${newTab.name}`);
  }, [nextTabNumber]);

  // Select a tab
  const handleSelectTab = useCallback((tabId) => {
    setActiveTabId(tabId);
  }, []);

  // Delete a tab (only for empty, unpaid tabs)
  const handleDeleteTab = useCallback((tabId) => {
    setTabs(prev => prev.filter(t => t.id !== tabId));
    if (activeTabId === tabId) {
      setActiveTabId(tabs.length > 1 ? tabs.find(t => t.id !== tabId)?.id : null);
    }
  }, [activeTabId, tabs]);
  
  // Helper to build itemData string from tab items
  // Format: "(item name), (category), (timestamp), (transaction), (cost)\n" per item
  const buildItemDataFromTab = useCallback((tab, transactionType) => {
    if (!tab || !tab.items || tab.items.length === 0) return '';
    
    return tab.items.map(item => {
      const itemName = item.name || 'Unknown';
      const category = item.category || 'other';
      const timestamp = item.addedAt || item.timestamp || new Date().toISOString();
      const timeStr = new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      const cost = (item.price || item.finalPrice || 0).toFixed(2);
      return `${itemName}, ${category}, ${timeStr}, ${transactionType}, ${cost}`;
    }).join('\n');
  }, []);
  
  // Append item data to eventSetupData.itemData
  const appendItemData = useCallback((newItemData) => {
    if (!newItemData) return;
    setEventSetupData(prev => ({
      ...prev,
      itemData: prev.itemData ? `${prev.itemData}\n${newItemData}` : newItemData
    }));
  }, [setEventSetupData]);

  // Archive a tab (for paid tabs - removes from UI but keeps in event data)
  const handleArchiveTab = useCallback((tabId) => {
    setTabs(prev => prev.map(t => 
      t.id === tabId 
        ? { ...t, status: 'archived', archivedAt: new Date().toISOString() }
        : t
    ));
    if (activeTabId === tabId) {
      setActiveTabId(null);
    }
    console.log(`[POS] Tab ${tabId} archived`);
  }, [activeTabId]);

  // Invoice a tab (marks as archived with isInvoice flag - payment to be collected later)
  // Invoice tabs show as yellow and their total is subtracted from revenue (shown as -$)
  // Only one invoice tab allowed - subsequent invoices merge items into existing invoice tab
  const handleInvoiceTab = useCallback((tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    
    // Check if there's already an existing invoice tab (not the current one)
    const existingInvoiceTab = tabs.find(t => t.isInvoice && t.status === 'archived' && t.id !== tabId);
    
    if (existingInvoiceTab) {
      // Merge items into existing invoice tab and delete the current tab
      console.log(`[POS] Merging tab ${tabId} items into existing invoice tab ${existingInvoiceTab.id}`);
      setTabs(prev => {
        const updatedTabs = prev.map(t => {
          if (t.id === existingInvoiceTab.id) {
            // Add items from current tab to existing invoice tab
            return {
              ...t,
              items: [...t.items, ...tab.items],
              invoicedAt: new Date().toISOString() // Update timestamp
            };
          }
          return t;
        }).filter(t => t.id !== tabId); // Remove the tab being invoiced
        return updatedTabs;
      });
      console.log(`[POS] Tab ${tabId} merged into invoice tab ${existingInvoiceTab.id}`);
    } else {
      // No existing invoice tab - mark this one as invoiced
      setTabs(prev => prev.map(t => 
        t.id === tabId 
          ? { ...t, status: 'archived', isInvoice: true, paymentMethod: 'invoice', invoicedAt: new Date().toISOString() }
          : t
      ));
      console.log(`[POS] Tab ${tabId} invoiced (payment to be collected later)`);
    }
    
    if (activeTabId === tabId) {
      setActiveTabId(null);
    }
  }, [activeTabId, tabs]);

  // Reopen an invoice tab (moves back to unpaid/open status)
  const handleReopenTab = useCallback((tabId) => {
    setTabs(prev => prev.map(t => 
      t.id === tabId 
        ? { ...t, status: 'open', isInvoice: false, paymentMethod: null, paidAt: null, invoicedAt: null }
        : t
    ));
    console.log(`[POS] Tab ${tabId} reopened (moved back to unpaid)`);
  }, []);

  // Update tab custom name
  const handleUpdateTabName = useCallback((tabId, customName) => {
    setTabs(prev => prev.map(tab =>
      tab.id === tabId
        ? { ...tab, customName }
        : tab
    ));
  }, []);

  // Update item modifiers (for editing flow) - persists to database
  const handleUpdateItemModifiers = useCallback(async (itemId, modifiers) => {
    // Update local state immediately
    setAllItems(prev => prev.map(item =>
      item._id === itemId
        ? { ...item, modifiers }
        : item
    ));
    
    // Persist to database
    try {
      await apiCall(`/menu-items/${itemId}/modifiers`, {
        method: 'PUT',
        body: { modifiers }
      });
      console.log(`[POS] Saved modifiers for item ${itemId} to database:`, modifiers);
    } catch (error) {
      console.error(`[POS] Failed to save modifiers for item ${itemId}:`, error);
    }
  }, [apiCall]);

  // ============================================
  // EVENT MANAGEMENT HANDLERS
  // ============================================

  /**
   * Handle header tap to reveal buttons
   * Button auto-hides after 3 seconds if not clicked
   * Shows: Test, Reader, Cancel Payment (during checkout) or End Event (outside checkout)
   */
  const handleHeaderTap = useCallback(() => {
    setShowEndEventButton(prev => {
      if (prev) {
        if (endEventTimeoutRef.current) clearTimeout(endEventTimeoutRef.current);
        return false;
      }
      if (endEventTimeoutRef.current) clearTimeout(endEventTimeoutRef.current);
      endEventTimeoutRef.current = setTimeout(() => {
        setShowEndEventButton(false);
      }, 5000);
      return true;
    });
  }, []);

  /**
   * Start a new POS event
   * Creates event in DB and initializes local state
   */
  const handleStartEvent = useCallback(async (name) => {
    try {
      setSyncing(true);
      const response = await apiCall('/pos-events', {
        method: 'POST',
        body: { name },
      });
      
      if (response && response._id) {
        startEvent(response._id, response.name);
        setTestTimeOffsetMins(0);
        // Record start time in eventSetupData
        const startTimeNow = new Date().toTimeString().slice(0, 5);
        setEventSetupData(prev => ({
          ...prev,
          startTime: startTimeNow,
          eventDate: new Date().toISOString().split('T')[0],
        }));
        // Create the spillage tab if it doesn't exist, or ensure it has status: 'open'
        setTabs(prev => {
          const spillageTab = prev.find(t => t.isSpillage);
          if (!spillageTab) {
            return [...prev, { id: 'spillage-tab', name: 'S', isSpillage: true, items: [], status: 'open' }];
          }
          // Ensure spillage tab always has status: 'open' (fix for tabs restored from localStorage without status)
          if (spillageTab.status !== 'open') {
            return prev.map(t => t.isSpillage ? { ...t, status: 'open' } : t);
          }
          return prev;
        });
        setShowEventModal(false);
        setNewEventName('');
        console.log(`[POS] Started event: ${response.name} (${response._id}) at ${startTimeNow}`);
      }
    } catch (error) {
      console.error('[POS] Failed to start event:', error);
      
      // Check if error is due to existing active event
      if (error.message && error.message.includes('active event already exists')) {
        // Try to get the active event info and offer options
        try {
          const activeResponse = await apiCall('/pos-events/active');
          if (activeResponse && activeResponse.active && activeResponse.event) {
            const activeEvent = activeResponse.event;
            const choice = window.confirm(
              `An active event "${activeEvent.name}" already exists.\n\n` +
              `Click OK to resume this event, or Cancel to end it and start a new one.`
            );
            
            if (choice) {
              // Resume the existing event
              startEvent(activeEvent._id, activeEvent.name);
              setShowEventModal(false);
              setNewEventName('');
              console.log(`[POS] Resumed existing event: ${activeEvent.name}`);
            } else {
              // End the existing event first, then start new one
              try {
                await apiCall(`/pos-events/${activeEvent._id}/end`, {
                  method: 'PUT',
                  body: { tabs: [] },
                });
                console.log(`[POS] Ended existing event: ${activeEvent.name}`);
                
                // Now try to start the new event again
                const newResponse = await apiCall('/pos-events', {
                  method: 'POST',
                  body: { name },
                });
                
                if (newResponse && newResponse._id) {
                  startEvent(newResponse._id, newResponse.name);
                  setShowEventModal(false);
                  setNewEventName('');
                  console.log(`[POS] Started new event: ${newResponse.name}`);
                }
              } catch (endError) {
                console.error('[POS] Failed to end existing event:', endError);
                alert(`Failed to end existing event: ${endError.message}`);
              }
            }
          }
        } catch (activeError) {
          console.error('[POS] Failed to get active event:', activeError);
          alert(`Failed to start event: ${error.message}`);
        }
      } else {
        alert(`Failed to start event: ${error.message}`);
      }
    } finally {
      setSyncing(false);
    }
  }, [apiCall, startEvent]);

  /**
   * End the current event
   * Syncs all tabs to DB and calculates summary
   */
  const handleEndEvent = useCallback(async () => {
    if (!eventId) return;
    
    try {
      setSyncing(true);
      // Record end time in eventSetupData
      const endTimeNow = new Date(Date.now() + (testMode ? testTimeOffsetMins * 60 * 1000 : 0)).toTimeString().slice(0, 5);
      setEventSetupData(prev => ({
        ...prev,
        endTime: endTimeNow,
      }));
      // Log tabs with tip amounts for debugging
      console.log('[POS] Ending event with tabs:', tabs.map(t => ({ id: t.id, name: t.name, tipAmount: t.tipAmount, status: t.status, itemCount: t.items?.length })));
      const response = await apiCall(`/pos-events/${eventId}/end`, {
        method: 'PUT',
        body: { tabs },
      });
      
      if (response && response.event) {
        setEventSummary(response.event.summary);
        setShowEndEventModal(false);
        setShowSummaryView(true);
        setIsPostEventEdit(true); // Mark as post-event edit mode
        console.log(`[POS] Ended event: ${eventName} at ${endTimeNow}`);
      }
    } catch (error) {
      console.error('[POS] Failed to end event:', error);
      
      // If the event is not active (already ended or doesn't exist), clear local state
      if (error.message && error.message.includes('not active')) {
        console.log('[POS] Event already ended - clearing local state');
        clearEvent();
        setShowEndEventModal(false);
      } else {
        alert(`Failed to end event: ${error.message}`);
      }
    } finally {
      setSyncing(false);
    }
  }, [apiCall, eventId, eventName, tabs, clearEvent, testMode, testTimeOffsetMins]);

  /**
   * Force quit the event - bypasses tab checks and forces to summary view
   * Used when tabs are stuck and cannot be closed normally
   */
  const handleForceQuit = useCallback(async () => {
    if (!eventId) return;
    
    try {
      setSyncing(true);
      console.log('[POS] Force quitting event with tabs:', tabs.map(t => ({ id: t.id, name: t.name, status: t.status, itemCount: t.items?.length })));
      
      // Record end time in eventSetupData (account for test mode time offset)
      const endTimeNow = new Date(Date.now() + (testMode ? testTimeOffsetMins * 60 * 1000 : 0)).toTimeString().slice(0, 5);
      setEventSetupData(prev => ({
        ...prev,
        endTime: endTimeNow,
      }));

      // Force all non-archived tabs to be marked as archived before ending
      const forcedTabs = tabs.map(t =>
        t.status !== 'archived' ? { ...t, status: 'archived', forceClosed: true } : t
      );
      
      const response = await apiCall(`/pos-events/${eventId}/end`, {
        method: 'PUT',
        body: { tabs: forcedTabs, forceQuit: true },
      });
      
      if (response && response.event) {
        setEventSummary(response.event.summary);
        setShowForceQuitConfirm(false);
        setShowEndEventModal(false);
        setShowSummaryView(true);
        setIsPostEventEdit(true); // Mark as post-event edit mode
        console.log(`[POS] Force quit event: ${eventName}`);
      }
    } catch (error) {
      console.error('[POS] Failed to force quit event:', error);
      // Even on error, try to show summary view with local data
      setShowForceQuitConfirm(false);
      setShowEndEventModal(false);
      setShowSummaryView(true);
      setIsPostEventEdit(true); // Mark as post-event edit mode
      console.log('[POS] Showing summary view despite error');
    } finally {
      setSyncing(false);
    }
  }, [apiCall, eventId, eventName, tabs, testMode, testTimeOffsetMins]);

  /**
   * Close summary view and clear local state
   */
  const handleCloseSummary = useCallback(() => {
    clearEvent();
    setShowSummaryView(false);
    setEventSummary(null);
  }, [clearEvent]);

  /**
   * Sync current state to DB (for periodic saves)
   */
  const handleSyncToDb = useCallback(async () => {
    if (!eventId) return;
    
    try {
      await apiCall(`/pos-events/${eventId}/sync`, {
        method: 'PUT',
        body: { tabs },
      });
      console.log('[POS] Synced to DB');
    } catch (error) {
      console.error('[POS] Failed to sync:', error);
    }
  }, [apiCall, eventId, tabs]);

  // Auto-sync to DB every 30 seconds when event is active
  useEffect(() => {
    if (!eventId) return;
    
    const interval = setInterval(() => {
      handleSyncToDb();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [eventId, handleSyncToDb]);

  /**
   * Handle checkout - Enter checkout mode to show tipping screen on horizontal view
   * 
   * CHECKOUT FLOW:
   * 1. User clicks CHECKOUT on vertical POS
   * 2. Horizontal view switches from MenuGallery2 to receipt/tipping screen
   * 3. Customer selects tip (15%, 20%, 25%, or custom)
   * 4. Tip selection triggers Square deep-link with total + tip
   */
  const handleCheckout = useCallback(() => {
    if (!activeTabId || selectedItems.length === 0) {
      alert('No items to checkout');
      return;
    }

    const activeTab = tabs.find(t => t.id === activeTabId);
    if (!activeTab) {
      alert('No active tab found');
      return;
    }

    console.log(`[POS Checkout] Showing checkout options for tab ${activeTab.id} (${activeTab.customName || activeTab.name})`);
    
    // Store checkout data for later use
    const checkoutData = {
      items: [...selectedItems],
      subtotal: total,
      tabInfo: {
        id: activeTab.id,
        name: activeTab.customName || activeTab.name
      }
    };
    
    setCheckoutItems(checkoutData.items);
    setCheckoutSubtotal(checkoutData.subtotal);
    setCheckoutTabInfo(checkoutData.tabInfo);
    
    // Show checkout options popup (Credit/Cash/Invoice)
    setShowCheckoutOptions(true);
  }, [activeTabId, selectedItems, tabs, total]);

  // State for reader disconnected warning
  const [showReaderDisconnectedWarning, setShowReaderDisconnectedWarning] = useState(false);

  /**
   * Start credit card checkout flow
   */
  const handleCreditCheckout = useCallback(() => {
    // Check if card reader is connected (local or remote)
    if (!readerConnected && !remoteReaderConnected) {
      // Show reader disconnected warning
      setShowReaderDisconnectedWarning(true);
      return;
    }
    
    setShowCheckoutOptions(false);
    setPaymentMethod('credit');
    setShowCustomTip(false);
    setCustomTipAmount('');
    
    // Enter checkout mode locally
    setCheckoutMode(true);
    setCheckoutStage('tip'); // Set initial stage locally
    
    // Broadcast checkout start via WebSocket to sync horizontal view on other devices
    const checkoutData = {
      items: checkoutItems,
      subtotal: checkoutSubtotal,
      tabInfo: checkoutTabInfo,
      paymentMethod: 'credit'
    };
    sendCheckoutStart(checkoutData);
    sendCheckoutStage('tip');
    
    console.log(`[POS Checkout] Credit checkout mode activated. Subtotal: $${checkoutSubtotal.toFixed(2)}`);
  }, [checkoutItems, checkoutSubtotal, checkoutTabInfo, sendCheckoutStart, sendCheckoutStage, readerConnected, remoteReaderConnected]);

  /**
   * Start cash checkout flow
   */
  const handleCashCheckout = useCallback(() => {
    setShowCheckoutOptions(false);
    setPaymentMethod('cash');
    setCashTendered('');
    
    // Enter checkout mode locally
    setCheckoutMode(true);
    setCheckoutStage('cash'); // Cash payment stage
    
    // Broadcast checkout start via WebSocket to sync horizontal view on other devices
    const checkoutData = {
      items: checkoutItems,
      subtotal: checkoutSubtotal,
      tabInfo: checkoutTabInfo,
      paymentMethod: 'cash'
    };
    sendCheckoutStart(checkoutData);
    sendCheckoutStage('cash');
    
    console.log(`[POS Checkout] Cash checkout mode activated. Subtotal: $${checkoutSubtotal.toFixed(2)}`);
  }, [checkoutItems, checkoutSubtotal, checkoutTabInfo, sendCheckoutStart, sendCheckoutStage]);

  /**
   * Handle invoice checkout - mark tab as invoiced for later payment
   * Only one invoice tab allowed - subsequent invoices merge items into existing invoice tab
   */
  const handleInvoiceCheckout = useCallback(() => {
    setShowCheckoutOptions(false);
    
    if (!checkoutTabInfo) {
      alert('No tab selected');
      return;
    }
    
    // Check if there's already an existing invoice tab (not the current one)
    const existingInvoiceTab = tabs.find(t => t.isInvoice && t.status === 'archived' && t.id !== checkoutTabInfo.id);
    
    if (existingInvoiceTab) {
      // Merge items into existing invoice tab and delete the current tab
      console.log(`[POS Checkout] Merging tab ${checkoutTabInfo.id} items into existing invoice tab ${existingInvoiceTab.id}`);
      setTabs(prev => {
        const currentTab = prev.find(t => t.id === checkoutTabInfo.id);
        const updatedTabs = prev.map(t => {
          if (t.id === existingInvoiceTab.id) {
            return {
              ...t,
              items: [...t.items, ...(currentTab?.items || [])],
              invoicedAt: new Date().toISOString()
            };
          }
          return t;
        }).filter(t => t.id !== checkoutTabInfo.id);
        return updatedTabs;
      });
    } else {
      // No existing invoice tab - mark this one as invoiced
      setTabs(prev => prev.map(t => 
        t.id === checkoutTabInfo.id 
          ? { ...t, status: 'archived', isInvoice: true, paymentMethod: 'invoice', paidAt: new Date().toISOString() }
          : t
      ));
      console.log(`[POS Checkout] Tab ${checkoutTabInfo.id} marked as invoiced`);
    }
    
    // Clear checkout state
    setCheckoutItems([]);
    setCheckoutSubtotal(0);
    setCheckoutTabInfo(null);
    
    // Move to next tab or clear selection
    if (activeTabId === checkoutTabInfo.id) {
      setActiveTabId(null);
    }
  }, [checkoutTabInfo, activeTabId, tabs]);

  /**
   * Complete cash payment
   */
  const handleCompleteCashPayment = useCallback(() => {
    if (!checkoutTabInfo) {
      alert('No tab selected');
      return;
    }
    
    const cashAmount = parseFloat(cashTendered) || 0;
    const taxAmount = checkoutSubtotal * 0.08;
    const totalDue = checkoutSubtotal + taxAmount;
    
    if (cashAmount < totalDue) {
      alert(`Insufficient cash. Need $${totalDue.toFixed(2)}, received $${cashAmount.toFixed(2)}`);
      return;
    }
    
    // Build itemData from tab items before marking as paid
    const itemDataStr = buildItemDataFromTab(checkoutTabInfo, 'CASH');
    if (itemDataStr) {
      appendItemData(itemDataStr);
    }
    
    // Mark tab as paid with cash (use 'archived' status to appear in paid tabs)
    setTabs(prev => prev.map(t => 
      t.id === checkoutTabInfo.id 
        ? { 
            ...t, 
            status: 'archived', 
            paymentMethod: 'cash', 
            tipAmount: 0, // No tip for cash
            paidAt: new Date().toISOString(),
            cashTendered: cashAmount,
            changeGiven: cashAmount - totalDue
          }
        : t
    ));
    
    // Save to server after payment completed
    setTimeout(() => saveToServerRef.current?.('cash-payment-complete'), 500);
    
    // Show success animation (same as credit card payment)
    setCheckoutStage('success');
    setPaymentMethod('cash');
    setPaymentStatus('payment_success'); // Trigger the success animation
    
    // Broadcast success stage to horizontal view with payment method (triggers animation there too)
    sendCheckoutStage('success', 'cash');
    
    // Delay checkout_complete broadcast until AFTER horizontal animation finishes (3s animation + buffer)
    // Sending it immediately would clear horizontal's checkoutMode before the animation plays
    setTimeout(() => {
      sendCheckoutComplete({ tipAmount: 0, finalTotal: totalDue, tabId: checkoutTabInfo.id, paymentMethod: 'cash' });
    }, 3500);
    
    // Clear vertical view checkout state after 1.5 seconds (Payment Complete transition)
    // Keep checkoutMode true so vertical can show receipt flow stages from horizontal
    // Horizontal will send empty stage when receipt flow completes to exit checkoutMode
    setTimeout(() => {
      // Don't exit checkoutMode - let horizontal control via empty stage webhook
      // setCheckoutMode(false);
      setCheckoutItems([]);
      setCheckoutSubtotal(0);
      setCheckoutTabInfo(null);
      setCashTendered('');
      setCheckoutStage(''); // Clear stage but keep checkoutMode for receipt flow
      setPaymentMethod('');
      setPaymentStatus(null); // Clear payment status
      
      // Move to next tab or clear selection
      if (activeTabId === checkoutTabInfo.id) {
        setActiveTabId(null);
      }
    }, 1500);
    
    console.log(`[POS Checkout] Cash payment completed. Total: $${totalDue.toFixed(2)}, Cash: $${cashAmount.toFixed(2)}, Change: $${(cashAmount - totalDue).toFixed(2)}`);
  }, [checkoutTabInfo, checkoutSubtotal, cashTendered, activeTabId, sendCheckoutComplete, sendCheckoutStage]);

  /**
   * Process payment with tip - Uses Stripe Terminal for Bluetooth reader
   * Called when customer selects a tip option
   */
  const handleProcessPaymentWithTip = useCallback(async (tipAmount) => {
    if (!checkoutTabInfo || checkoutItems.length === 0) {
      alert('No checkout data');
      return;
    }

    const taxAmount = checkoutSubtotal * 0.08;
    const finalTotal = checkoutSubtotal + taxAmount + tipAmount;
    const totalCents = Math.round(finalTotal * 100);
    
    try {
      setCheckoutLoading(true);
      
      // ============================================
      // STRIPE TERMINAL CHECKOUT FLOW
      // ============================================
      console.log(`[POS Checkout] Processing payment with Stripe Terminal`);
      console.log(`[POS Checkout] Subtotal: $${checkoutSubtotal.toFixed(2)}, Tip: $${tipAmount.toFixed(2)}, Total: $${finalTotal.toFixed(2)}`);
      
      // Check if running in native app with Stripe Terminal
      if (window.stripeBridge) {
        console.log('[POS Checkout] Using Stripe Terminal native bridge');
        
        let cleanupComplete = null;
        let cleanupError = null;
        const cleanupCallbacks = () => {
          if (cleanupComplete) cleanupComplete();
          if (cleanupError) cleanupError();
        };

        // Set up payment status handlers
        cleanupComplete = registerGlobalCallback('onPaymentComplete', (result) => {
          cleanupCallbacks();
          console.log('[POS Checkout] Payment completed:', result);
          if (result.success) {
            setPaymentStatus('payment_success');
            setPaymentStatusMessage(`Payment successful! Transaction: ${result.transactionId || 'N/A'}`);
            updateCheckoutStage('success', 'credit'); // Show "Payment Complete" on vertical screen with payment method
            
            // Store card details from Stripe result for receipt
            if (result.cardBrand) setCardBrand(result.cardBrand);
            if (result.cardLast4) setCardLast4(result.cardLast4);
            
            // Build itemData from tab items before marking as paid
            if (checkoutTabInfo) {
              const itemDataStr = buildItemDataFromTab(checkoutTabInfo, 'CREDIT');
              if (itemDataStr) {
                appendItemData(itemDataStr);
              }
            }
            
            // Mark tab as paid and store tip amount
            if (checkoutTabInfo?.id) {
              setTabs(prev => prev.map(t => 
                t.id === checkoutTabInfo.id 
                  ? { ...t, status: 'paid', paidAt: new Date().toISOString(), tipAmount: tipAmount || 0, paymentMethod: 'credit' }
                  : t
              ));
              setActiveTabId(null);
            }
            
            // Clear vertical view checkout state after 1.5 seconds
            // Horizontal view handles its own receipt prompt flow
            setTimeout(() => {
              setPaymentStatus(null);
              setPaymentStatusMessage(null);
              setCurrentCheckoutId(null);
              setCheckoutLoading(false);
              setShowScanCard(false);
              setCheckoutMode(false);
              setCheckoutStage('');
              setCheckoutItems([]);
              setCheckoutSubtotal(0);
              setCheckoutTabInfo(null);
            }, 1500);
          } else {
            setPaymentStatus('payment_failed');
            setPaymentStatusMessage('Payment failed. Please try again.');
            updateCheckoutStage('failed'); // Show "Payment Failed" on vertical screen
            setTimeout(() => {
              setPaymentStatus(null);
              setPaymentStatusMessage(null);
              setCheckoutLoading(false);
              updateCheckoutStage('payment'); // Return to payment screen
              // Stay on scan card screen so user can retry
            }, 1500);
          }
        });
        
        cleanupError = registerGlobalCallback('onPaymentError', (error) => {
          cleanupCallbacks();
          console.error('[POS Checkout] Payment error:', error);
          setPaymentStatus('payment_failed');
          setPaymentStatusMessage(`Payment error: ${error}`);
          updateCheckoutStage('failed'); // Show "Payment Failed" on vertical screen
          setTimeout(() => {
            setPaymentStatus(null);
            setPaymentStatusMessage(null);
            setCheckoutLoading(false);
            updateCheckoutStage('payment'); // Return to payment screen
            // Stay on scan card screen so user can retry
          }, 1500);
        });
        
        // Process payment via Stripe Terminal
        updateCheckoutStage('processing'); // Show "Processing Payment" on vertical screen
        window.stripeBridge.processPayment(totalCents, 'usd');
        
      } else {
        // Fallback for web testing
        console.log('[POS Checkout] Stripe Terminal not available - showing test mode');
        alert('Stripe Terminal requires native app with connected reader. This is web testing mode.');
        
        // Simulate payment for testing - also store tip on tab
        if (checkoutTabInfo?.id) {
          setTabs(prev => prev.map(t => 
            t.id === checkoutTabInfo.id 
              ? { ...t, status: 'paid', paidAt: new Date().toISOString(), tipAmount: tipAmount || 0, paymentMethod: 'credit' }
              : t
          ));
          setActiveTabId(null);
        }
        
        setTimeout(() => {
          setPaymentStatus('payment_success');
          setPaymentStatusMessage('Test payment successful (web mode)');
          setTimeout(() => {
            setPaymentStatus(null);
            setPaymentStatusMessage(null);
            setCheckoutMode(false);
            setCheckoutLoading(false);
            setShowScanCard(false);
            setSelectedTipAmount(0);
          }, 2000);
        }, 2000);
      }
      
      // Broadcast checkout complete via WebSocket to sync horizontal view on other devices
      sendCheckoutComplete({ tipAmount, finalTotal, tabId: checkoutTabInfo.id });

    } catch (error) {
      console.error('[POS Checkout] Error:', error);
      alert(`Checkout failed: ${error.message}`);
      setCheckoutLoading(false);
    }
  }, [checkoutTabInfo, checkoutItems, checkoutSubtotal, sendCheckoutComplete, updateCheckoutStage]);

  // Called when customer selects a tip - shows scan card screen and starts payment collection
  const handleTipSelected = useCallback((tipAmount) => {
    console.log('[POS Checkout] Tip selected:', tipAmount);
    console.log('[POS Checkout] readerInfo:', JSON.stringify(readerInfo));
    console.log('[POS Checkout] stripeBridge available:', !!window.stripeBridge);
    console.log('[POS Checkout] readerConnected:', readerConnected);
    
    setSelectedTipAmount(tipAmount);
    setShowScanCard(true);
    setShowCustomTip(false);
    setPaymentStatus(null);
    setPaymentStatusMessage(null);
    updateCheckoutStage('payment'); // Show "Accepting Payment" on vertical screen
    
    // Save tip to the tab locally (only if not already archived)
    if (checkoutTabInfo?.id) {
      setTabs(prev => prev.map(t => 
        t.id === checkoutTabInfo.id && t.status !== 'archived'
          ? { ...t, tipAmount }
          : t
      ));
    }
    
    // Broadcast tip amount to all devices via WebSocket
    sendTipUpdate({ tipAmount, tabId: checkoutTabInfo?.id });
    
    // Calculate total amount in cents (including 8% tax)
    const taxAmount = checkoutSubtotal * 0.08;
    const totalAmount = checkoutSubtotal + taxAmount + tipAmount;
    const amountCents = Math.round(totalAmount * 100);
    
    // Check if this device has the reader connected
    if (window.stripeBridge && readerConnected) {
      // This device has the reader - process payment directly
      console.log('[POS Checkout] This device has reader - processing payment directly');
      
      // Check if this is a simulated reader
      const isSimulatedReader = readerInfo?.serialNumber?.toUpperCase()?.includes('SIMULATOR');
      if (isSimulatedReader) {
        console.log('[POS Checkout] Simulated reader detected - waiting for manual card tap');
        // Don't auto-start payment for simulated readers
      } else {
        // Real reader - auto-start payment collection
        setTimeout(() => {
          console.log('[POS Checkout] Starting payment collection with real reader...');
          handleProcessPaymentWithTip(tipAmount);
        }, 500);
      }
    } else {
      // This device does NOT have the reader - send WebSocket message to device with reader
      console.log('[POS Checkout] This device does not have reader - sending process_payment via WebSocket');
      sendProcessPayment({
        amountCents,
        tipAmount,
        tabId: checkoutTabInfo?.id,
        tabName: checkoutTabInfo?.name,
        subtotal: checkoutSubtotal
      });
    }
  }, [handleProcessPaymentWithTip, readerInfo, readerConnected, updateCheckoutStage, checkoutSubtotal, checkoutTabInfo, sendProcessPayment, sendTipUpdate, setTabs]);

  const handleItemClick = useCallback((item, modifierData = null) => {
    const timestamp = testMode && testTimeOffsetMins > 0
      ? new Date(Date.now() + testTimeOffsetMins * 60 * 1000).toISOString()
      : new Date().toISOString();
    const modifierName = typeof modifierData === 'string' ? modifierData : modifierData?.name;
    const modifierPrice = typeof modifierData === 'object' && modifierData !== null && modifierData.priceAdjustment !== undefined ? Number(modifierData.priceAdjustment) : 0;
    console.log(`[POS] Item clicked: "${item.name}"${modifierName ? ` with modifier: "${modifierName}"` : ''} at ${timestamp}`);
    console.log(`[POS] DEBUG - item.price: ${item.price}, modifierData:`, modifierData);
    
    // If no active tab, auto-create one first
    let targetTabId = activeTabId;
    
    // Check if active tab is paid or archived - don't allow adding items to paid/archived tabs
    const activeTab = tabs.find(t => t.id === activeTabId);
    console.log(`[POS] Active tab: ${activeTab?.name}, status: ${activeTab?.status}, isSpillage: ${activeTab?.isSpillage}`);
    if (activeTab?.status === 'paid' || activeTab?.status === 'archived') {
      console.log('[POS] Cannot add items to paid/archived tab');
      return;
    }
    
    if (!targetTabId) {
      const newTab = {
        id: `tab-${Date.now()}`,
        name: `P${nextTabNumber}`,
        items: []
      };
      setTabs(prev => [...prev, newTab]);
      setActiveTabId(newTab.id);
      setNextTabNumber(prev => prev + 1);
      targetTabId = newTab.id;
      console.log(`[POS] Auto-created new tab: ${newTab.name}`);
    }
    
    // Add item to the active tab with adjusted price
    const basePrice = parseFloat(item.price) || 0;
    const adjustedPrice = basePrice + modifierPrice;
    console.log(`[POS] Price calculation: item.price=${item.price}, basePrice=${basePrice}, modifierPrice=${modifierPrice}, adjustedPrice=${adjustedPrice}`);
    const newItem = {
      ...item,
      addedAt: timestamp,
      modifier: modifierName || item.modifier || null,
      modifierPriceAdjustment: modifierPrice,
      basePrice: basePrice,
      price: adjustedPrice
    };
    console.log(`[POS] New item created with price: ${newItem.price}`);
    
    setTabs(prev => {
      const updatedTabs = prev.map(tab => 
        tab.id === targetTabId 
          ? { ...tab, items: [...tab.items, newItem] }
          : tab
      );
      
      // Check spend limit after adding item
      const updatedTab = updatedTabs.find(t => t.id === targetTabId);
      if (updatedTab?.spendLimit) {
        const currentTotal = (updatedTab.items || []).reduce((sum, i) => sum + (parseFloat(i.price) || 0), 0);
        const remaining = updatedTab.spendLimit - currentTotal;
        if (remaining <= 20 && remaining > 0) {
          // Within $20 of limit - show warning
          setTimeout(() => {
            setSpendLimitWarningTab(updatedTab);
            setShowSpendLimitWarning(true);
          }, 100);
        }
      }
      
      return updatedTabs;
    });
    
    // Update last action with tab name
    const actionName = modifierName ? `${toTitleCase(item.name)} ${modifierName}` : toTitleCase(item.name);
    const targetTab = tabs.find(t => t.id === targetTabId);
    const tabDisplayName = (targetTab?.customName || targetTab?.name || '').toUpperCase();
    setLastAction({ type: 'add', itemName: actionName, tabName: tabDisplayName });
    
    // Save to server after item added
    setTimeout(() => saveToServer('item-added'), 500);
  }, [activeTabId, nextTabNumber, tabs, saveToServer, testMode, testTimeOffsetMins]);

  const handleRemoveItem = useCallback((index) => {
    if (!activeTabId) return;
    
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (!activeTab || !activeTab.items[index]) return;
    
    const itemToRemove = activeTab.items[index];
    console.log(`[POS] Item removed: "${itemToRemove.name}"`);
    
    setTabs(prev => prev.map(tab =>
      tab.id === activeTabId
        ? { ...tab, items: tab.items.filter((_, i) => i !== index) }
        : tab
    ));
    
    // Update last action with tab name
    const tabDisplayName = (activeTab?.customName || activeTab?.name || '').toUpperCase();
    setLastAction({ type: 'remove', itemName: toTitleCase(itemToRemove.name), tabName: tabDisplayName });
    
    // Save to server after item removed
    setTimeout(() => saveToServer('item-removed'), 500);
  }, [activeTabId, tabs, saveToServer]);

  const handleMoveItems = useCallback((fromTabId, toTabId, indices) => {
    if (!fromTabId || !toTabId || indices.length === 0) return;
    
    const fromTab = tabs.find(t => t.id === fromTabId);
    const toTab = tabs.find(t => t.id === toTabId);
    if (!fromTab || !toTab) return;
    
    // Get items to move (sorted indices in reverse to remove from end first)
    const sortedIndices = [...indices].sort((a, b) => b - a);
    const itemsToMove = indices.map(i => fromTab.items[i]).filter(Boolean);
    
    if (itemsToMove.length === 0) return;
    
    console.log(`[POS] Moving ${itemsToMove.length} items from ${fromTab.name} to ${toTab.name}`);
    
    setTabs(prev => prev.map(tab => {
      if (tab.id === fromTabId) {
        // Remove items from source tab
        const newItems = tab.items.filter((_, i) => !indices.includes(i));
        return { ...tab, items: newItems };
      }
      if (tab.id === toTabId) {
        // Add items to destination tab
        return { ...tab, items: [...tab.items, ...itemsToMove] };
      }
      return tab;
    }));
    
    // Update last action
    const fromTabName = (fromTab?.customName || fromTab?.name || '').toUpperCase();
    const toTabName = (toTab?.customName || toTab?.name || '').toUpperCase();
    setLastAction({ type: 'move', itemName: `${itemsToMove.length} item${itemsToMove.length > 1 ? 's' : ''}`, tabName: `${fromTabName} → ${toTabName}` });
  }, [tabs]);

  const handleDuplicateItems = useCallback((tabId, indices) => {
    if (!tabId || indices.length === 0) return;
    
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    
    // Get items to duplicate (with their modifiers)
    const itemsToDuplicate = indices.map(i => tab.items[i]).filter(Boolean);
    
    if (itemsToDuplicate.length === 0) return;
    
    // Create deep copies of the items (preserving modifiers)
    const duplicatedItems = itemsToDuplicate.map(item => ({
      ...item,
      // Generate new unique key for React
      _duplicateKey: `${item._id || item.name}-${Date.now()}-${Math.random()}`
    }));
    
    console.log(`[POS] Duplicating ${duplicatedItems.length} items in ${tab.name}`);
    
    setTabs(prev => prev.map(t => {
      if (t.id === tabId) {
        return { ...t, items: [...t.items, ...duplicatedItems] };
      }
      return t;
    }));
    
    // Update last action
    const tabDisplayName = (tab?.customName || tab?.name || '').toUpperCase();
    setLastAction({ type: 'add', itemName: `${duplicatedItems.length} item${duplicatedItems.length > 1 ? 's' : ''} duplicated`, tabName: tabDisplayName });
  }, [tabs]);

  // Use prop dimensions when isStandalone with explicit dimensions, otherwise use measured size
  // When using prop height, subtract the event header (50px) because frameSize.height already excludes it
  // (frameRef sits below the header, so the measured height is correct without adjustment)
  const effectiveWidth = (isStandalone && propOuterWidth) ? propOuterWidth : frameSize.width;
  const effectiveHeight = (isStandalone && propOuterHeight) ? propOuterHeight - 50 : frameSize.height;
  const frameReady = effectiveWidth > 0 && effectiveHeight > 0;

  const stageDims = useMemo(() => {
    if (!frameReady) return { width: 0, height: 0 };
    const fw = effectiveWidth;
    const fh = effectiveHeight;
    if (orientation === 'horizontal') return { width: fw, height: fh };
    // 9:19 vertical
    const stageHeight = fh;
    const stageWidth = fh * (9 / 19);
    return { width: stageWidth, height: stageHeight };
  }, [frameReady, effectiveWidth, effectiveHeight, orientation]);

  // ============================================
  // HORIZONTAL VIEW (CUSTOMER-FACING)
  // When orientation is horizontal and layoutMode is 'auto':
  // - If checkoutMode is true: show receipt/tipping screen
  // - Otherwise: show MenuGallery2
  // ============================================
  if (isHorizontalDevice) {
    // CHECKOUT MODE: Show receipt with tip options (Square-style light theme)
    // Also show when checkoutStage is 'success' to display the Thank You animation
    // Also show receipt prompt/keypad screens
    if ((checkoutMode && checkoutItems.length > 0) || checkoutStage === 'success' || showReceiptPrompt || showReceiptKeypad) {
      const tipPercentages = [
        { label: '15%', value: 0.15 },
        { label: '20%', value: 0.20 },
        { label: '25%', value: 0.25 },
      ];
      
      return (
        <div style={{ 
          width: (isStandalone && propOuterWidth) ? `${propOuterWidth}px` : '100vw', 
          height: (isStandalone && propOuterHeight) ? `${propOuterHeight}px` : '100vh', 
          overflow: 'hidden', 
          background: 'linear-gradient(to top, rgba(179, 179, 179, 1) 0%, rgba(185, 185, 185, 1) 8%, rgba(210, 210, 210, 1) 25%, rgba(240, 240, 240, 1) 50%, rgba(255, 255, 255, 1) 70%)',
          display: 'flex',
          flexDirection: 'column',
          color: '#333',
        }} className="pos-horizontal-checkout">
          {/* Header with logo - white background, no border */}
          <div style={{
            padding: '24px 36px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            minHeight: '102px',
            boxSizing: 'border-box',
          }}>
            {logoUrl && (
              <img 
                src={logoUrl} 
                alt="Echo" 
                style={{ height: '54px', width: 'auto' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </div>
          
          {/* Main Content Container - fixed height between header and footer */}
          <div style={{
            height: 'calc(100vh - 204px)',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '16px 20px',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}>
            {/* Inner container for all screens */}
            <div style={{
              width: '100%',
              maxWidth: '600px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              overflow: 'hidden',
            }}>
              {showReceiptKeypad ? (
                /* RECEIPT KEYPAD SCREEN - email/phone input */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  paddingTop: '1vh',
                  gap: '1vh',
                }}>
                  <div style={{ 
                    fontSize: 'clamp(18px, 3vh, 26px)', 
                    fontWeight: '600', 
                    color: '#333',
                    textAlign: 'center',
                  }}>
                    Enter Email or Phone
                  </div>
                  
                  {/* Input display */}
                  <div style={{
                    width: '98%',
                    padding: '12px',
                    fontSize: 'clamp(16px, 2.5vh, 22px)',
                    textAlign: 'center',
                    background: '#fff',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    minHeight: '40px',
                    color: receiptContact ? '#333' : '#999',
                  }}>
                    {receiptContact || 'email@example.com or (555) 123-4567'}
                  </div>
                  
                  {/* Keypad Container - Full width, minimal gaps */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    width: '100%',
                    flex: 1,
                  }}>
                    {/* Number/Special Row - 1,2,3,4,5,6,7,8,9,0,.,_,@,.com,⌫ */}
                    <div style={{
                      display: 'flex',
                      gap: '3px',
                      justifyContent: 'center',
                      padding: '0 2px',
                    }}>
                      {['1','2','3','4','5','6','7','8','9','0','.','_','@','.com','⌫'].map(key => (
                        <button
                          key={key}
                          onClick={() => {
                            if (key === '⌫') {
                              setReceiptContact(prev => prev.slice(0, -1));
                            } else {
                              setReceiptContact(prev => prev + key);
                            }
                          }}
                          style={{
                            padding: 'clamp(10px, 2vh, 16px) clamp(4px, 1vw, 8px)',
                            fontSize: key === '.com' ? 'clamp(12px, 2vh, 16px)' : 'clamp(16px, 2.5vh, 22px)',
                            fontWeight: '600',
                            background: key === '⌫' ? '#e0e0e0' : '#f5f5f5',
                            border: '1px solid #ccc',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            flex: key === '.com' ? '1.5' : '1',
                          }}
                        >
                          {key}
                        </button>
                      ))}
                    </div>
                    
                    {showSymbolKeypad ? (
                      /* SYMBOL KEYPAD */
                      <>
                        {/* Symbol rows */}
                        <div style={{
                          display: 'flex',
                          gap: '3px',
                          justifyContent: 'center',
                          padding: '0 2px',
                        }}>
                          {['!','#','$','%','&','*','(',')','-','+'].map(key => (
                            <button
                              key={key}
                              onClick={() => setReceiptContact(prev => prev + key)}
                              style={{
                                padding: 'clamp(12px, 2.5vh, 18px) clamp(8px, 1.5vw, 14px)',
                                fontSize: 'clamp(18px, 3vh, 24px)',
                                fontWeight: '600',
                                background: '#f5f5f5',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                flex: 1,
                              }}
                            >
                              {key}
                            </button>
                          ))}
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '3px',
                          justifyContent: 'center',
                          padding: '0 2px',
                        }}>
                          {['=','[',']','{','}','|','\\',':',';','"'].map(key => (
                            <button
                              key={key}
                              onClick={() => setReceiptContact(prev => prev + key)}
                              style={{
                                padding: 'clamp(12px, 2.5vh, 18px) clamp(8px, 1.5vw, 14px)',
                                fontSize: 'clamp(18px, 3vh, 24px)',
                                fontWeight: '600',
                                background: '#f5f5f5',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                flex: 1,
                              }}
                            >
                              {key}
                            </button>
                          ))}
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '3px',
                          justifyContent: 'center',
                          padding: '0 2px',
                        }}>
                          {["'",'<','>',',','?','/','~','`','^'].map(key => (
                            <button
                              key={key}
                              onClick={() => setReceiptContact(prev => prev + key)}
                              style={{
                                padding: 'clamp(12px, 2.5vh, 18px) clamp(8px, 1.5vw, 14px)',
                                fontSize: 'clamp(18px, 3vh, 24px)',
                                fontWeight: '600',
                                background: '#f5f5f5',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                flex: 1,
                              }}
                            >
                              {key}
                            </button>
                          ))}
                        </div>
                        
                        {/* RETURN, CANCEL, SEND row */}
                        <div style={{
                          display: 'flex',
                          gap: '6px',
                          marginTop: '6px',
                          padding: '0 2px',
                        }}>
                          <button
                            onClick={() => setShowSymbolKeypad(false)}
                            style={{
                              flex: 1,
                              padding: 'clamp(14px, 2.5vh, 20px)',
                              fontSize: 'clamp(16px, 2.5vh, 20px)',
                              fontWeight: '700',
                              background: '#2196F3',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                            }}
                          >
                            RETURN
                          </button>
                          <button
                            onClick={() => {
                              if (receiptTimerRef.current) clearTimeout(receiptTimerRef.current);
                              // Send receipt_canceled to vertical view
                              sendCheckoutStage('receipt_canceled');
                              setShowReceiptKeypad(false);
                              setShowReceiptPrompt(false);
                              setReceiptContact('');
                              setShowSymbolKeypad(false);
                              setCheckoutMode(false);
                              setCheckoutStage('');
                              // Clear vertical view status after 1.5 seconds
                              setTimeout(() => sendCheckoutStage(''), 1500);
                            }}
                            style={{
                              flex: 1,
                              padding: 'clamp(14px, 2.5vh, 20px)',
                              fontSize: 'clamp(16px, 2.5vh, 20px)',
                              fontWeight: '700',
                              background: '#666',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                            }}
                          >
                            CANCEL
                          </button>
                          <button
                            onClick={async () => {
                              if (!receiptContact) return;
                              setReceiptSending(true);
                              // Show "Sending Receipt" on vertical with spinner
                              sendCheckoutStage('receipt_sending');
                              try {
                                const response = await fetch('/api/receipts/send', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    contact: receiptContact,
                                    tabId: checkoutTabInfo?.id,
                                    items: checkoutItems,
                                    subtotal: checkoutSubtotal,
                                    tip: selectedTipAmount,
                                    total: (checkoutSubtotal * 1.08) + selectedTipAmount,
                                    paymentMethod: paymentMethod,
                                    cardBrand: cardBrand,
                                    cardLast4: cardLast4,
                                  }),
                                });
                                if (!response.ok) throw new Error('Failed to send receipt');
                                console.log('[POS] Receipt sent to:', receiptContact);
                                // Send 'receipt_sent' status to vertical view (shows green "RECEIPT SENT!")
                                sendCheckoutStage('receipt_sent');
                              } catch (err) {
                                console.error('[POS] Failed to send receipt:', err);
                                // Clear vertical notification on error
                                sendCheckoutStage('');
                              }
                              setReceiptSending(false);
                              setShowReceiptKeypad(false);
                              setShowReceiptPrompt(false);
                              setReceiptContact('');
                              setShowSymbolKeypad(false);
                              setCheckoutMode(false);
                              setCheckoutStage('');
                              setCardBrand('');
                              setCardLast4('');
                              // Clear vertical view status after 1.5 seconds
                              setTimeout(() => sendCheckoutStage(''), 1500);
                            }}
                            disabled={!receiptContact || receiptSending}
                            style={{
                              flex: 1.5,
                              padding: 'clamp(14px, 2.5vh, 20px)',
                              fontSize: 'clamp(16px, 2.5vh, 20px)',
                              fontWeight: '700',
                              background: receiptContact ? '#22c55e' : '#ccc',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: receiptContact ? 'pointer' : 'not-allowed',
                            }}
                          >
                            {receiptSending ? 'SENDING...' : 'SEND'}
                          </button>
                        </div>
                      </>
                    ) : (
                      /* LETTER KEYPAD */
                      <>
                        {/* Letter row 1: q-p */}
                        <div style={{
                          display: 'flex',
                          gap: '3px',
                          justifyContent: 'center',
                          padding: '0 2px',
                        }}>
                          {['q','w','e','r','t','y','u','i','o','p'].map(key => (
                            <button
                              key={key}
                              onClick={() => setReceiptContact(prev => prev + key)}
                              style={{
                                padding: 'clamp(12px, 2.5vh, 18px) clamp(8px, 1.5vw, 14px)',
                                fontSize: 'clamp(18px, 3vh, 24px)',
                                fontWeight: '600',
                                background: '#f5f5f5',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                flex: 1,
                                textTransform: 'lowercase',
                              }}
                            >
                              {key}
                            </button>
                          ))}
                        </div>
                        
                        {/* Letter row 2: a-l */}
                        <div style={{
                          display: 'flex',
                          gap: '3px',
                          justifyContent: 'center',
                          padding: '0 2px',
                        }}>
                          {['a','s','d','f','g','h','j','k','l'].map(key => (
                            <button
                              key={key}
                              onClick={() => setReceiptContact(prev => prev + key)}
                              style={{
                                padding: 'clamp(12px, 2.5vh, 18px) clamp(8px, 1.5vw, 14px)',
                                fontSize: 'clamp(18px, 3vh, 24px)',
                                fontWeight: '600',
                                background: '#f5f5f5',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                flex: 1,
                                textTransform: 'lowercase',
                              }}
                            >
                              {key}
                            </button>
                          ))}
                        </div>
                        
                        {/* Letter row 3: z-m */}
                        <div style={{
                          display: 'flex',
                          gap: '3px',
                          justifyContent: 'center',
                          padding: '0 2px',
                        }}>
                          {['z','x','c','v','b','n','m'].map(key => (
                            <button
                              key={key}
                              onClick={() => setReceiptContact(prev => prev + key)}
                              style={{
                                padding: 'clamp(12px, 2.5vh, 18px) clamp(8px, 1.5vw, 14px)',
                                fontSize: 'clamp(18px, 3vh, 24px)',
                                fontWeight: '600',
                                background: '#f5f5f5',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                flex: 1,
                                textTransform: 'lowercase',
                              }}
                            >
                              {key}
                            </button>
                          ))}
                        </div>
                        
                        {/* SYMB, CANCEL, SEND row */}
                        <div style={{
                          display: 'flex',
                          gap: '6px',
                          marginTop: '6px',
                          padding: '0 2px',
                        }}>
                          <button
                            onClick={() => setShowSymbolKeypad(true)}
                            style={{
                              flex: 1,
                              padding: 'clamp(14px, 2.5vh, 20px)',
                              fontSize: 'clamp(16px, 2.5vh, 20px)',
                              fontWeight: '700',
                              background: '#2196F3',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                            }}
                          >
                            SYMB
                          </button>
                          <button
                            onClick={() => {
                              if (receiptTimerRef.current) clearTimeout(receiptTimerRef.current);
                              // Send receipt_canceled to vertical view
                              sendCheckoutStage('receipt_canceled');
                              setShowReceiptKeypad(false);
                              setShowReceiptPrompt(false);
                              setReceiptContact('');
                              setShowSymbolKeypad(false);
                              setCheckoutMode(false);
                              setCheckoutStage('');
                              // Clear vertical view status after 1.5 seconds
                              setTimeout(() => sendCheckoutStage(''), 1500);
                            }}
                            style={{
                              flex: 1,
                              padding: 'clamp(14px, 2.5vh, 20px)',
                              fontSize: 'clamp(16px, 2.5vh, 20px)',
                              fontWeight: '700',
                              background: '#666',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                            }}
                          >
                            CANCEL
                          </button>
                          <button
                            onClick={async () => {
                              if (!receiptContact) return;
                              setReceiptSending(true);
                              // Show "Sending Receipt" on vertical with spinner
                              sendCheckoutStage('receipt_sending');
                              try {
                                const response = await fetch('/api/receipts/send', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    contact: receiptContact,
                                    tabId: checkoutTabInfo?.id,
                                    items: checkoutItems,
                                    subtotal: checkoutSubtotal,
                                    tip: selectedTipAmount,
                                    total: (checkoutSubtotal * 1.08) + selectedTipAmount,
                                    paymentMethod: paymentMethod,
                                    cardBrand: cardBrand,
                                    cardLast4: cardLast4,
                                  }),
                                });
                                if (!response.ok) throw new Error('Failed to send receipt');
                                console.log('[POS] Receipt sent to:', receiptContact);
                                // Send 'receipt_sent' status to vertical view (shows green "RECEIPT SENT!")
                                sendCheckoutStage('receipt_sent');
                              } catch (err) {
                                console.error('[POS] Failed to send receipt:', err);
                                // Clear vertical notification on error
                                sendCheckoutStage('');
                              }
                              setReceiptSending(false);
                              setShowReceiptKeypad(false);
                              setShowReceiptPrompt(false);
                              setReceiptContact('');
                              setShowSymbolKeypad(false);
                              setCheckoutMode(false);
                              setCheckoutStage('');
                              setCardBrand('');
                              setCardLast4('');
                              // Clear vertical view status after 1.5 seconds
                              setTimeout(() => sendCheckoutStage(''), 1500);
                            }}
                            disabled={!receiptContact || receiptSending}
                            style={{
                              flex: 1.5,
                              padding: 'clamp(14px, 2.5vh, 20px)',
                              fontSize: 'clamp(16px, 2.5vh, 20px)',
                              fontWeight: '700',
                              background: receiptContact ? '#22c55e' : '#ccc',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: receiptContact ? 'pointer' : 'not-allowed',
                            }}
                          >
                            {receiptSending ? 'SENDING...' : 'SEND'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : showReceiptPrompt ? (
                /* RECEIPT PROMPT SCREEN - "Would you like a receipt?" */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4vh',
                }}>
                  <div style={{ 
                    fontSize: 'clamp(24px, 5vh, 36px)', 
                    fontWeight: '600', 
                    color: '#333',
                    textAlign: 'center',
                  }}>
                    Would you like a receipt?
                  </div>
                  
                  <div style={{ display: 'flex', gap: '20px', width: '80%', maxWidth: '400px' }}>
                    <button
                      onClick={() => {
                        // Clear timer and return to menu
                        if (receiptTimerRef.current) clearTimeout(receiptTimerRef.current);
                        setShowReceiptPrompt(false);
                        setCheckoutMode(false);
                        setCheckoutStage('');
                        // Clear vertical notification when declining receipt
                        sendCheckoutStage('');
                      }}
                      style={{
                        flex: 1,
                        padding: '20px',
                        fontSize: 'clamp(18px, 3vh, 24px)',
                        fontWeight: '600',
                        background: '#f0f0f0',
                        color: '#666',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      No Thanks
                    </button>
                    <button
                      onClick={() => {
                        // Clear timer and show keypad
                        if (receiptTimerRef.current) clearTimeout(receiptTimerRef.current);
                        setShowReceiptPrompt(false);
                        setShowReceiptKeypad(true);
                        // Broadcast to vertical view
                        sendCheckoutStage('receipt_request');
                      }}
                      style={{
                        flex: 1,
                        padding: '20px',
                        fontSize: 'clamp(18px, 3vh, 24px)',
                        fontWeight: '600',
                        background: '#22c55e',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      Yes
                    </button>
                  </div>
                  
                  {/* Timer indicator */}
                  <div style={{ fontSize: '14px', color: '#999' }}>
                    Auto-dismiss in 10 seconds
                  </div>
                </div>
              ) : paymentMethod === 'cash' && checkoutStage === 'success' ? (
                /* CASH SUCCESS ANIMATION - same as CREDIT but says "Payment Complete!" */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2vh',
                }}>
                  {/* CSS Animations */}
                  <style>{`
                    @keyframes success-checkmark {
                      0% { transform: scale(0); opacity: 0; }
                      50% { transform: scale(1.2); opacity: 1; }
                      100% { transform: scale(1); opacity: 1; }
                    }
                    @keyframes success-circle {
                      0% { stroke-dashoffset: 166; }
                      100% { stroke-dashoffset: 0; }
                    }
                    .success-animation {
                      animation: success-checkmark 0.6s ease-out;
                    }
                  `}</style>
                  
                  <div className="success-animation" style={{ textAlign: 'center' }}>
                    <svg width="120" height="120" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="54" fill="none" stroke="#22c55e" strokeWidth="4" 
                        strokeDasharray="339.292" strokeDashoffset="0"
                        style={{ animation: 'success-circle 0.6s ease-out' }} />
                      <path d="M34 60 L52 78 L86 44" fill="none" stroke="#22c55e" strokeWidth="6" 
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div style={{ 
                    fontSize: 'clamp(28px, 5vh, 42px)', 
                    fontWeight: '700', 
                    color: '#22c55e',
                    textAlign: 'center',
                  }}>
                    Thank you!
                  </div>
                  <div style={{ fontSize: 'clamp(56px, 12vh, 84px)', fontWeight: '700', color: '#333' }}>
                    ${(checkoutSubtotal * 1.08).toFixed(2)}
                  </div>
                </div>
              ) : paymentMethod === 'cash' && checkoutStage === 'cash' && !showTabView ? (
                /* CASH PAYMENT SCREEN - shows just Total and View Tab button */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '3vh',
                }}>
                  {/* Total amount display (including 8% tax) */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 'clamp(14px, 2vh, 18px)', color: '#888', marginBottom: '1vh' }}>
                      Total Due
                    </div>
                    <div style={{ fontSize: 'clamp(56px, 12vh, 84px)', fontWeight: '700', color: '#333' }}>
                      ${(checkoutSubtotal * 1.08).toFixed(2)}
                    </div>
                  </div>
                  
                  {/* View Tab button - styled like CREDIT tip page back button */}
                  <button
                    onClick={() => setShowTabView(true)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      background: 'transparent',
                      color: '#666',
                      border: 'none',
                      cursor: 'pointer',
                      marginTop: '8px',
                    }}
                  >
                    View Tab
                  </button>
                </div>
              ) : showScanCard ? (
                /* SCAN CARD SCREEN - shown after tip selection */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2vh',
                }}>
                  {/* CSS Animations */}
                  <style>{`
                    @keyframes success-checkmark {
                      0% { transform: scale(0); opacity: 0; }
                      50% { transform: scale(1.2); opacity: 1; }
                      100% { transform: scale(1); opacity: 1; }
                    }
                    @keyframes success-circle {
                      0% { stroke-dashoffset: 166; }
                      100% { stroke-dashoffset: 0; }
                    }
                    .success-animation {
                      animation: success-checkmark 0.6s ease-out;
                    }
                  `}</style>
                  
                  {paymentStatus === 'payment_success' ? (
                    /* SUCCESS ANIMATION */
                    <>
                      <div className="success-animation" style={{ textAlign: 'center' }}>
                        <svg width="120" height="120" viewBox="0 0 120 120">
                          <circle cx="60" cy="60" r="54" fill="none" stroke="#22c55e" strokeWidth="4" 
                            strokeDasharray="339.292" strokeDashoffset="0"
                            style={{ animation: 'success-circle 0.6s ease-out' }} />
                          <path d="M34 60 L52 78 L86 44" fill="none" stroke="#22c55e" strokeWidth="6" 
                            strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <div style={{ 
                        fontSize: 'clamp(28px, 5vh, 42px)', 
                        fontWeight: '700', 
                        color: '#22c55e',
                        textAlign: 'center',
                      }}>
                        Thank you!
                      </div>
                      <div style={{ fontSize: 'clamp(56px, 12vh, 84px)', fontWeight: '700', color: '#333' }}>
                        ${(checkoutSubtotal * 1.08 + selectedTipAmount).toFixed(2)}
                      </div>
                    </>
                  ) : (
                    /* PAYMENT SCREEN (waiting or failed) */
                    <>
                      {/* Total amount at top */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 'clamp(56px, 12vh, 84px)', fontWeight: '700', color: '#333' }}>
                          ${(checkoutSubtotal * 1.08 + selectedTipAmount).toFixed(2)}
                        </div>
                        {selectedTipAmount > 0 && (
                          <div style={{ fontSize: 'clamp(14px, 2vh, 18px)', color: '#888', marginTop: '0.5vh' }}>
                            (includes ${selectedTipAmount.toFixed(2)} tip)
                          </div>
                        )}
                      </div>
                      
                      {/* Simulate Success/Failure Buttons */}
                      <div style={{ display: 'flex', gap: '12px', marginTop: '2vh', justifyContent: 'center' }}>
                        <button
                          onClick={() => {
                            const amountCents = Math.round((checkoutSubtotal + selectedTipAmount) * 100);
                            sendSimulateTap({ amountCents, shouldSucceed: true });
                          }}
                          disabled={checkoutStage === 'processing' || paymentStatus === 'payment_success'}
                          style={{
                            padding: '12px 24px',
                            fontSize: 'clamp(14px, 2vh, 18px)',
                            fontWeight: '600',
                            background: checkoutStage === 'processing' || paymentStatus === 'payment_success' ? '#6c757d' : '#28a745',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: checkoutStage === 'processing' || paymentStatus === 'payment_success' ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Simulate Success
                        </button>
                        <button
                          onClick={() => {
                            const amountCents = Math.round((checkoutSubtotal + selectedTipAmount) * 100);
                            sendSimulateTap({ amountCents, shouldSucceed: false });
                          }}
                          disabled={checkoutStage === 'processing' || paymentStatus === 'payment_success'}
                          style={{
                            padding: '12px 24px',
                            fontSize: 'clamp(14px, 2vh, 18px)',
                            fontWeight: '600',
                            background: checkoutStage === 'processing' || paymentStatus === 'payment_success' ? '#6c757d' : '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: checkoutStage === 'processing' || paymentStatus === 'payment_success' ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Simulate Failure
                        </button>
                      </div>
                      
                      {/* Instruction text or Processing */}
                      <div style={{ 
                        fontSize: 'clamp(24px, 4vh, 36px)', 
                        fontWeight: '600', 
                        color: paymentStatus === 'payment_failed' ? '#ef4444' : '#800080',
                        textAlign: 'center',
                        marginTop: '1vh',
                      }}>
                        {checkoutStage === 'processing' ? 'Processing...' : paymentStatus === 'payment_failed' ? 'Payment Failed' : 'Tap, Insert, Swipe'}
                      </div>
                      
                      {/* Stripe Reader M2 icon */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginTop: '1vh',
                      }}>
                        <img 
                          src="/assets/icons/StripeReaderM2.png" 
                          alt="Stripe Reader M2"
                          style={{
                            width: 'clamp(120px, 25vh, 200px)',
                            height: 'auto',
                            filter: paymentStatus === 'payment_failed' 
                              ? 'brightness(0) saturate(100%) invert(27%) sepia(98%) saturate(7426%) hue-rotate(356deg) brightness(95%) contrast(114%)' // Red
                              : 'brightness(0) saturate(100%) invert(12%) sepia(100%) saturate(5000%) hue-rotate(280deg) brightness(80%)', // Purple
                          }}
                        />
                      </div>
                    </>
                  )}
                  
                                    
                  {/* Retry button - shown after payment failure */}
                  {paymentStatus === 'payment_failed' && !checkoutLoading && (
                    <button
                      onClick={() => {
                        setPaymentStatus(null);
                        setPaymentStatusMessage(null);
                        handleProcessPaymentWithTip(selectedTipAmount);
                      }}
                      style={{
                        padding: '12px 32px',
                        fontSize: 'clamp(14px, 2vh, 18px)',
                        fontWeight: '600',
                        background: '#800080',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginTop: '2vh',
                      }}
                    >
                      Retry
                    </button>
                  )}
                  
                  {/* Back button - styled like View Tab button */}
                  <button
                    onClick={() => {
                      setShowScanCard(false);
                      setSelectedTipAmount(0);
                      updateCheckoutStage('tip'); // Broadcast back to tip screen
                    }}
                    disabled={checkoutLoading}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      background: 'transparent',
                      color: '#666',
                      border: 'none',
                      cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                      marginTop: '2vh',
                    }}
                  >
                    Back
                  </button>
                </div>
              ) : !showTabView ? (
                /* TIP VIEW or KEYPAD VIEW - fills container height */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                }}>
                  {/* Large Total Display (including 8% tax) */}
                  <div style={{ textAlign: 'center', marginBottom: '1vh', flexShrink: 0 }}>
                    <div style={{ fontSize: 'clamp(28px, 5vh, 42px)', fontWeight: '700', color: '#333' }}>
                      ${(checkoutSubtotal * 1.08).toFixed(2)}
                    </div>
                    <div style={{ fontSize: 'clamp(12px, 1.8vh, 16px)', color: '#888', marginTop: '0.3vh' }}>
                      Add a tip
                    </div>
                  </div>
                  
                  {/* Content area - flex grows to fill */}
                  <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '1.5vh' }}>
                    {!showCustomTip ? (
                      <>
                        {/* Tip percentage buttons - horizontal row - takes up most space */}
                        <div style={{ display: 'flex', gap: 'clamp(8px, 1.5vw, 16px)', width: '100%', flex: 2 }}>
                          {tipPercentages.map(({ label, value }) => {
                            const tipAmount = checkoutSubtotal * value;
                            return (
                              <button
                                key={label}
                                onClick={() => handleTipSelected(tipAmount)}
                                disabled={checkoutLoading}
                                style={{
                                  flex: 1,
                                  fontSize: 'clamp(12px, 2vh, 16px)',
                                  background: '#fff',
                                  color: '#800080',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '8px',
                                  cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '1vh',
                                  opacity: checkoutLoading ? 0.6 : 1,
                                }}
                              >
                                <span style={{ fontSize: 'clamp(28px, 6vh, 44px)', fontWeight: '400' }}>{label}</span>
                                <span style={{ fontSize: 'clamp(16px, 3vh, 22px)', color: '#666' }}>${tipAmount.toFixed(2)}</span>
                              </button>
                            );
                          })}
                        </div>
                        
                        {/* Custom Tip Amount button */}
                        <button
                          onClick={() => setShowCustomTip(true)}
                          disabled={checkoutLoading}
                          style={{
                            width: '100%',
                            flex: 1,
                            fontSize: 'clamp(16px, 3vh, 22px)',
                            fontWeight: '600',
                            background: '#fff',
                            color: '#800080',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          Custom Tip Amount
                        </button>
                        
                        {/* No Tip button */}
                        <button
                          onClick={() => handleTipSelected(0)}
                          disabled={checkoutLoading}
                          style={{
                            width: '100%',
                            flex: 1,
                            fontSize: 'clamp(16px, 3vh, 22px)',
                            fontWeight: '600',
                            background: '#fff',
                            color: '#800080',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          No Tip
                        </button>
                      </>
                    ) : (
                      /* Custom tip input with number pad */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(4px, 0.8vh, 8px)', width: '100%', flex: 1 }}>
                        {/* Display area */}
                        <div style={{
                          width: '100%',
                          padding: 'clamp(6px, 1vh, 12px)',
                          fontSize: 'clamp(20px, 3.5vh, 32px)',
                          fontWeight: '500',
                          background: '#fff',
                          color: '#333',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          boxSizing: 'border-box',
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          ${customTipAmount || '0'}
                        </div>
                        
                        {/* Show calculated total */}
                        <div style={{ textAlign: 'center', fontSize: 'clamp(12px, 1.8vh, 16px)', color: '#666' }}>
                          Total: ${(checkoutSubtotal + (parseFloat(customTipAmount) || 0)).toFixed(2)}
                        </div>
                        
                        {/* Number pad */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(3px, 0.6vh, 6px)', flex: 1 }}>
                          {/* Row 1: 1, 2, 3 */}
                          <div style={{ display: 'flex', gap: 'clamp(3px, 0.6vh, 6px)', flex: 1 }}>
                            {['1', '2', '3'].map((num) => (
                              <button
                                key={num}
                                onClick={() => setCustomTipAmount(prev => prev + num)}
                                style={{
                                  flex: 1,
                                  fontSize: 'clamp(16px, 2.5vh, 22px)',
                                  fontWeight: '600',
                                  background: '#fff',
                                  color: '#333',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {num}
                              </button>
                            ))}
                          </div>
                          {/* Row 2: 4, 5, 6 */}
                          <div style={{ display: 'flex', gap: 'clamp(3px, 0.6vh, 6px)', flex: 1 }}>
                            {['4', '5', '6'].map((num) => (
                              <button
                                key={num}
                                onClick={() => setCustomTipAmount(prev => prev + num)}
                                style={{
                                  flex: 1,
                                  fontSize: 'clamp(16px, 2.5vh, 22px)',
                                  fontWeight: '600',
                                  background: '#fff',
                                  color: '#333',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {num}
                              </button>
                            ))}
                          </div>
                          {/* Row 3: 7, 8, 9 */}
                          <div style={{ display: 'flex', gap: 'clamp(3px, 0.6vh, 6px)', flex: 1 }}>
                            {['7', '8', '9'].map((num) => (
                              <button
                                key={num}
                                onClick={() => setCustomTipAmount(prev => prev + num)}
                                style={{
                                  flex: 1,
                                  fontSize: 'clamp(16px, 2.5vh, 22px)',
                                  fontWeight: '600',
                                  background: '#fff',
                                  color: '#333',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {num}
                              </button>
                            ))}
                          </div>
                          {/* Row 4: Undo, 0, Check */}
                          <div style={{ display: 'flex', gap: 'clamp(3px, 0.6vh, 6px)', flex: 1 }}>
                            {/* Undo button */}
                            <button
                              onClick={() => setCustomTipAmount(prev => prev.slice(0, -1))}
                              style={{
                                flex: 1,
                                fontSize: 'clamp(14px, 2vh, 18px)',
                                fontWeight: '600',
                                background: '#fff',
                                color: '#666',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <img 
                                src="/assets/icons/backspace.svg" 
                                alt="Backspace" 
                                style={{ width: 'clamp(24px, 4vh, 36px)', height: 'clamp(24px, 4vh, 36px)' }}
                              />
                            </button>
                            {/* 0 button */}
                            <button
                              onClick={() => setCustomTipAmount(prev => prev + '0')}
                              style={{
                                flex: 1,
                                fontSize: 'clamp(16px, 2.5vh, 22px)',
                                fontWeight: '600',
                                background: '#fff',
                                color: '#333',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              0
                            </button>
                            {/* Green check button */}
                            <button
                              onClick={() => handleTipSelected(parseFloat(customTipAmount) || 0)}
                              disabled={checkoutLoading}
                              style={{
                                flex: 1,
                                fontSize: 'clamp(16px, 2.5vh, 22px)',
                                fontWeight: '600',
                                background: checkoutLoading ? '#ccc' : '#fff',
                                color: '#22c55e',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <img 
                                src="/assets/icons/check.svg" 
                                alt="Confirm" 
                                style={{ width: 'clamp(20px, 3.5vh, 32px)', height: 'clamp(20px, 3.5vh, 32px)', filter: 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(95%) contrast(90%)' }}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* View Tab / Back button - always at bottom */}
                  <button
                    onClick={() => {
                      if (showCustomTip) {
                        setShowCustomTip(false);
                        setCustomTipAmount('');
                        // Stay on tip screen, no stage change needed
                      } else {
                        setShowTabView(true);
                        updateCheckoutStage('tab');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      background: 'transparent',
                      color: '#666',
                      border: 'none',
                      cursor: 'pointer',
                      flexShrink: 0,
                      marginTop: '8px',
                    }}
                  >
                    {showCustomTip ? 'Back' : 'View Tab'}
                  </button>
                </div>
              ) : (
                /* TAB VIEW - Receipt with items - fills container */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                }}>
                  {/* Receipt container - scrollable items inside */}
                  <div style={{
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    background: '#fff',
                    borderRadius: '8px',
                    padding: '16px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}>
                    {/* Items list - ONLY this scrolls */}
                    <div ref={receiptContainerRef} style={{
                      flex: 1,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      minHeight: 0,
                      WebkitOverflowScrolling: 'touch',
                      touchAction: 'pan-y',
                    }}>
                      {checkoutItems.map((item, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '10px 0',
                          borderBottom: idx < checkoutItems.length - 1 ? '1px solid #e0e0e0' : 'none',
                          fontSize: '16px',
                        }}>
                          <span style={{ color: '#333' }}>
                            {(item.name || '').toUpperCase()}
                            {item.modifier && <span style={{ color: '#888', marginLeft: '6px' }}>({item.modifier})</span>}
                          </span>
                          <span style={{ fontWeight: '500', color: '#333' }}>${(item.price || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Subtotal, Tax, Total - always visible at bottom of receipt */}
                    <div style={{
                      padding: '12px 0 0 0',
                      marginTop: '8px',
                      borderTop: '2px solid #333',
                      flexShrink: 0,
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '14px',
                        color: '#666',
                        marginBottom: '4px',
                      }}>
                        <span>Subtotal</span>
                        <span>${checkoutSubtotal.toFixed(2)}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '14px',
                        color: '#666',
                        marginBottom: '8px',
                      }}>
                        <span>Tax (8%)</span>
                        <span>${(checkoutSubtotal * 0.08).toFixed(2)}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#333',
                      }}>
                        <span>Total</span>
                        <span>${(checkoutSubtotal * 1.08).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Back button - always visible at bottom */}
                  <button
                    onClick={() => {
                      setShowTabView(false);
                      // Return to the appropriate screen based on payment method
                      if (paymentMethod === 'cash') {
                        updateCheckoutStage('cash');
                      } else {
                        updateCheckoutStage('tip');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      background: 'transparent',
                      color: '#666',
                      border: 'none',
                      cursor: 'pointer',
                      flexShrink: 0,
                      marginTop: '8px',
                    }}
                  >
                    Back
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Empty footer - same size as header */}
          <div style={{
            padding: '24px 36px',
            background: 'transparent',
            minHeight: '102px',
            boxSizing: 'border-box',
            flexShrink: 0,
          }} />
        </div>
      );
    }
    
    // DEFAULT: Show MenuGallery2
    return (
      <div style={{ 
        width: (isStandalone && propOuterWidth) ? `${propOuterWidth}px` : '100%', 
        height: (isStandalone && propOuterHeight) ? `${propOuterHeight}px` : '100vh', 
        overflow: 'hidden', 
        background: '#fff',
        fontFamily: 'Montserrat, "Helvetica Neue", Helvetica, Arial, sans-serif'
      }}>
        <MenuGallery2 
          viewMode="menu"
          orientationOverride="horizontal"
          outerWidth={propOuterWidth}
          outerHeight={propOuterHeight}
          disableInnerScale={true}
        />
      </div>
    );
  }

  // ============================================
  // VERTICAL VIEW (POS ADMIN INTERFACE)
  // When layoutMode is forced (not 'auto'), OR when layoutMode is 'auto' with vertical orientation
  // we're in standalone mode - render POSContent directly filling the viewport without the viewer wrapper
  // ============================================
  const isStandaloneMode = layoutMode !== 'auto' || (layoutMode === 'auto' && orientation === 'vertical');

  // Standalone mode: render POSContent directly filling the viewport
  if (isStandaloneMode) {
    // If showing brief summary view after event end (only if showEventSetup is false)
    // Also show if isPostEventEdit is true (resumed event in post-event mode)
    if (showSummaryView && (eventSummary || isPostEventEdit) && !showEventSetup) {
      // Use prop dimensions when available, otherwise use viewport
      const summaryHeight = (isStandalone && propOuterHeight) ? `${propOuterHeight}px` : '100vh';
      
      // Responsive sizing based on container width
      const sw = propOuterWidth || 375;
      const bigNum = Math.max(20, Math.min(32, sw / 10));
      const medNum = Math.max(16, Math.min(24, sw / 13));
      const labelFs = Math.max(11, Math.min(14, sw / 26));
      const headFs  = Math.max(16, Math.min(24, sw / 15));
      const subHeadFs = Math.max(13, Math.min(18, sw / 20));
      const padH = Math.max(10, Math.min(20, sw / 18));

      return (
        <div style={{
          width: '100%',
          height: summaryHeight,
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}>
          {/* Header with logo - matching POS UI */}
          <div style={{
            background: '#fff',
            padding: '8px 16px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}>
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Echo"
                style={{
                  height: '32px',
                  width: 'auto',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
          </div>

          {/* Summary content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: `${padH}px`,
            boxSizing: 'border-box',
            width: '100%',
          }}>
            {(() => {
              // Calculate summary from tabs if eventSummary is null
              const computedSummary = eventSummary || (() => {
                const paidTabs = tabs.filter(t => !t.isSpillage && (t.status === 'paid' || t.status === 'archived' || t.status === 'closed'));
                const totalRevenue = paidTabs.reduce((sum, tab) => {
                  const tabItemsTotal = (tab.items || []).reduce((itemSum, item) => itemSum + (item.price || item.finalPrice || 0), 0);
                  return sum + tabItemsTotal;
                }, 0);
                const totalTips = paidTabs.reduce((sum, tab) => sum + (tab.tipAmount || 0), 0);
                const totalItems = paidTabs.reduce((sum, tab) => sum + (tab.items?.length || 0), 0);
                const totalTabs = paidTabs.length;
                return { totalRevenue, totalTips, totalItems, totalTabs };
              })();

              return (
            <div style={{ width: '100%', maxWidth: '600px', margin: '0 auto', boxSizing: 'border-box' }}>
              <h1 style={{ fontSize: `${headFs}px`, marginBottom: `${padH}px`, textAlign: 'center', color: '#333' }}>
                {eventName || 'Event'}
              </h1>

              {/* Totals */}
              <div style={{
                background: '#f5f5f5',
                borderRadius: '12px',
                padding: `${padH}px`,
                marginBottom: `${padH}px`,
                border: '1px solid #e0e0e0',
                boxSizing: 'border-box',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: `${bigNum}px`, fontWeight: 'bold', color: '#4CAF50', wordBreak: 'break-all' }}>
                      ${(computedSummary.totalRevenue || 0).toFixed(2)}
                    </div>
                    <div style={{ fontSize: `${labelFs}px`, color: '#666' }}>Sales</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: `${bigNum}px`, fontWeight: 'bold', color: '#2196F3', wordBreak: 'break-all' }}>
                      ${(computedSummary.totalTips || 0).toFixed(2)}
                    </div>
                    <div style={{ fontSize: `${labelFs}px`, color: '#666' }}>Tips</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: `${medNum}px`, fontWeight: 'bold', color: '#333' }}>
                      {computedSummary.totalItems || 0}
                    </div>
                    <div style={{ fontSize: `${labelFs}px`, color: '#666' }}>Total Items</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: `${medNum}px`, fontWeight: 'bold', color: '#333' }}>
                      {computedSummary.totalTabs || 0}
                    </div>
                    <div style={{ fontSize: `${labelFs}px`, color: '#666' }}>Total Tabs</div>
                  </div>
                </div>
              </div>

              {/* Income Statement Section */}
              <div style={{
                background: '#f5f5f5',
                borderRadius: '12px',
                padding: `${padH}px`,
                marginBottom: `${padH}px`,
                border: '1px solid #e0e0e0',
                boxSizing: 'border-box',
              }}>
                <h2 style={{ fontSize: `${subHeadFs}px`, marginBottom: '12px', color: '#333' }}>Income Statement</h2>

                {/* Spillage - using shared spillageTotal for consistency */}
                {spillageTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: '#fff', borderRadius: '8px', marginBottom: '8px', boxSizing: 'border-box' }}>
                    <span style={{ fontWeight: 'bold', color: '#333', fontSize: `${labelFs}px` }}>Spillage</span>
                    <span style={{ fontWeight: 'bold', color: '#ef4444', fontSize: `${labelFs}px` }}>-${spillageTotal.toFixed(2)}</span>
                  </div>
                )}

                {/* Net (simplified - full breakdown in Event Setup) */}
                {(() => {
                  const sales = computedSummary.totalRevenue || 0;
                  const tips = computedSummary.totalTips || 0;
                  const netIncome = sales + tips;
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: netIncome >= 0 ? '#dcfce7' : '#fee2e2', borderRadius: '8px', marginTop: '8px', boxSizing: 'border-box', gap: '8px' }}>
                      <span style={{ fontWeight: 'bold', fontSize: `${labelFs}px`, color: '#333', flexShrink: 0 }}>Net (before expenses)</span>
                      <span style={{ fontWeight: 'bold', fontSize: `${labelFs}px`, color: netIncome >= 0 ? '#22c55e' : '#ef4444', whiteSpace: 'nowrap' }}>
                        {netIncome >= 0 ? '' : '-'}${Math.abs(netIncome).toFixed(2)}
                      </span>
                    </div>
                  );
                })()}

                <div style={{ fontSize: `${Math.max(10, labelFs - 1)}px`, color: '#666', marginTop: '10px', textAlign: 'center' }}>
                  Full breakdown with COGS & Operating Expenses available in Finalize Event
                </div>
              </div>
              
              {/* Action Buttons - Show Finalize Event when in post-event mode */}
              {(eventSummary || isPostEventEdit) ? (
                <div style={{ display: 'flex', gap: '12px', marginBottom: `${padH}px`, boxSizing: 'border-box' }}>
                  <button
                    onClick={() => {
                      console.log('[Event Summary] FINALIZE EVENT - returning to event summary page');
                      setShowSummaryView(false);
                      setShowEventSetup(true);
                    }}
                    disabled={syncing}
                    style={{
                      flex: 1,
                      padding: '14px 8px',
                      background: syncing ? '#ccc' : '#800080',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: `${Math.max(14, Math.min(18, sw / 20))}px`,
                      fontWeight: 'bold',
                      cursor: syncing ? 'not-allowed' : 'pointer',
                      boxSizing: 'border-box',
                    }}
                  >
                    FINALIZE EVENT
                  </button>
                </div>
              ) : (
                /* Finalize Event Button - goes directly to event setup/finalize page */
                <button
                  onClick={() => {
                    console.log('[Event Summary] FINALIZE EVENT clicked');
                    const offsetMs = testMode ? testTimeOffsetMins * 60 * 1000 : 0;
                    const endTimeValue = eventSummary?.endTime
                      ? new Date(new Date(eventSummary.endTime).getTime() + offsetMs).toTimeString().slice(0, 5)
                      : new Date(Date.now() + offsetMs).toTimeString().slice(0, 5);
                    const buildInventory = (cat) => allItems
                      .filter(item => normalizeCategoryKey(item.category) === cat)
                      .map(item => ({ name: item.name, sent: '', returned: '' }));
                    setEventSetupData(prev => ({
                      ...prev,
                      eventName: eventName || '',
                      eventDate: eventStarted ? new Date(eventStarted).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                      startTime: eventStarted ? new Date(eventStarted).toTimeString().slice(0, 5) : '',
                      endTime: endTimeValue,
                      inventory: {
                        cocktails: prev.inventory?.cocktails?.length > 0 ? prev.inventory.cocktails : buildInventory('cocktails'),
                        mocktails: prev.inventory?.mocktails?.length > 0 ? prev.inventory.mocktails : buildInventory('mocktails'),
                        beer: prev.inventory?.beer?.length > 0 ? prev.inventory.beer : buildInventory('beer'),
                        wine: prev.inventory?.wine?.length > 0 ? prev.inventory.wine : buildInventory('wine'),
                      },
                    }));
                    console.log('[Event Summary] FINALIZE EVENT - navigating to Event Summary page');
                    setShowSummaryView(false);
                    setShowEventSetup(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 8px',
                    background: '#800080',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: `${Math.max(14, Math.min(18, sw / 20))}px`,
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  FINALIZE EVENT
                </button>
              )}
            </div>
              );
            })()}
          </div>
        </div>
      );
    }

    // Event Setup/Edit page
    if (showEventSetup) {
      const isPostEvent = eventSummary != null || isPostEventEdit; // Post-event if we have event summary data OR we're in post-event edit mode
      const glasswareLost = {
        rox: Math.max(0, (eventSetupData.glasswareSent?.rox || 0) - (eventSetupData.glasswareReturned?.rox || 0)),
        tmbl: Math.max(0, (eventSetupData.glasswareSent?.tmbl || 0) - (eventSetupData.glasswareReturned?.tmbl || 0)),
      };
      const glasswareNetLoss = (glasswareLost.rox + glasswareLost.tmbl) * 3;
      
      const updateInventoryItem = (category, index, field, value) => {
        setEventSetupData(prev => {
          const newInventory = { ...prev.inventory };
          newInventory[category] = [...newInventory[category]];
          newInventory[category][index] = { ...newInventory[category][index], [field]: value };
          return { ...prev, inventory: newInventory };
        });
      };
      
      const addInventoryItem = (category) => {
        setEventSetupData(prev => {
          const newInventory = { ...prev.inventory };
          newInventory[category] = [...newInventory[category], { name: '', sent: 0, returned: 0 }];
          return { ...prev, inventory: newInventory };
        });
      };
      
      const removeInventoryItem = (category, index) => {
        setEventSetupData(prev => {
          const newInventory = { ...prev.inventory };
          newInventory[category] = newInventory[category].filter((_, i) => i !== index);
          return { ...prev, inventory: newInventory };
        });
      };
      
      const handleFinalizeEvent = async () => {
        setSyncing(true);
        try {
          if (isPostEvent) {
            // Post-event: Save setup data and go back to brief summary
            const response = await fetch('/api/catering-events/finalize', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                eventId,
                setupData: eventSetupData,
                summary: eventSummary,
              }),
            });
            if (response.ok) {
              setShowEventSetup(false);
              // Stay on brief summary view
            }
          } else {
            // Pre-event: Start a new event with the setup data
            const newEventName = eventSetupData.eventName || `Event ${new Date().toLocaleDateString()}`;
            setShowEventSetup(false);
            await handleStartEvent(newEventName);
          }
        } catch (err) {
          console.error('Failed to finalize event:', err);
        } finally {
          setSyncing(false);
        }
      };
      
      const renderInventorySection = (title, category) => {
        const items = eventSetupData.inventory[category] || [];
        if (items.length === 0) return null;
        
        return (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#333', marginBottom: '12px', textTransform: 'uppercase' }}>
              {title}
            </h3>
            {items.map((item, idx) => {
              const sent = parseFloat(item.sent) || 0;
              const returned = parseFloat(item.returned) || 0;
              const net = (sent - returned).toFixed(2);
              
              return (
                <div key={idx} style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ flex: '2 1 0', minWidth: 0, padding: '8px 6px', background: '#fff', borderRadius: '6px', fontSize: '13px', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box' }}>
                    {item.name}
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={item.sent === '0' || item.sent === 0 ? '' : item.sent}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^-?\d*\.?\d{0,2}$/.test(val)) {
                        updateInventoryItem(category, idx, 'sent', val);
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', boxSizing: 'border-box' }}
                  />
                  <input
                    type="text"
                    inputMode="decimal"
                    value={item.returned === '0' || item.returned === 0 ? '' : item.returned}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^-?\d*\.?\d{0,2}$/.test(val)) {
                        updateInventoryItem(category, idx, 'returned', val);
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', boxSizing: 'border-box' }}
                  />
                  <div style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', background: '#fff', borderRadius: '6px', fontSize: '13px', textAlign: 'center', fontWeight: 'bold', color: parseFloat(net) > 0 ? '#ef4444' : '#333', boxSizing: 'border-box' }}>
                    {net}
                  </div>
                </div>
              );
            })}
          </div>
        );
      };
      
      return (
        <div 
          className="scrollable-content"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#fff',
            overflowY: 'scroll',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            padding: '16px',
            boxSizing: 'border-box',
            zIndex: 1000,
          }}>
          
          {/* CSS for input focus styling - hide placeholder and show white background */}
          <style>{`
            .scrollable-content input:focus::placeholder { color: transparent; }
            .scrollable-content input:focus { background: #fff !important; outline: 2px solid #800080; outline-offset: -1px; border-color: #800080 !important; }
          `}</style>
          
          {/* Event Save Success Animation Overlay */}
          {showEventSaveSuccess && (
            <>
              <style>{`
                @keyframes event-save-checkmark {
                  0% { transform: scale(0); opacity: 0; }
                  50% { transform: scale(1.2); opacity: 1; }
                  100% { transform: scale(1); opacity: 1; }
                }
                @keyframes event-save-circle {
                  0% { stroke-dashoffset: 166; }
                  100% { stroke-dashoffset: 0; }
                }
                .event-save-animation {
                  animation: event-save-checkmark 0.6s ease-out;
                }
              `}</style>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
              }}>
                <div className="event-save-animation" style={{ textAlign: 'center' }}>
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="54" fill="none" stroke="#ffffff" strokeWidth="4" 
                      strokeDasharray="339.292" strokeDashoffset="0"
                      style={{ animation: 'event-save-circle 0.6s ease-out' }} />
                    <path d="M34 60 L52 78 L86 44" fill="none" stroke="#ffffff" strokeWidth="6" 
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div style={{ 
                  fontSize: '32px', 
                  fontWeight: '700', 
                  color: '#ffffff',
                  textAlign: 'center',
                  marginTop: '20px',
                }}>
                  Event Saved
                </div>
              </div>
            </>
          )}
          
          {/* Title */}
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 16px 0', color: '#333', textAlign: 'center' }}>
            Event Summary
          </h1>
          
          {/* Details Section */}
            <div style={{ background: '#f5f5f5', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>Details</h2>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#666', marginBottom: '4px' }}>Event Name</label>
                <input
                  type="text"
                  value={eventSetupData.eventName}
                  onChange={(e) => setEventSetupData(prev => ({ ...prev, eventName: e.target.value }))}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#666', marginBottom: '4px' }}>Event Date</label>
                  <input
                    type="date"
                    value={eventSetupData.eventDate}
                    onChange={(e) => setEventSetupData(prev => ({ ...prev, eventDate: e.target.value }))}
                    style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#666', marginBottom: '4px' }}>Number of Patrons</label>
                  <input
                    type="number"
                    min="0"
                    value={eventSetupData.numberOfPatrons || ''}
                    onChange={(e) => setEventSetupData(prev => ({ ...prev, numberOfPatrons: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0 }))}
                    style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box', background: '#fff' }}
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#666', marginBottom: '4px' }}>Start Time</label>
                  <input
                    type="time"
                    value={eventSetupData.startTime}
                    onChange={(e) => setEventSetupData(prev => ({ ...prev, startTime: e.target.value }))}
                    style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#666', marginBottom: '4px' }}>End Time</label>
                  <input
                    type="time"
                    value={eventSetupData.endTime}
                    onChange={(e) => setEventSetupData(prev => ({ ...prev, endTime: e.target.value }))}
                    style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              
              {/* Read-only stats from event summary */}
              {isPostEvent && eventSummary && (
                <>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ flex: 1, background: '#fff', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{eventSummary.totalTabs || 0}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Total Tabs</div>
                    </div>
                    <div style={{ flex: 1, background: '#fff', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{eventSummary.totalItems || 0}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Items Sold</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ flex: 1, background: '#fff', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50' }}>${(eventSummary.totalRevenue || 0).toFixed(2)}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Sales</div>
                    </div>
                    <div style={{ flex: 1, background: '#fff', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196F3' }}>${(eventSummary.totalTips || 0).toFixed(2)}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Total Tips</div>
                    </div>
                  </div>
                  <div style={{ background: '#fff', padding: '12px', borderRadius: '8px', textAlign: 'center', marginBottom: '12px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>${spillageTotal.toFixed(2)}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Total Spillage</div>
                  </div>
                </>
              )}
              
            </div>
            
            {/* Operating Expenses Section */}
            <div style={{ background: '#f5f5f5', borderRadius: '12px', padding: '16px', marginBottom: '16px', boxSizing: 'border-box', width: '100%', overflowX: 'hidden' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>Operating Expenses</h2>

              {/* Additional Expenses */}
              {(() => {
                const addlExpenses = eventSetupData.additionalExpenses || [];
                const EXPENSE_OPTIONS = ['Accommodation', 'Transportation', 'Permit', 'Insurance'];
                return (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label style={{ fontSize: '14px', color: '#666' }}>Additional Expenses</label>
                      <button
                        onClick={() => {
                          setEventSetupData(prev => ({
                            ...prev,
                            additionalExpenses: [...(prev.additionalExpenses || []), { label: EXPENSE_OPTIONS[0], amount: '' }],
                          }));
                        }}
                        style={{ padding: '6px 14px', background: '#800080', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}
                      >
                        + Add
                      </button>
                    </div>
                    {addlExpenses.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {addlExpenses.map((exp, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
                            <select
                              value={exp.label}
                              onChange={(e) => {
                                setEventSetupData(prev => {
                                  const updated = [...(prev.additionalExpenses || [])];
                                  updated[idx] = { ...updated[idx], label: e.target.value };
                                  return { ...prev, additionalExpenses: updated };
                                });
                              }}
                              style={{ flex: '1 1 0', minWidth: 0, padding: '8px 6px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', background: '#fff', boxSizing: 'border-box' }}
                            >
                              {EXPENSE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                            <input
                              type="text"
                              inputMode="decimal"
                              placeholder="$0.00"
                              value={exp.amount === '0' || exp.amount === 0 ? '' : (exp.amount ?? '')}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || /^-?\d*\.?\d{0,2}$/.test(val)) {
                                  setEventSetupData(prev => {
                                    const updated = [...(prev.additionalExpenses || [])];
                                    updated[idx] = { ...updated[idx], amount: val };
                                    return { ...prev, additionalExpenses: updated };
                                  });
                                }
                              }}
                              onFocus={(e) => e.target.select()}
                              style={{ flex: '0 0 80px', width: '80px', padding: '8px 6px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', background: '#fff', textAlign: 'right', boxSizing: 'border-box' }}
                            />
                            <button
                              onClick={() => {
                                setEventSetupData(prev => ({
                                  ...prev,
                                  additionalExpenses: (prev.additionalExpenses || []).filter((_, i) => i !== idx),
                                }));
                              }}
                              style={{ flex: '0 0 auto', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '20px', lineHeight: 1, padding: '2px 4px' }}
                            >×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {addlExpenses.length === 0 && (
                      <div style={{ fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '8px 0' }}>
                        No additional expenses added
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Labor Section */}
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#666', marginBottom: '8px' }}>Labor</label>
                
                {/* Column Headers - only show if there are labor entries */}
                {eventSetupData.labor && eventSetupData.labor.length > 0 && (
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', display: 'flex', gap: '6px', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
                    <span style={{ flex: '2 1 0', minWidth: 0, textAlign: 'left' }}>Job</span>
                    <span style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}>$/hr</span>
                    <span style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}>Hrs</span>
                    <span style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}>Pay</span>
                  </div>
                )}
                
                {/* Labor Rows */}
                {eventSetupData.labor && eventSetupData.labor.map((laborer, idx) => {
                  const pay = (parseFloat(laborer.rate) || 0) * (parseFloat(laborer.hours) || 0);
                  return (
                    <div key={idx} style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
                      <select
                        value={laborer.job}
                        onChange={(e) => {
                          if (e.target.value === 'Remove') {
                            setEventSetupData(prev => ({
                              ...prev,
                              labor: prev.labor.filter((_, i) => i !== idx)
                            }));
                          } else {
                            setEventSetupData(prev => {
                              const newLabor = [...prev.labor];
                              newLabor[idx] = { ...newLabor[idx], job: e.target.value };
                              return { ...prev, labor: newLabor };
                            });
                          }
                        }}
                        style={{ flex: '2 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', background: '#fff', boxSizing: 'border-box' }}
                      >
                        <option value="Bartender">Bartender</option>
                        <option value="Barback">Barback</option>
                        <option value="Server">Server</option>
                        <option value="Photographer">Photographer</option>
                        <option value="Other">Other</option>
                        <option value="Remove" style={{ color: '#ef4444' }}>Remove</option>
                      </select>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={laborer.rate === '0' || laborer.rate === 0 ? '' : laborer.rate}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                            setEventSetupData(prev => {
                              const newLabor = [...prev.labor];
                              newLabor[idx] = { ...newLabor[idx], rate: val };
                              return { ...prev, labor: newLabor };
                            });
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        placeholder="$/hr"
                        style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', boxSizing: 'border-box' }}
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        value={laborer.hours === '0' || laborer.hours === 0 ? '' : laborer.hours}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                            setEventSetupData(prev => {
                              const newLabor = [...prev.labor];
                              newLabor[idx] = { ...newLabor[idx], hours: val };
                              return { ...prev, labor: newLabor };
                            });
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        placeholder="hrs"
                        style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', boxSizing: 'border-box' }}
                      />
                      <div style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', background: '#fff', borderRadius: '6px', fontSize: '13px', textAlign: 'center', fontWeight: 'bold', boxSizing: 'border-box' }}>
                        ${pay.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
                
                {/* Total Row with Add Labor Button */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => {
                      // Calculate default hours from event duration
                      let defaultHours = '';
                      if (eventSetupData.startTime && eventSetupData.endTime) {
                        const [startH, startM] = eventSetupData.startTime.split(':').map(Number);
                        const [endH, endM] = eventSetupData.endTime.split(':').map(Number);
                        const startMinutes = startH * 60 + startM;
                        const endMinutes = endH * 60 + endM;
                        const durationMinutes = endMinutes >= startMinutes ? endMinutes - startMinutes : (24 * 60 - startMinutes + endMinutes);
                        defaultHours = (durationMinutes / 60).toFixed(2);
                      }
                      setEventSetupData(prev => ({
                        ...prev,
                        labor: [...(prev.labor || []), { job: 'Bartender', rate: '', hours: defaultHours }]
                      }));
                    }}
                    style={{
                      flex: 4,
                      padding: '8px',
                      background: 'transparent',
                      color: '#666',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    + Add Labor
                  </button>
                  <div style={{ flex: 2 }}></div>
                  <div style={{ flex: 2 }}></div>
                  <div style={{ flex: 2, padding: '8px', fontSize: '14px', textAlign: 'center', fontWeight: 'bold' }}>
                    ${(eventSetupData.labor || []).reduce((sum, l) => sum + (parseFloat(l.rate) || 0) * (parseFloat(l.hours) || 0), 0).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Inventory Section */}
            <div style={{ background: '#f5f5f5', borderRadius: '12px', padding: '16px', marginBottom: '16px', boxSizing: 'border-box', width: '100%', overflowX: 'hidden' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>Inventory</h2>

              {/* Column Headers */}
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px', display: 'flex', gap: '6px', width: '100%', boxSizing: 'border-box' }}>
                <span style={{ flex: '2 1 0', minWidth: 0 }}>Item</span>
                <span style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}>Sent</span>
                <span style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}>Returned</span>
                <span style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}>Net</span>
              </div>
              
              {/* Glassware Rox */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
                <div style={{ flex: '2 1 0', minWidth: 0, padding: '8px 6px', background: '#fff', borderRadius: '6px', fontSize: '13px', color: '#333', boxSizing: 'border-box' }}>
                  Rox
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={eventSetupData.glasswareSent?.rox === '0' || eventSetupData.glasswareSent?.rox === 0 ? '' : (eventSetupData.glasswareSent?.rox ?? '')}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*$/.test(val)) {
                      setEventSetupData(prev => ({ ...prev, glasswareSent: { ...prev.glasswareSent, rox: val } }));
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                  style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', boxSizing: 'border-box' }}
                />
                <input
                  type="text"
                  inputMode="numeric"
                  value={eventSetupData.glasswareReturned?.rox === '0' || eventSetupData.glasswareReturned?.rox === 0 ? '' : (eventSetupData.glasswareReturned?.rox ?? '')}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*$/.test(val)) {
                      setEventSetupData(prev => ({ ...prev, glasswareReturned: { ...prev.glasswareReturned, rox: val } }));
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                  style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', boxSizing: 'border-box' }}
                />
                <div style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', background: '#fff', borderRadius: '6px', fontSize: '13px', textAlign: 'center', fontWeight: 'bold', color: glasswareLost.rox > 0 ? '#ef4444' : '#333', boxSizing: 'border-box' }}>
                  {glasswareLost.rox}
                </div>
              </div>

              {/* Glassware Tmbl */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
                <div style={{ flex: '2 1 0', minWidth: 0, padding: '8px 6px', background: '#fff', borderRadius: '6px', fontSize: '13px', color: '#333', boxSizing: 'border-box' }}>
                  Tmbl
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={eventSetupData.glasswareSent?.tmbl === '0' || eventSetupData.glasswareSent?.tmbl === 0 ? '' : (eventSetupData.glasswareSent?.tmbl ?? '')}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*$/.test(val)) {
                      setEventSetupData(prev => ({ ...prev, glasswareSent: { ...prev.glasswareSent, tmbl: val } }));
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                  style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', boxSizing: 'border-box' }}
                />
                <input
                  type="text"
                  inputMode="numeric"
                  value={eventSetupData.glasswareReturned?.tmbl === '0' || eventSetupData.glasswareReturned?.tmbl === 0 ? '' : (eventSetupData.glasswareReturned?.tmbl ?? '')}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*$/.test(val)) {
                      setEventSetupData(prev => ({ ...prev, glasswareReturned: { ...prev.glasswareReturned, tmbl: val } }));
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                  style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', boxSizing: 'border-box' }}
                />
                <div style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', background: '#fff', borderRadius: '6px', fontSize: '13px', textAlign: 'center', fontWeight: 'bold', color: glasswareLost.tmbl > 0 ? '#ef4444' : '#333', boxSizing: 'border-box' }}>
                  {glasswareLost.tmbl}
                </div>
              </div>

              {/* Ice */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
                <div style={{ flex: '2 1 0', minWidth: 0, padding: '8px 6px', background: '#fff', borderRadius: '6px', fontSize: '13px', color: '#333', boxSizing: 'border-box' }}>
                  Ice (blocks)
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={eventSetupData.iceSent === '0' || eventSetupData.iceSent === 0 ? '' : (eventSetupData.iceSent ?? '')}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*$/.test(val)) {
                      setEventSetupData(prev => ({ ...prev, iceSent: val }));
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                  style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', boxSizing: 'border-box' }}
                />
                <input
                  type="text"
                  inputMode="numeric"
                  value={eventSetupData.iceReturned === '0' || eventSetupData.iceReturned === 0 ? '' : (eventSetupData.iceReturned ?? '')}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*$/.test(val)) {
                      setEventSetupData(prev => ({ ...prev, iceReturned: val }));
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                  style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', boxSizing: 'border-box' }}
                />
                <div style={{ flex: '1 1 0', minWidth: 0, padding: '8px 4px', background: '#fff', borderRadius: '6px', fontSize: '13px', textAlign: 'center', fontWeight: 'bold', color: ((parseFloat(eventSetupData.iceSent) || 0) - (parseFloat(eventSetupData.iceReturned) || 0)) > 0 ? '#ef4444' : '#333', boxSizing: 'border-box' }}>
                  {((parseFloat(eventSetupData.iceSent) || 0) - (parseFloat(eventSetupData.iceReturned) || 0)).toFixed(0)}
                </div>
              </div>
              
              {/* Beverage Inventory */}
              {renderInventorySection('Social Tonics', 'cocktails')}
              {renderInventorySection('CLASSICS', 'mocktails')}
              {renderInventorySection('Beer', 'beer')}
              {renderInventorySection('Wine', 'wine')}
            </div>
            
            {/* Income Statement Section - Always visible */}
            <div style={{ background: '#f5f5f5', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>Income Statement</h2>
              
              {/* Sales - sum of all item prices from paid tabs (excluding spillage), minus tips */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: '#fff', borderRadius: '8px', marginBottom: '8px' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>Sales</span>
                <span style={{ fontWeight: 'bold', color: '#22c55e' }}>${(() => {
                  // Calculate sales from paid tabs (excluding spillage)
                  const paidTabsTotal = tabs
                    .filter(t => !t.isSpillage && (t.status === 'paid' || t.status === 'archived' || t.status === 'closed'))
                    .reduce((sum, tab) => {
                      const tabItemsTotal = (tab.items || []).reduce((itemSum, item) => itemSum + (item.price || item.finalPrice || 0), 0);
                      return sum + tabItemsTotal + (tab.tipAmount || 0);
                    }, 0);
                  // Subtract tips to get pure sales
                  const totalTips = tabs
                    .filter(t => !t.isSpillage)
                    .reduce((sum, tab) => sum + (tab.tipAmount || 0), 0);
                  return (paidTabsTotal - totalTips).toFixed(2);
                })()}</span>
              </div>
              
              {/* Tips */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: '#fff', borderRadius: '8px', marginBottom: '8px' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>Tips</span>
                <span style={{ fontWeight: 'bold', color: '#22c55e' }}>${tabs
                  .filter(t => !t.isSpillage)
                  .reduce((sum, tab) => sum + (tab.tipAmount || 0), 0)
                  .toFixed(2)}</span>
              </div>
              
              {/* COGS Section - calculated from tabs (excluding spillage) */}
              {(() => {
                // Build item counts from non-spillage tabs
                const itemCounts = {};
                tabs.filter(t => !t.isSpillage).forEach(tab => {
                  (tab.items || []).forEach(item => {
                    const key = item.name;
                    if (!itemCounts[key]) {
                      itemCounts[key] = { name: item.name, category: item.category, count: 0 };
                    }
                    itemCounts[key].count += 1;
                  });
                });
                const cogsItems = Object.values(itemCounts);
                let cogsTotal = 0;
                
                return (
                  <div style={{ background: '#fff', borderRadius: '8px', marginBottom: '8px', overflow: 'hidden' }}>
                    <div style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                      <span style={{ fontWeight: 'bold', color: '#333' }}>COGS</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', padding: '8px 12px', display: 'flex', gap: '8px', borderBottom: '1px solid #eee' }}>
                      <span style={{ flex: 2 }}>Item</span>
                      <span style={{ flex: 1, textAlign: 'center' }}># Sold</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>Cost</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>Total</span>
                    </div>
                    {cogsItems.length > 0 ? cogsItems.map(item => {
                      const menuItem = allItems.find(i => i.name === item.name);
                      const costPerUnit = menuItem?.costPerUnit || 0;
                      const isWine = menuItem?.category === 'wine' || menuItem?.section === 'wine';
                      const totalCost = item.count * costPerUnit;
                      cogsTotal += totalCost;
                      return (
                        <div key={item.name} style={{ display: 'flex', gap: '8px', padding: '8px 12px', borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
                          <div style={{ flex: 2, fontSize: '14px', color: '#333' }}>{item.name}</div>
                          <div style={{ flex: 1, fontSize: '14px', textAlign: 'center' }}>{item.count}</div>
                          <div style={{ flex: 1, fontSize: '14px', textAlign: 'center', color: '#666' }} title={isWine ? '$ / Glass' : '$ / Unit'}>${costPerUnit.toFixed(2)}</div>
                          <div style={{ flex: 1, fontSize: '14px', textAlign: 'center', fontWeight: 'bold', color: '#ef4444' }}>-${totalCost.toFixed(2)}</div>
                        </div>
                      );
                    }) : (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                        No items sold yet
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: '#f9f9f9' }}>
                      <span style={{ flex: 1 }}></span>
                      <span style={{ fontWeight: 'bold', color: '#ef4444' }}>
                        -${cogsTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })()}
              
              {/* Operating Expenses */}
              <div style={{ background: '#fff', borderRadius: '8px', marginBottom: '8px', overflow: 'hidden' }}>
                <div style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                  <span style={{ fontWeight: 'bold', color: '#333' }}>Expenses</span>
                </div>
                {/* Additional Expenses (single aggregated line) */}
                {(() => {
                  const addlTotal = (eventSetupData.additionalExpenses || []).reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
                  return addlTotal > 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ color: '#333' }}>Add. Expenses</span>
                      <span style={{ color: '#ef4444' }}>-${addlTotal.toFixed(2)}</span>
                    </div>
                  ) : null;
                })()}
                {/* Spillage - using shared spillageTotal for consistency */}
                {spillageTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ color: '#333' }}>Spillage</span>
                    <span style={{ color: '#ef4444' }}>-${spillageTotal.toFixed(2)}</span>
                  </div>
                )}
                {/* Sales Tax - 8% of non-spillage paid tabs (not spillage) */}
                {(() => {
                  const paidTabs = tabs.filter(t => !t.isSpillage && (t.status === 'paid' || t.status === 'archived'));
                  const totalSales = paidTabs.reduce((sum, tab) => {
                    return sum + (tab.items || []).reduce((itemSum, item) => itemSum + (parseFloat(item.price) || 0), 0);
                  }, 0);
                  const salesTax = totalSales * 0.08;
                  return salesTax > 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ color: '#333' }}>Sales Tax (8%)</span>
                      <span style={{ color: '#ef4444' }}>-${salesTax.toFixed(2)}</span>
                    </div>
                  ) : null;
                })()}
                {/* Labor costs */}
                {eventSetupData.labor && eventSetupData.labor.length > 0 && (
                  <>
                    <div style={{ fontSize: '12px', color: '#666', padding: '8px 12px', display: 'flex', gap: '8px', borderBottom: '1px solid #eee', background: '#fafafa' }}>
                      <span style={{ flex: 2 }}>Title</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>Rate</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>Hours</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>Pay</span>
                    </div>
                    {eventSetupData.labor.map((laborer, idx) => {
                      const pay = (parseFloat(laborer.rate) || 0) * (parseFloat(laborer.hours) || 0);
                      return (
                        <div key={idx} style={{ display: 'flex', gap: '8px', padding: '8px 12px', borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
                          <div style={{ flex: 2, fontSize: '14px', color: '#333' }}>{laborer.job}</div>
                          <div style={{ flex: 1, fontSize: '14px', textAlign: 'center' }}>${parseFloat(laborer.rate || 0).toFixed(2)}</div>
                          <div style={{ flex: 1, fontSize: '14px', textAlign: 'center' }}>{parseFloat(laborer.hours || 0).toFixed(2)}</div>
                          <div style={{ flex: 1, fontSize: '14px', textAlign: 'center', fontWeight: 'bold', color: '#ef4444' }}>-${pay.toFixed(2)}</div>
                        </div>
                      );
                    })}
                  </>
                )}
                {/* Show empty state if no expenses */}
                {!((eventSetupData.additionalExpenses || []).some(e => parseFloat(e.amount) > 0)) &&
                 !(eventSetupData.labor && eventSetupData.labor.length > 0) && (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                    No expenses entered yet
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: '#f9f9f9' }}>
                  <span style={{ flex: 1 }}></span>
                  <span style={{ fontWeight: 'bold', color: '#ef4444' }}>
                    -${(() => {
                      const paidTabs = tabs.filter(t => !t.isSpillage && (t.status === 'paid' || t.status === 'archived'));
                      const totalSales = paidTabs.reduce((sum, tab) => {
                        return sum + (tab.items || []).reduce((itemSum, item) => itemSum + (parseFloat(item.price) || 0), 0);
                      }, 0);
                      const salesTax = totalSales * 0.08;
                      return (
                        (eventSetupData.additionalExpenses || []).reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0) +
                        (eventSetupData.labor || []).reduce((sum, l) => sum + (parseFloat(l.rate) || 0) * (parseFloat(l.hours) || 0), 0) +
                        spillageTotal +
                        salesTax
                      ).toFixed(2);
                    })()}
                  </span>
                </div>
              </div>
              
              {/* Net Income */}
              {(() => {
                // Calculate sales from paid tabs (excluding spillage)
                const paidTabsTotal = tabs
                  .filter(t => !t.isSpillage && (t.status === 'paid' || t.status === 'archived' || t.status === 'closed'))
                  .reduce((sum, tab) => {
                    const tabItemsTotal = (tab.items || []).reduce((itemSum, item) => itemSum + (item.price || item.finalPrice || 0), 0);
                    return sum + tabItemsTotal + (tab.tipAmount || 0);
                  }, 0);
                const totalTips = tabs
                  .filter(t => !t.isSpillage)
                  .reduce((sum, tab) => sum + (tab.tipAmount || 0), 0);
                const sales = paidTabsTotal - totalTips;
                const tips = totalTips;
                
                // Use shared spillageTotal for consistency
                const operatingExpenses =
                  (eventSetupData.additionalExpenses || []).reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0) +
                  (eventSetupData.labor || []).reduce((sum, l) => sum + (parseFloat(l.rate) || 0) * (parseFloat(l.hours) || 0), 0) +
                  spillageTotal;
                
                // COGS calculation from tabs (excluding spillage)
                let cogs = 0;
                tabs.filter(t => !t.isSpillage).forEach(tab => {
                  (tab.items || []).forEach(item => {
                    const menuItem = allItems.find(i => i.name === item.name);
                    const costPerUnit = menuItem?.costPerUnit || 0;
                    cogs += costPerUnit;
                  });
                });
                
                const netIncome = sales + tips - cogs - operatingExpenses;
                return (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', background: netIncome >= 0 ? '#dcfce7' : '#fee2e2', borderRadius: '8px', marginTop: '8px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#333' }}>Net Income</span>
                    <span style={{ fontWeight: 'bold', fontSize: '18px', color: netIncome >= 0 ? '#22c55e' : '#ef4444' }}>
                      {netIncome >= 0 ? '' : '-'}${Math.abs(netIncome).toFixed(2)}
                    </span>
                  </div>
                );
              })()}
            </div>
            
            {/* Action Buttons */}
            {isPostEvent ? (
              // Post-event: Show EDIT EVENT and SAVE RESULTS buttons
              <div style={{ display: 'flex', gap: '12px', marginBottom: '32px' }}>
                <button
                  onClick={() => {
                    console.log('[Event Summary] EDIT EVENT - returning to POS view');
                    // Keep paid/archived tabs as archived (green and uneditable)
                    // Only return to POS view without changing tab statuses
                    setShowEventSetup(false);
                    setShowSummaryView(false);
                    setIsPostEventEdit(true); // Mark that we're in post-event edit mode
                    // This will return to the POS view with the active event
                  }}
                  disabled={syncing}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: syncing ? '#ccc' : '#666',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: syncing ? 'not-allowed' : 'pointer',
                  }}
                >
                  EDIT EVENT
                </button>
                <button
                  onClick={async () => {
                    console.log('[Event Summary] SAVE RESULTS - saving to database');
                    console.log('[Event Summary] eventId:', eventId);
                    console.log('[Event Summary] eventSetupData:', eventSetupData);
                    console.log('[Event Summary] eventSummary:', eventSummary);
                    console.log('[Event Summary] tabs with tips:', tabs.map(t => ({ id: t.id, name: t.name, tipAmount: t.tipAmount })));
                    
                    // Calculate total tips from tabs (in case eventSummary doesn't have it)
                    const totalTipsFromTabs = tabs.reduce((sum, t) => sum + (t.tipAmount || 0), 0);
                    
                    // Calculate spillage data from spillage tab using costPerUnit or item price as fallback
                    const spillageTab = tabs.find(t => t.isSpillage);
                    const spillageItems = [];
                    let spillageTotal = 0;
                    if (spillageTab?.items) {
                      // Group items by name and count, track price for fallback
                      const itemCounts = {};
                      spillageTab.items.forEach(item => {
                        if (!itemCounts[item.name]) {
                          itemCounts[item.name] = { 
                            name: item.name, 
                            category: item.category, 
                            quantity: 0,
                            itemPrice: item.basePrice || item.price || 0 // Store price for fallback
                          };
                        }
                        itemCounts[item.name].quantity += 1;
                      });
                      // Calculate cost for each item
                      Object.values(itemCounts).forEach(item => {
                        const menuItem = allItems.find(i => i.name === item.name);
                        const costPerUnit = menuItem?.costPerUnit || item.itemPrice || 0;
                        const totalCost = item.quantity * costPerUnit;
                        spillageItems.push({ name: item.name, category: item.category, quantity: item.quantity, costPerUnit, totalCost });
                        spillageTotal += totalCost;
                      });
                    }
                    
                    // Calculate COGS from all non-spillage tabs (same as Event Summary display)
                    let cogsTotal = 0;
                    tabs.filter(t => !t.isSpillage).forEach(tab => {
                      (tab.items || []).forEach(item => {
                        const menuItem = allItems.find(i => i.name === item.name);
                        const costPerUnit = menuItem?.costPerUnit || 0;
                        cogsTotal += costPerUnit;
                      });
                    });
                    
                    const sales = eventSummary?.totalRevenue || 0;
                    const tips = eventSummary?.totalTips || totalTipsFromTabs;
                    
                    const summaryWithTips = {
                      ...eventSummary,
                      totalTips: tips
                    };
                    
                    // Build itemData from all paid/archived tabs (excluding spillage)
                    // Format: "itemName, category, timeStr, transactionType, cost"
                    const itemDataParts = [];
                    tabs.filter(t => !t.isSpillage && (t.status === 'paid' || t.status === 'archived')).forEach(tab => {
                      const txnType = (tab.paymentMethod || 'credit').toUpperCase();
                      (tab.items || []).forEach(item => {
                        const itemName = (item.name || 'Unknown').replace(/,/g, ' ').trim();
                        const category = (item.category || 'other').toLowerCase();
                        const timestamp = item.addedAt || item.timestamp || tab.paidAt || new Date().toISOString();
                        const timeStr = new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                        const cost = (parseFloat(item.price) || 0).toFixed(2);
                        itemDataParts.push(`${itemName}, ${category}, ${timeStr}, ${txnType}, ${cost}`);
                      });
                    });
                    const finalItemData = itemDataParts.join('\n');
                    
                    // Update eventSetupData with final itemData
                    const finalSetupData = { ...eventSetupData, itemData: finalItemData };
                    
                    setSyncing(true);
                    try {
                      const response = await fetch('/api/catering-events/finalize', {
                        method: 'POST',
                        headers: { 
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          eventId,
                          setupData: finalSetupData,
                          summary: summaryWithTips,
                          tabs: tabs,
                          spillageData: { items: spillageItems, total: spillageTotal },
                          cogsData: { total: cogsTotal },
                        }),
                      });
                      const data = await response.json();
                      if (response.ok) {
                        // Show success animation
                        setShowEventSaveSuccess(true);
                        // After 2 seconds, return to home/event list
                        setTimeout(() => {
                          setShowEventSaveSuccess(false);
                          setShowEventSetup(false);
                          setShowSummaryView(false);
                          setEventSummary(null);
                          clearEvent(); // Clear event from localStorage
                        }, 2000);
                      } else {
                        console.error('[Event Summary] Save failed:', data);
                        alert(`Failed to save event: ${data.message || 'Unknown error'}`);
                      }
                    } catch (err) {
                      console.error('Failed to save event:', err);
                      alert(`Failed to save event: ${err.message}`);
                    } finally {
                      setSyncing(false);
                    }
                  }}
                  disabled={syncing}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: syncing ? '#ccc' : '#800080',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: syncing ? 'not-allowed' : 'pointer',
                  }}
                >
                  {syncing ? 'SAVING...' : 'SAVE RESULTS'}
                </button>
              </div>
            ) : (
              // Pre-event: Show START EVENT button
              <button
                onClick={handleFinalizeEvent}
                disabled={syncing}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: syncing ? '#ccc' : '#800080',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: syncing ? 'not-allowed' : 'pointer',
                  marginBottom: '32px',
                }}
              >
                {syncing ? 'STARTING...' : 'START EVENT'}
              </button>
            )}
        </div>
      );
    }

    // If no active event, show start event screen
    if (!eventId) {
      // Use prop dimensions when available, otherwise use viewport
      const startEventHeight = (isStandalone && propOuterHeight) ? `${propOuterHeight}px` : '100vh';
      
      return (
        <div style={{
          width: '100%',
          height: startEventHeight,
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header with logo - matching POS UI */}
          <div style={{
            background: '#fff',
            padding: '8px 16px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            {logoUrl && (
              <img 
                src={logoUrl} 
                alt="Echo" 
                style={{ 
                  height: '32px', 
                  width: 'auto',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
          </div>
          
          {/* Start event form - centered */}
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}>
            <div style={{
              maxWidth: '400px',
              width: '100%',
            }}>
              <h1 style={{ color: '#333', fontSize: '24px', marginBottom: '24px', textAlign: 'center' }}>
                Start New Event
              </h1>
              <input
                type="text"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                placeholder="Event Name (optional)"
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#333',
                  marginBottom: '20px',
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={() => handleStartEvent(newEventName || `Event ${new Date().toLocaleDateString()}`)}
                disabled={syncing}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: syncing ? '#ccc' : '#800080',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: syncing ? 'not-allowed' : 'pointer',
                  marginBottom: '12px',
                }}
              >
                {syncing ? 'STARTING...' : 'START EVENT'}
              </button>
              
              {/* Event Setup Button */}
              <button
                onClick={() => {
                  // Build inventory from menu items
                  const buildInventory = (cat) => allItems
                    .filter(item => normalizeCategoryKey(item.category) === cat)
                    .map(item => ({ name: item.name, sent: '', returned: '' }));
                  
                  // Pre-populate with event name from input
                  setEventSetupData(prev => ({
                    ...prev,
                    eventName: newEventName || '',
                    eventDate: new Date().toISOString().split('T')[0],
                    inventory: {
                      cocktails: prev.inventory?.cocktails?.length > 0 ? prev.inventory.cocktails : buildInventory('cocktails'),
                      mocktails: prev.inventory?.mocktails?.length > 0 ? prev.inventory.mocktails : buildInventory('mocktails'),
                      beer: prev.inventory?.beer?.length > 0 ? prev.inventory.beer : buildInventory('beer'),
                      wine: prev.inventory?.wine?.length > 0 ? prev.inventory.wine : buildInventory('wine'),
                    },
                  }));
                  setShowEventSetup(true);
                }}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: '#fff',
                  color: '#800080',
                  border: '2px solid #800080',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                EVENT SETUP
              </button>
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button
                  onClick={() => setTestMode(prev => !prev)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    fontSize: '11px',
                    fontWeight: '600',
                    letterSpacing: '0.08em',
                    color: testMode ? '#22c55e' : '#888',
                    fontFamily: 'Montserrat, sans-serif',
                    textTransform: 'uppercase',
                  }}
                >
                  TEST
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Active event: show POS UI with event header
    // Header height for positioning calculations - exported to POSContent via prop
    const eventHeaderHeight = 50;
    
    // Use prop dimensions when available, otherwise use viewport
    const containerWidth = (isStandalone && propOuterWidth) ? `${propOuterWidth}px` : '100%';
    const containerHeight = (isStandalone && propOuterHeight) ? `${propOuterHeight}px` : '100vh';

    // Calculate footer and handle bar heights to position the sidebar just above the footer
    const calcItemButtonSize = (effectiveWidth - 16) / 3;
    const calcFooterHeight = calcItemButtonSize / 2;
    const calcHandleBarHeight = 36;
    const sidebarBottomOffset = calcFooterHeight + calcHandleBarHeight;
    
    return (
      <div
        style={{
          position: 'relative',
          width: containerWidth,
          height: containerHeight,
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Event Header Bar - tap to reveal END EVENT button */}
        <div 
          onClick={handleHeaderTap}
          style={{
            background: '#fff',
            color: '#333',
            padding: '8px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            height: `${eventHeaderHeight}px`,
            boxSizing: 'border-box',
            borderBottom: '1px solid #e0e0e0',
            cursor: 'pointer',
            position: 'relative',
          }}
        >
          {/* Left side: Logo - hidden when buttons are revealed */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexShrink: 0,
          }}>
            {logoUrl && (
              <img 
                src={logoUrl} 
                alt="Echo" 
                style={{ 
                  height: '32px', 
                  width: 'auto',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
          </div>
          
          {/* Center: Empty - Reader and TEST buttons moved to header tap reveal */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          </div>
          
          {/* Right side: Event name/time - always positioned at right edge */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-end',
            height: '100%',
            flex: 1,
          }}>
            {/* Event name and time OR Payment Processing indicator */}
            <div style={{ 
              fontSize: '14px',
              textAlign: 'right',
            }}>
              {checkoutMode ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                  <span style={{ fontWeight: 'bold', color: checkoutStage === 'failed' ? '#ef4444' : checkoutStage === 'receipt_canceled' ? '#f59e0b' : (checkoutStage === 'success' || checkoutStage === 'receipt_sent') ? '#22c55e' : '#800080' }}>
                    {checkoutStage === 'tip' && 'ADDING TIP'}
                    {checkoutStage === 'tab' && 'VIEWING RECEIPT'}
                    {checkoutStage === 'payment' && 'TAKING PAYMENT'}
                    {checkoutStage === 'processing' && 'PAYMENT PROCESSING'}
                    {checkoutStage === 'success' && 'PAYMENT COMPLETE!'}
                    {checkoutStage === 'receipt_request' && 'REQUESTING RECEIPT'}
                    {checkoutStage === 'receipt_sending' && 'SENDING RECEIPT'}
                    {checkoutStage === 'receipt_sent' && 'RECEIPT SENT!'}
                    {checkoutStage === 'receipt_canceled' && 'REQUEST CANCELED'}
                    {checkoutStage === 'failed' && 'PAYMENT FAILED'}
                    {!checkoutStage && 'TAKING PAYMENT'}
                  </span>
                  {checkoutStage !== 'success' && checkoutStage !== 'failed' && checkoutStage !== 'receipt_sent' && checkoutStage !== 'receipt_canceled' && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #e0e0e0',
                      borderTopColor: '#800080',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }} />
                  )}
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#333' }}>{eventName}</span>
                    {/* Reader connection status dot */}
                    <div style={{
                      width: '10px',
                      height: '10px',
                      minWidth: '10px',
                      minHeight: '10px',
                      borderRadius: '50%',
                      background: readerConnected ? '#22c55e' : '#ef4444',
                      boxShadow: readerConnected ? '0 0 4px #22c55e' : '0 0 4px #ef4444',
                      flexShrink: 0,
                    }} />
                  </div>
                  {eventStarted && (
                    <span style={{ color: '#666', fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {new Date(eventStarted).toLocaleDateString([], { month: 'short', day: 'numeric' })} • {new Date(eventStarted).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          
        </div>

        {/* Sidebar backdrop - tap anywhere outside sidebar to close */}
        {showEndEventButton && (
          <div
            onClick={() => {
              if (endEventTimeoutRef.current) clearTimeout(endEventTimeoutRef.current);
              setShowEndEventButton(false);
            }}
            style={{
              position: 'absolute',
              top: `${eventHeaderHeight}px`,
              left: 0,
              right: 0,
              bottom: `${sidebarBottomOffset}px`,
              width: '100%',
              background: 'rgba(0,0,0,0.25)',
              zIndex: 110,
            }}
          />
        )}

        {/* Right slide-in sidebar menu */}
        <div
          style={{
            position: 'absolute',
            top: `${eventHeaderHeight}px`,
            right: 0,
            bottom: `${sidebarBottomOffset}px`,
            width: '50%',
            background: '#fff',
            zIndex: 111,
            borderLeft: '1px solid #e0e0e0',
            boxShadow: '-4px 0 16px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
            padding: '20px 16px',
            gap: '10px',
            boxSizing: 'border-box',
            transform: showEndEventButton ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.25s ease',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* End Event / Summary */}
          {!checkoutMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (forceQuitLongPressRef.current) {
                  clearTimeout(forceQuitLongPressRef.current);
                  forceQuitLongPressRef.current = null;
                }
                if (endEventTimeoutRef.current) clearTimeout(endEventTimeoutRef.current);
                setShowEndEventButton(false);
                if (isPostEventEdit) {
                  setShowEventSetup(true);
                  setShowSummaryView(false);
                } else {
                  setShowEndEventModal(true);
                }
              }}
              onTouchStart={(e) => {
                forceQuitLongPressRef.current = setTimeout(() => {
                  e.preventDefault();
                  setShowEndEventButton(false);
                  setShowForceQuitConfirm(true);
                }, 1500);
              }}
              onTouchEnd={() => {
                if (forceQuitLongPressRef.current) {
                  clearTimeout(forceQuitLongPressRef.current);
                  forceQuitLongPressRef.current = null;
                }
              }}
              onTouchCancel={() => {
                if (forceQuitLongPressRef.current) {
                  clearTimeout(forceQuitLongPressRef.current);
                  forceQuitLongPressRef.current = null;
                }
              }}
              onMouseDown={() => {
                forceQuitLongPressRef.current = setTimeout(() => {
                  setShowEndEventButton(false);
                  setShowForceQuitConfirm(true);
                }, 1500);
              }}
              onMouseUp={() => {
                if (forceQuitLongPressRef.current) {
                  clearTimeout(forceQuitLongPressRef.current);
                  forceQuitLongPressRef.current = null;
                }
              }}
              onMouseLeave={() => {
                if (forceQuitLongPressRef.current) {
                  clearTimeout(forceQuitLongPressRef.current);
                  forceQuitLongPressRef.current = null;
                }
              }}
              style={{
                width: '100%',
                padding: '14px',
                background: isPostEventEdit ? '#3b82f6' : '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 'bold',
                letterSpacing: '0.05em',
                cursor: 'pointer',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              {isPostEventEdit ? 'SUMMARY' : 'END EVENT'}
            </button>
          )}

          {/* Cancel Payment - only shown during active checkout */}
          {checkoutMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (endEventTimeoutRef.current) clearTimeout(endEventTimeoutRef.current);
                setShowEndEventButton(false);
                setCheckoutMode(false);
                setCheckoutItems([]);
                setCheckoutSubtotal(0);
                setCheckoutTabInfo(null);
                setShowScanCard(false);
                setShowTabView(false);
                setShowCustomTip(false);
                setSelectedTipAmount(0);
                setCheckoutStage('');
                setPaymentStatus(null);
                setPaymentStatusMessage(null);
                sendCheckoutCancel();
              }}
              style={{
                width: '100%',
                padding: '14px',
                background: '#f59e0b',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 'bold',
                letterSpacing: '0.05em',
                cursor: 'pointer',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              CANCEL PAYMENT
            </button>
          )}

          {/* Reader */}
          {window.stripeBridge && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.stripeBridge.openReaderSetup) {
                  window.stripeBridge.openReaderSetup();
                } else {
                  setShowReaderSetup(true);
                }
              }}
              style={{
                width: '100%',
                padding: '14px',
                background: '#555',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 'bold',
                letterSpacing: '0.05em',
                cursor: 'pointer',
                fontFamily: 'Montserrat, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxSizing: 'border-box',
              }}
            >
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: readerConnected ? '#4ade80' : '#f87171',
                flexShrink: 0,
              }} />
              READER
            </button>
          )}

          {/* +15 MIN button - only shown in test mode */}
          {testMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setTestTimeOffsetMins(prev => prev + 15);
              }}
              style={{
                width: '100%',
                padding: '14px',
                background: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 'bold',
                letterSpacing: '0.05em',
                cursor: 'pointer',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              +15 MIN{testTimeOffsetMins > 0 ? ` (+${testTimeOffsetMins})` : ''}
            </button>
          )}
        </div>

        {/* POS Content */}
        <div 
          ref={frameRef}
          style={{ 
            flex: 1, 
            overflow: 'hidden',
            width: '100%',
            background: '#fff',
          }}
        >
          {frameReady ? (
            <POSContent
              outerWidth={effectiveWidth}
              outerHeight={effectiveHeight}
              items={items}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              onItemClick={handleItemClick}
              loading={loading}
              total={total}
              categoryCounts={categoryCounts}
              selectedItems={selectedItems}
              lastAction={lastAction}
              onRemoveItem={handleRemoveItem}
              tabs={tabs}
              activeTabId={activeTabId}
              onCreateTab={handleCreateTab}
              onSelectTab={handleSelectTab}
              onDeleteTab={handleDeleteTab}
              onArchiveTab={handleArchiveTab}
              onInvoiceTab={handleInvoiceTab}
              onReopenTab={handleReopenTab}
              onUpdateTabName={handleUpdateTabName}
              onUpdateItemModifiers={handleUpdateItemModifiers}
              onMoveItems={handleMoveItems}
              onDuplicateItems={handleDuplicateItems}
              onCheckout={handleCheckout}
              checkoutLoading={checkoutLoading}
              showSpendLimitWarning={showSpendLimitWarning}
              setShowSpendLimitWarning={setShowSpendLimitWarning}
              spendLimitWarningTab={spendLimitWarningTab}
              setSpendLimitWarningTab={setSpendLimitWarningTab}
              isSidebarOpen={showEndEventButton}
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666',
              gap: '20px',
            }}>
              <div>Loading POS...</div>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '12px 24px',
                  background: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Refresh
              </button>
            </div>
          )}
        </div>

        {/* End Event Confirmation Modal */}
        {showEndEventModal && (
          (() => {
            // Check for open tabs (excluding spillage tab)
            const openTabs = tabs.filter(t => !t.isSpillage && t.status !== 'archived');
            const hasOpenTabs = openTabs.length > 0;
            
            return (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}>
                <div style={{
                  background: '#2a2a2a',
                  borderRadius: '16px',
                  padding: '30px',
                  maxWidth: '400px',
                  width: '90%',
                  textAlign: 'center',
                }}>
                  {hasOpenTabs ? (
                    <>
                      <h2 style={{ color: '#ef4444', fontSize: '20px', marginBottom: '16px' }}>
                        Cannot End Event
                      </h2>
                      <p style={{ color: '#888', marginBottom: '16px' }}>
                        Please close or pay all tabs before ending the event.
                      </p>
                      <p style={{ color: '#ef4444', marginBottom: '24px', fontWeight: 'bold' }}>
                        {openTabs.length} open tab{openTabs.length > 1 ? 's' : ''}: {openTabs.map(t => (t.customName || t.name).toUpperCase()).join(', ')}
                      </p>
                      <button
                        onClick={() => setShowEndEventModal(false)}
                        style={{
                          width: '100%',
                          padding: '14px',
                          background: '#444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '16px',
                          cursor: 'pointer',
                        }}
                      >
                        OK
                      </button>
                    </>
                  ) : (
                    <>
                      <h2 style={{ color: '#fff', fontSize: '20px', marginBottom: '16px' }}>
                        End Event?
                      </h2>
                      <p style={{ color: '#888', marginBottom: '24px' }}>
                        This will sync all tabs to the database and show the event summary.
                        You have {tabs.filter(t => !t.isSpillage).length} tab(s) with {selectedItems.length} total item(s).
                      </p>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          onClick={() => setShowEndEventModal(false)}
                          style={{
                            flex: 1,
                            padding: '14px',
                            background: '#444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            cursor: 'pointer',
                          }}
                        >
                          CANCEL
                        </button>
                        <button
                          onClick={handleEndEvent}
                          disabled={syncing}
                          style={{
                            flex: 1,
                            padding: '14px',
                            background: syncing ? '#555' : '#c62828',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            cursor: syncing ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {syncing ? 'SYNCING...' : 'END EVENT'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()
        )}

        {/* Force Quit Confirmation Modal */}
        {showForceQuitConfirm && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: '#1a1a1a',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '90%',
              width: '320px',
              textAlign: 'center',
              border: '2px solid #ef4444',
            }}>
              <h2 style={{ color: '#ef4444', fontSize: '20px', marginBottom: '16px' }}>
                ⚠️ FORCE QUIT
              </h2>
              <p style={{ color: '#fff', marginBottom: '16px', fontWeight: 'bold' }}>
                Are you sure you want to force quit?
              </p>
              <p style={{ color: '#f59e0b', marginBottom: '24px', fontSize: '14px' }}>
                Data loss may occur. All open tabs will be force-closed.
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowForceQuitConfirm(false)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: '#444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    cursor: 'pointer',
                  }}
                >
                  CANCEL
                </button>
                <button
                  onClick={handleForceQuit}
                  disabled={syncing}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: syncing ? '#555' : '#ef4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: syncing ? 'not-allowed' : 'pointer',
                  }}
                >
                  {syncing ? 'FORCING...' : 'FORCE QUIT'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Checkout Options Popup (Credit/Cash/Invoice) */}
        {showCheckoutOptions && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: '#1a1a1a',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '90%',
              width: '320px',
              textAlign: 'center',
            }}>
              <h2 style={{ color: '#fff', fontSize: '20px', marginBottom: '8px' }}>
                Payment Method
              </h2>
              <p style={{ color: '#888', marginBottom: '24px', fontSize: '16px' }}>
                Total: ${(checkoutSubtotal * 1.08).toFixed(2)}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={handleCreditCheckout}
                  style={{
                    padding: '16px',
                    background: '#4caf50',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  CREDIT
                </button>
                <button
                  onClick={handleCashCheckout}
                  style={{
                    padding: '16px',
                    background: '#2196f3',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  CASH
                </button>
                <button
                  onClick={handleInvoiceCheckout}
                  style={{
                    padding: '16px',
                    background: '#f59e0b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  INVOICE
                </button>
                <button
                  onClick={() => {
                    setShowCheckoutOptions(false);
                    setCheckoutItems([]);
                    setCheckoutSubtotal(0);
                    setCheckoutTabInfo(null);
                  }}
                  style={{
                    padding: '14px',
                    background: '#444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    marginTop: '8px',
                  }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reader Disconnected Warning Modal */}
        {showReaderDisconnectedWarning && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
          }}>
            <div style={{
              background: '#1a1a1a',
              borderRadius: '16px',
              padding: '32px 24px',
              maxWidth: '90%',
              width: '320px',
              textAlign: 'center',
            }}>
              {/* Warning Icon */}
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px auto',
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </div>
              <h2 style={{ color: '#ef4444', fontSize: '22px', marginBottom: '12px', fontWeight: 'bold' }}>
                Card Reader Disconnected
              </h2>
              <p style={{ color: '#999', marginBottom: '24px', fontSize: '15px', lineHeight: '1.5' }}>
                Please connect a card reader to process credit card payments.
              </p>
              <button
                onClick={() => setShowReaderDisconnectedWarning(false)}
                style={{
                  padding: '14px 32px',
                  background: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* Cash Payment Number Pad (Vertical View) */}
        {checkoutMode && paymentMethod === 'cash' && checkoutStage === 'cash' && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.95)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            overflowY: 'auto',
            zIndex: 1000,
            padding: '16px 10px',
          }}>
            <div style={{
              background: '#1a1a1a',
              borderRadius: '16px',
              padding: '16px',
              width: '100%',
              maxWidth: '350px',
              textAlign: 'center',
              margin: 'auto 0',
              boxSizing: 'border-box',
            }}>
              <h2 style={{ color: '#fff', fontSize: '16px', marginBottom: '10px' }}>
                RECEIVING PAYMENT
              </h2>
              <div style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>
                Total Due (incl. 8% tax)
              </div>
              <div style={{ color: '#4caf50', fontSize: '28px', fontWeight: 'bold', marginBottom: '12px' }}>
                ${(checkoutSubtotal * 1.08).toFixed(2)}
              </div>
              
              <div style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>
                Cash Tendered
              </div>
              <div style={{ 
                background: '#333', 
                borderRadius: '8px', 
                padding: '8px', 
                marginBottom: '10px',
                fontSize: '22px',
                fontWeight: 'bold',
                color: '#fff',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                ${cashTendered || '0.00'}
              </div>
              
              {/* Change calculation - always visible */}
              <div style={{ 
                background: parseFloat(cashTendered) >= (checkoutSubtotal * 1.08) && parseFloat(cashTendered) > 0 ? '#1b5e20' : '#333', 
                borderRadius: '8px', 
                padding: '8px', 
                marginBottom: '10px',
              }}>
                <div style={{ color: parseFloat(cashTendered) >= (checkoutSubtotal * 1.08) && parseFloat(cashTendered) > 0 ? '#a5d6a7' : '#888', fontSize: '12px' }}>Change Due</div>
                <div style={{ color: parseFloat(cashTendered) >= (checkoutSubtotal * 1.08) && parseFloat(cashTendered) > 0 ? '#4caf50' : '#fff', fontSize: '22px', fontWeight: 'bold' }}>
                  ${parseFloat(cashTendered) > 0 ? Math.max(0, parseFloat(cashTendered) - (checkoutSubtotal * 1.08)).toFixed(2) : '0.00'}
                </div>
              </div>
              
              {/* Number pad */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '6px',
                marginBottom: '10px',
              }}>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '⌫'].map((key) => (
                  <button
                    key={key}
                    onClick={() => {
                      if (key === '⌫') {
                        setCashTendered(prev => prev.slice(0, -1));
                      } else if (key === '.') {
                        if (!cashTendered.includes('.')) {
                          setCashTendered(prev => prev + '.');
                        }
                      } else {
                        setCashTendered(prev => {
                          const newVal = prev + key;
                          // Limit to 2 decimal places
                          if (newVal.includes('.') && newVal.split('.')[1]?.length > 2) {
                            return prev;
                          }
                          return newVal;
                        });
                      }
                    }}
                    style={{
                      padding: '10px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      background: key === '⌫' ? '#ef4444' : '#444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    {key}
                  </button>
                ))}
              </div>
              
              {/* Quick amount buttons */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                {[20, 50, 100].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setCashTendered(amount.toFixed(2))}
                    style={{
                      flex: 1,
                      padding: '8px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      background: '#333',
                      color: '#fff',
                      border: '1px solid #555',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    ${amount}
                  </button>
                ))}
                <button
                  onClick={() => setCashTendered((checkoutSubtotal * 1.08).toFixed(2))}
                  style={{
                    flex: 1,
                    padding: '8px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    background: '#333',
                    color: '#4caf50',
                    border: '1px solid #4caf50',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  EXACT
                </button>
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    setCheckoutMode(false);
                    setCheckoutItems([]);
                    setCheckoutSubtotal(0);
                    setCheckoutTabInfo(null);
                    setCashTendered('');
                    setCheckoutStage('');
                    setPaymentMethod('');
                    sendCheckoutCancel();
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  CANCEL
                </button>
                <button
                  onClick={handleCompleteCashPayment}
                  disabled={!cashTendered || parseFloat(cashTendered) < (checkoutSubtotal * 1.08)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: (!cashTendered || parseFloat(cashTendered) < (checkoutSubtotal * 1.08)) ? '#555' : '#4caf50',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: (!cashTendered || parseFloat(cashTendered) < (checkoutSubtotal * 1.08)) ? 'not-allowed' : 'pointer',
                  }}
                >
                  COMPLETE
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Checkout Success Overlay */}
        {showCheckoutSuccess && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,128,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}>
            <div style={{ textAlign: 'center', color: '#fff' }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>✓</div>
              <h2 style={{ fontSize: '28px', marginBottom: '10px' }}>Payment Complete</h2>
              {lastCheckoutResult && (
                <p style={{ fontSize: '18px', opacity: 0.9 }}>
                  ${(lastCheckoutResult.totalCharged?.amount / 100).toFixed(2)} charged
                </p>
              )}
            </div>
          </div>
        )}

        {/* Payment Confirmation Dialog - Shows when user returns from Square */}
        {showPaymentConfirmDialog && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2002,
          }}>
            <div style={{ textAlign: 'center', color: '#fff', padding: '40px', maxWidth: '400px' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>💳</div>
              <h2 style={{ fontSize: '28px', marginBottom: '20px' }}>Was the payment successful?</h2>
              <p style={{ fontSize: '16px', opacity: 0.8, marginBottom: '30px' }}>
                Confirm whether the payment was completed in Square POS
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <button
                  onClick={() => handlePaymentConfirm(true)}
                  style={{
                    padding: '16px 32px',
                    fontSize: '18px',
                    fontWeight: 600,
                    background: '#22c55e',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    minWidth: '120px'
                  }}
                >
                  ✓ YES
                </button>
                <button
                  onClick={() => handlePaymentConfirm(false)}
                  style={{
                    padding: '16px 32px',
                    fontSize: '18px',
                    fontWeight: 600,
                    background: '#ef4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    minWidth: '120px'
                  }}
                >
                  ✕ NO
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reader Setup Modal */}
        {showReaderSetup && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2002,
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ margin: 0, fontSize: '24px' }}>Card Reader Setup</h2>
                <button
                  onClick={() => setShowReaderSetup(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '28px',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              {readerConnected ? (
                <div>
                  <div style={{
                    background: '#e8f5e9',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2e7d32', marginBottom: '8px' }}>
                      ✓ Reader Connected
                    </div>
                    <div style={{ color: '#666' }}>
                      Serial: {readerInfo?.serialNumber || 'Unknown'}
                    </div>
                    {readerInfo?.batteryLevel && (
                      <div style={{ color: '#666' }}>
                        Battery: {Math.round(readerInfo.batteryLevel * 100)}%
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleDisconnectReader}
                    style={{
                      width: '100%',
                      padding: '16px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      background: '#ef4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Disconnect Reader
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{
                    background: '#fff3cd',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    color: '#856404'
                  }}>
                    <strong>Setup Instructions:</strong>
                    <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      <li>Power on your M2 reader</li>
                      <li>Hold power button for 5+ seconds until LED flashes blue</li>
                      <li>Click "Scan for Readers" below</li>
                      <li>Select your reader from the list</li>
                    </ol>
                  </div>

                  <button
                    onClick={handleDiscoverReaders}
                    disabled={scanningReaders}
                    style={{
                      width: '100%',
                      padding: '16px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      background: scanningReaders ? '#9ca3af' : '#800080',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: scanningReaders ? 'not-allowed' : 'pointer',
                      marginBottom: '16px'
                    }}
                  >
                    {scanningReaders ? 'Scanning...' : 'Scan for Readers'}
                  </button>

                  {discoveredReaders.length > 0 && (
                    <div>
                      <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Available Readers:</h3>
                      {discoveredReaders.map((reader, index) => (
                        <button
                          key={index}
                          onClick={() => handleConnectReader(index)}
                          style={{
                            width: '100%',
                            padding: '16px',
                            marginBottom: '8px',
                            background: '#f3f4f6',
                            border: '2px solid #800080',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            textAlign: 'left'
                          }}
                        >
                          <div style={{ fontWeight: 'bold' }}>Reader #{index + 1}</div>
                          <div style={{ fontSize: '14px', color: '#666' }}>Click to connect</div>
                        </button>
                      ))}
                    </div>
                  )}

                  {!scanningReaders && discoveredReaders.length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '32px',
                      color: '#9ca3af',
                      fontSize: '14px'
                    }}>
                      No readers found. Make sure your M2 reader is in pairing mode.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Status Overlay - Shows result after confirmation (not for 'processing' or 'payment_failed' - those show in header) */}
        {paymentStatus && paymentStatus !== 'processing' && paymentStatus !== 'payment_failed' && !showPaymentConfirmDialog && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: paymentStatus === 'payment_success' ? 'rgba(0,128,0,0.95)' 
              : (paymentStatus === 'pending' || paymentStatus === 'processing') ? 'rgba(128,0,128,0.95)'
              : (paymentStatus === 'payment_failed' || paymentStatus === 'payment_canceled') ? 'rgba(200,50,50,0.95)'
              : 'rgba(128,0,128,0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2001,
          }}>
            <div style={{ textAlign: 'center', color: '#fff', padding: '40px' }}>
              {(paymentStatus === 'pending' || paymentStatus === 'processing') && (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
                  <h2 style={{ fontSize: '28px', marginBottom: '10px' }}>Processing Payment</h2>
                  <p style={{ fontSize: '18px', opacity: 0.9, marginBottom: '20px' }}>
                    {paymentStatusMessage || 'Processing...'}
                  </p>
                  <p style={{ fontSize: '14px', opacity: 0.7 }}>
                    Please wait...
                  </p>
                  <button
                    onClick={() => {
                      setPaymentStatus(null);
                      setPaymentStatusMessage(null);
                      setCurrentCheckoutId(null);
                      setCheckoutLoading(false);
                    }}
                    style={{
                      marginTop: '30px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      background: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      border: '2px solid #fff',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </>
              )}
              {paymentStatus === 'payment_success' && (
                <>
                  <style>{`
                    @keyframes success-checkmark-vertical {
                      0% { transform: scale(0); opacity: 0; }
                      50% { transform: scale(1.2); opacity: 1; }
                      100% { transform: scale(1); opacity: 1; }
                    }
                    @keyframes success-circle-vertical {
                      0% { stroke-dashoffset: 166; }
                      100% { stroke-dashoffset: 0; }
                    }
                    .success-animation-vertical {
                      animation: success-checkmark-vertical 0.6s ease-out;
                    }
                  `}</style>
                  <div className="success-animation-vertical" style={{ marginBottom: '20px' }}>
                    <svg width="80" height="80" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="54" fill="none" stroke="#22c55e" strokeWidth="4" 
                        strokeDasharray="339.292" strokeDashoffset="0"
                        style={{ animation: 'success-circle-vertical 0.6s ease-out' }} />
                      <path d="M34 60 L52 78 L86 44" fill="none" stroke="#22c55e" strokeWidth="6" 
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h2 style={{ fontSize: '28px', marginBottom: '10px' }}>Payment Complete!</h2>
                  <p style={{ fontSize: '18px', opacity: 0.9 }}>
                    {paymentStatusMessage}
                  </p>
                </>
              )}
              {(paymentStatus === 'payment_canceled' || paymentStatus === 'payment_failed') && (
                <>
                  <div style={{ fontSize: '64px', marginBottom: '20px' }}>✕</div>
                  <h2 style={{ fontSize: '28px', marginBottom: '10px' }}>
                    {paymentStatus === 'payment_canceled' ? 'Payment Canceled' : 'Payment Failed'}
                  </h2>
                  <p style={{ fontSize: '18px', opacity: 0.9 }}>
                    {paymentStatusMessage}
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Preview mode: render with viewer wrapper for admin interface
  const containerPadding = '60px 16px 16px 16px';

  return (
    <div style={{ padding: containerPadding, width: '100%', height: '100%', minHeight: '100vh', boxSizing: 'border-box' }}>
      {/* Only show orientation toggle when layoutMode is 'auto' */}
      {layoutMode === 'auto' && (
        <div style={{ display: 'flex', gap: 12, marginTop: 0, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontWeight: 600 }}>Orientation:</span>
            {[
              { key: 'horizontal', label: '16:10' },
              { key: 'vertical', label: '9:19' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setOrientation(key)}
                style={{
                  border: '1px solid #333',
                  background: orientation === key ? '#333' : 'transparent',
                  color: orientation === key ? '#fff' : '#333',
                  padding: '6px 10px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.85rem',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          position: 'relative',
          width: '100%',
          background: '#f7f7f7',
          border: '1px solid #ddd',
          borderRadius: 8,
          padding: '12px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          ref={frameRef}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            aspectRatio: '16 / 10',
            overflow: 'hidden',
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: 6,
          }}
        >
          {frameReady && (
            <div
              style={{
                position: 'absolute',
                width: `${stageDims.width}px`,
                height: `${stageDims.height}px`,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                border: orientation === 'vertical' ? '1px solid #aaa' : 'none',
                overflow: 'hidden'
              }}
            >
              <POSContent
                outerWidth={stageDims.width}
                outerHeight={stageDims.height}
                items={items}
                activeCategory={activeCategory}
                setActiveCategory={setActiveCategory}
                onItemClick={handleItemClick}
                loading={loading}
                total={total}
                categoryCounts={categoryCounts}
                selectedItems={selectedItems}
                lastAction={lastAction}
                onRemoveItem={handleRemoveItem}
                tabs={tabs}
                activeTabId={activeTabId}
                onCreateTab={handleCreateTab}
                onSelectTab={handleSelectTab}
                onDeleteTab={handleDeleteTab}
                onArchiveTab={handleArchiveTab}
                onInvoiceTab={handleInvoiceTab}
                onReopenTab={handleReopenTab}
                onUpdateTabName={handleUpdateTabName}
                onUpdateItemModifiers={handleUpdateItemModifiers}
                onMoveItems={handleMoveItems}
                onDuplicateItems={handleDuplicateItems}
                onCheckout={handleCheckout}
                checkoutLoading={checkoutLoading}
                showSpendLimitWarning={showSpendLimitWarning}
                setShowSpendLimitWarning={setShowSpendLimitWarning}
                spendLimitWarningTab={spendLimitWarningTab}
                setSpendLimitWarningTab={setSpendLimitWarningTab}
                isSidebarOpen={showEndEventButton}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
